import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Option,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaArrowDown, FaArrowUp, FaRegStar, FaStar } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import type { DocumentLineInput } from '../types/documents.types';
import { useWindowStylesWatcher } from '../../../hooks/useWindowStylesWatcher';
import { windowStylesByType } from '../../../config/windowStyles';
import type { WindowStyleConfig } from '../../../config/windowStyles';
import ImageCanvasViewer from '../../../components/ImageCanvasViewer';
import { windowStyleFavoritesService } from '../../../services/windowStyleFavorites.service';
import { windowGeneratorTemplatesService, type WindowGeneratorTemplate, type WindowGeneratorPayload, type OnSavedToLibraryArg } from '../../../services/windowGeneratorTemplates.service';
import {
  uploadVisualDesignerPreviewAndGetUrl,
  uploadVisualDesignerPreviewsAndGetUrls,
  uploadVisualDesignerReferenceImageVariantAndGetUrl,
} from '../../../services/visualDesignerPreviewUpload.service';
import { WindowGeneratorBaySchematicSvg, WindowGeneratorThumbnail, WindowGeneratorSvg } from '../../../components/windowGeneratorRender';
import { documentsService } from '../services/documents.service';
import { computeBayGeometry, createDefaultBayConfig, normalizeBayConfig } from '../../../utils/bayGeometry';
import {
  attachV2LitePreviewUrls,
  mergeBulkResultsIntoLines,
  qualifiesForV2LiteWgBulk,
  resolveCanonicalTemplatePayload,
} from '../utils/windowPayloadPatches';
import { resolveQuoteLineCardLivePayload } from '../utils/visualDesignerPayloadResolution';
import {
  finalSurveyUsesEmptyMeasureFields,
  formatFirstSurveyReferenceSummary,
  getFirstSurveyReference,
} from '../utils/visualDesignerSurveyClone';
import {
  BulkApplyError,
  BulkRunConcurrentEditError,
  runBulkApplyOrchestration,
} from '../utils/visualDesignerBulkApply';
import { EXTENDERS_MM_OUTPUT_LABEL } from '../utils/visualDesignerFieldFormatters';
import {
  ensureExtenderTopMm,
  hasActiveTopFrameExtender,
  isExtenderTrickleVentActive,
  parseSemanticTricklePosition,
  resolveTricklePositionAfterTopExtenderRemoved,
  semanticTricklePositionToVentPos,
  type TrickleVentsSemanticPosition,
  isJambLikeVentPos,
} from '../utils/trickleVentPosition';
import {
  CUSTOM_FRAME_COLOUR_CODE,
  FRAME_COLOUR_OPTIONS,
  FRAME_SIDE_COLOUR_OPTIONS,
  WINDOW_GENERATOR_PX2MM,
  buildVisualDesignerLineDescription,
  formatProfileDefinition,
  getDesignerSummaryRows,
  isBayLikePayload,
  isDoorType,
  type DesignerSummaryMeta,
} from '../utils/visualDesignerSummary';
import {
  BIFOLD_DOORS_TYPE_ID,
  BIFOLD_PANEL_COUNT_RANGE,
  DEFAULT_MASTER_LEAF,
  DEFAULT_STACK_DIRECTION,
  DEFAULT_THRESHOLD,
  DEFAULT_TRAFFIC_DOOR,
  DOOR_TYPE_LABELS,
  FRENCH_DOORS_TYPE_ID,
  MASTER_LEAF_OPTIONS,
  SLIDER_PANEL_COUNT_RANGE,
  SLIDING_DOORS_TYPE_ID,
  STACK_DIRECTION_OPTIONS,
  THRESHOLD_OPTIONS,
  TRAFFIC_DOOR_OPTIONS,
  applyMasterLeafToPayload,
  buildBifoldDoorsPayload,
  buildFrenchDoorsPayload,
  buildSlidingDoorsPayload,
  clampPanelCount,
  describeSlidingPanels,
  doorKindUsesHingePlacement,
  doorKindUsesOpenDirection,
  getDoorKind,
  parseThresholdCode,
  type DoorKind,
  type DoorSideCode,
  type StackDirectionCode,
  type ThresholdCode,
  type TrafficDoorCode,
} from '../utils/doorTypes';
import {
  DEFAULT_GLASS_SPECIFICATION,
  GLASS_SPECIFICATION_OPTIONS,
  parseGlassSpecificationCode,
  type GlassSpecificationCode,
} from '../utils/glassSpecification';

export { formatProfileDefinition, getDesignerSummaryRows, isBayLikePayload };

// Lazy: the editor is only needed once a Window Generator modal is opened.
const WindowGenerator = React.lazy(() => import('../../../components/windowgenerator')) as unknown as React.FC<{
  onSavedToLibrary?: (t: OnSavedToLibraryArg) => void;
  initialPayload?: WindowGeneratorPayload | null;
  onExportPayload?: (payload: WindowGeneratorPayload) => void;
  handleStyleOptions?: Array<{ code: string; label: string }>;
}>;

const VISUAL_DESIGNER_FEATURE = 'visual_designer';
const VISUAL_DESIGNER_SCHEMA_VERSION = 8;
const MIN_FENESTRATION_DIM_MM = 200;
const MAX_FENESTRATION_WIDTH_MM = 8000;
const MAX_FENESTRATION_HEIGHT_MM = 8000;

const QUOTE_WINDOW_TYPES: Array<{
  id: 'casement' | 'flush-casement' | 'tilt-and-turn' | 'sliding-sash' | 'upvc-door' | 'french-doors' | 'bifold-doors' | 'sliding-doors' | 'door-window' | 'other';
  name: string;
  imagePath: string;
}> = [
  { id: 'casement', name: 'Casement Window', imagePath: '/images/window-types/casement.jpg' },
  { id: 'flush-casement', name: 'Flush Sash Window', imagePath: '/images/window-types/flush-sash.jpg' },
  { id: 'tilt-and-turn', name: 'Tilt & Turn Window', imagePath: '/images/window-types/tilt-and-turn.jpg' },
  { id: 'sliding-sash', name: 'Sliding Sash Window', imagePath: '/images/window-types/sliding-sash.jpg' },
  { id: 'upvc-door', name: 'Door', imagePath: '/images/window-types/upvc-door.jpg' },
  { id: FRENCH_DOORS_TYPE_ID, name: DOOR_TYPE_LABELS[FRENCH_DOORS_TYPE_ID], imagePath: '/images/window-types/french-doors.png' },
  { id: BIFOLD_DOORS_TYPE_ID, name: DOOR_TYPE_LABELS[BIFOLD_DOORS_TYPE_ID], imagePath: '/images/window-types/bi-fold-doors.png' },
  { id: SLIDING_DOORS_TYPE_ID, name: DOOR_TYPE_LABELS[SLIDING_DOORS_TYPE_ID], imagePath: '/images/window-types/sliders.png' },
  { id: 'door-window', name: 'Door & Window', imagePath: '/images/window-types/door-flag.png' },
  { id: 'other', name: 'Other', imagePath: '/images/window-types/casement.jpg' },
];

const MATERIAL_OPTIONS: Array<{ id: 'upvc' | 'aluminium' | 'timber' | 'composite'; name: string }> = [
  { id: 'upvc', name: 'uPVC' },
  { id: 'aluminium', name: 'Aluminium' },
  { id: 'timber', name: 'Timber' },
  { id: 'composite', name: 'Composite' },
];

type GlassSelectValue = 'clear' | 'satin' | `tile:${string}`;

type WindowHingeTypeCode = 'standard' | 'egress' | 'ez_clean' | 'fire_rated';
type BarSideCode = 'none' | 'both' | 'outside' | 'inside';

const WINDOW_HINGE_TYPE_OPTIONS: Array<{ code: WindowHingeTypeCode; label: string }> = [
  { code: 'standard', label: 'Standard (default)' },
  { code: 'egress', label: 'Egress' },
  { code: 'ez_clean', label: 'EZ Clean' },
  { code: 'fire_rated', label: 'Fire-Rated' },
];

const BAR_SIDE_OPTIONS: Array<{ code: BarSideCode; label: string }> = [
  { code: 'none', label: 'None (default)' },
  { code: 'both', label: 'Both' },
  { code: 'outside', label: 'Outside' },
  { code: 'inside', label: 'Inside' },
];

function inferGlassTileNameFromPath(path: string): string {
  const p = String(path || '').trim();
  if (!p) return '';
  const name = (p.split('/').pop() || p)
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (!name) return '';
  return name.replace(/\b\w/g, (m) => m.toUpperCase());
}

function deepCloneJson<T>(value: T): T {
  // Prevent shared-reference mutations between quote lines (esp. WG payload/root tree)
  const sc = (globalThis as any)?.structuredClone as undefined | ((v: any) => any);
  if (typeof sc === 'function') return sc(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function stripAutoDerivedNotes(input: string): string {
  const raw = String(input || '');
  if (!raw.trim()) return '';
  const isAutoSegment = (seg: string) => {
    const s = seg.trim();
    if (!s) return false;
    if (/^Extenders\s+T\d+\s+B\d+\s+L\d+\s+R\d+\s+mm$/i.test(s)) return true;
    if (/^Georgian bars\b/i.test(s)) return true;
    if (/^Astragal bars\b/i.test(s)) return true;
    if (/^Corner joint:\s*Timber Weld$/i.test(s)) return true;
    return false;
  };
  const lines = raw.split('\n');
  const cleanedLines: string[] = [];
  for (const line of lines) {
    const segments = line.split('•').map(s => s.trim()).filter(Boolean);
    const kept = segments.filter(s => !isAutoSegment(s));
    const joined = kept.join(' • ').trim();
    if (joined) cleanedLines.push(joined);
  }
  return cleanedLines.join('\n').trim();
}

const STYLE_FAMILY_OPTIONS: Array<{ code: WindowStyleConfig['category']; label: string }> = [
  { code: 'traditional', label: 'Standard' },
  { code: 'heritage', label: 'Heritage' },
];

const DEFAULT_STYLE_FAMILY_CATEGORY: WindowStyleConfig['category'] = 'traditional';
const normalizeStyleFamilyCategory = (value: unknown): WindowStyleConfig['category'] => {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'heritage' ? 'heritage' : DEFAULT_STYLE_FAMILY_CATEGORY;
};
const DEFAULT_COMBINED_FRAME_CODE = 'white-smooth';
const FRAME_RAL_BY_SIDE_CODE: Record<string, string> = {
  'white-smooth': 'RAL9016',
  'white-wood-grain': 'RAL9016',
  'agate-grey': 'RAL7038',
  'black-noir': 'RAL5002',
  'anthracite-grey-grain': 'RAL7016',
  'anthracite-smooth': 'RAL7016',
  'anthracite': 'RAL7016',
  'grey': 'RAL7016',
  'black': 'RAL9005',
  'chartwell-green': 'RAL6021',
  'cream': 'RAL9001',
  'golden-oak': 'RAL8003',
  'honey-oak': 'RAL3000',
  'irish-oak': 'RAL8001',
  'pebble-grey': 'RAL7032',
  'rosewood': 'RAL8017',
  'silver-grey': 'RAL7001',
  'natural-untreated': 'RAL1001',
  'primed': 'RAL9010',
};
const FRAME_SIDE_COLOUR_OPTIONS_WITH_CUSTOM: Array<{ code: string; label: string }> = [
  ...FRAME_SIDE_COLOUR_OPTIONS,
  { code: CUSTOM_FRAME_COLOUR_CODE, label: 'RAL Custom' },
];
function normalizeRalText(raw: unknown): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  const match = text.match(/^ral\s*[-\s]*([a-z0-9]+)$/i);
  if (match) return `RAL${String(match[1] || '').toUpperCase()}`;
  return text;
}
function parseHexColour(raw: unknown): string | null {
  const value = String(raw || '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(value)) {
    const expanded = value.split('').map((c) => `${c}${c}`).join('');
    return `#${expanded.toUpperCase()}`;
  }
  if (/^[0-9a-f]{6}$/i.test(value)) return `#${value.toUpperCase()}`;
  return null;
}
function normalizeHexInput(raw: unknown): string {
  const value = String(raw || '').trim().replace(/\s+/g, '');
  if (!value) return '';
  return value.startsWith('#') ? value : `#${value.replace(/^#+/, '')}`;
}

/** Handle colours aligned with Window Generator (HANDLE COLOUR). */
const HANDLE_COLOUR_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'white', label: 'White' },
  { code: 'black', label: 'Black' },
  { code: 'grey', label: 'Grey' },
  { code: 'anthracite-grey', label: 'Anthracite Grey' },
  { code: 'gold', label: 'Gold' },
  { code: 'chrome', label: 'Chrome' },
  { code: 'satin-chrome', label: 'Satin Chrome' },
  { code: 'na', label: 'N/A' },
];

type HandleStyleCode =
  | 'lever'
  | 'teardrop'
  | 'monkey-tail'
  | 'pear-drop'
  | 'bulb-end'
  | 'cockspur'
  | 'inline'
  | 'offset'
  | 'other'
  | 'na';

const DOOR_HANDLE_STYLE_OPTIONS: Array<{ code: HandleStyleCode; label: string }> = [
  { code: 'lever', label: 'Lever' },
  { code: 'teardrop', label: 'Teardrop' },
  { code: 'na', label: 'N/A' },
];

const WINDOW_HANDLE_STYLE_OPTIONS: Array<{ code: HandleStyleCode; label: string }> = [
  { code: 'monkey-tail', label: 'Monkey Tail' },
  { code: 'pear-drop', label: 'Pear Drop' },
  { code: 'bulb-end', label: 'Bulb End' },
  { code: 'cockspur', label: 'Cockspur' },
  { code: 'inline', label: 'Inline' },
  { code: 'offset', label: 'Offset' },
  { code: 'other', label: 'Other' },
  { code: 'na', label: 'N/A' },
];

const HANDLE_STYLE_OPTIONS: Array<{ code: HandleStyleCode; label: string }> = [
  ...DOOR_HANDLE_STYLE_OPTIONS,
  ...WINDOW_HANDLE_STYLE_OPTIONS.filter((o) => !DOOR_HANDLE_STYLE_OPTIONS.some((d) => d.code === o.code)),
];

type DoorHingePlacementCode = 'left' | 'right';
type DoorOpenDirectionCode = 'in' | 'out' | 'bi-directional';

const DOOR_HINGE_PLACEMENT_OPTIONS: Array<{ code: DoorHingePlacementCode; label: string }> = [
  { code: 'left', label: 'Left' },
  { code: 'right', label: 'Right' },
];

const DOOR_OPEN_DIRECTION_OPTIONS: Array<{ code: DoorOpenDirectionCode; label: string }> = [
  { code: 'out', label: 'Out' },
  { code: 'in', label: 'In' },
  { code: 'bi-directional', label: 'Bi-directional' },
];

type ProfileVariantCode =
  | 'chamfered'
  | 'sculptured'
  | 'flush-sash'
  | 'aluminium-add'
  | 'aluminium-contemporary'
  | 'aluminium-flush'
  | 'aluminium-flush-contemporary'
  | 'aluminium-stepped-crittal'
  | 'aluminium-flush-stepped'
  | 'timber-flush'
  | 'timber-timberweld-tm'
  | 'timber-casement';
type OptionalProfileVariantCode = ProfileVariantCode | '';

const PROFILE_VARIANT_OPTIONS: Array<{ code: ProfileVariantCode; label: string }> = [
  { code: 'chamfered', label: 'Chamfered (Bevelled)' },
  { code: 'sculptured', label: 'Sculptured (Ovolo)' },
  { code: 'flush-sash', label: 'Flush Sash' },
  { code: 'aluminium-add', label: 'Add' },
  { code: 'aluminium-contemporary', label: 'Contemporary' },
  { code: 'aluminium-flush', label: 'Flush' },
  { code: 'aluminium-flush-contemporary', label: 'Flush Contemporary' },
  { code: 'aluminium-stepped-crittal', label: 'Stepped (Crittal)' },
  { code: 'aluminium-flush-stepped', label: 'Flush Stepped' },
  { code: 'timber-flush', label: 'Flush' },
  { code: 'timber-timberweld-tm', label: 'Timberweld (TM)' },
  { code: 'timber-casement', label: 'Casement' },
];

const BEADING_TYPE_OPTIONS: Array<{ code: ProfileVariantCode; label: string }> = [
  { code: 'chamfered', label: 'Chamfered (Bevelled)' },
  { code: 'sculptured', label: 'Sculptured (Ovolo)' },
  { code: 'flush-sash', label: 'Flush Sash' },
];

const PROFILE_VARIANT_GROUPS: Array<{ label: string; options: Array<{ code: ProfileVariantCode; label: string }> }> = [
  { label: 'None', options: [] },
  {
    label: 'uPVC/Composite',
    options: [
      { code: 'chamfered', label: 'Chamfered (Bevelled)' },
      { code: 'sculptured', label: 'Sculptured (Ovolo)' },
      { code: 'flush-sash', label: 'Flush Sash' },
    ],
  },
  {
    label: 'Aluminium',
    options: [
      { code: 'aluminium-contemporary', label: 'Contemporary' },
      { code: 'aluminium-flush', label: 'Flush' },
      { code: 'aluminium-flush-contemporary', label: 'Flush Contemporary' },
      { code: 'aluminium-stepped-crittal', label: 'Stepped (Crittal)' },
      { code: 'aluminium-flush-stepped', label: 'Flush Stepped' },
    ],
  },
  {
    label: 'Timber',
    options: [
      { code: 'timber-flush', label: 'Flush' },
      { code: 'timber-timberweld-tm', label: 'Timberweld (TM)' },
      { code: 'timber-casement', label: 'Casement' },
    ],
  },
];

const PROFILE_VARIANT_CODE_SET = new Set<ProfileVariantCode>(PROFILE_VARIANT_OPTIONS.map((o) => o.code));

function getStoredVariantCode(specs: VisualDesignerMetaV1['specs'] | undefined): OptionalProfileVariantCode {
  const storedCode = specs?.variant_code as ProfileVariantCode | undefined;
  if (storedCode && PROFILE_VARIANT_CODE_SET.has(storedCode)) return storedCode;

  const variantLabel = String(specs?.variant || '').toLowerCase();
  const exactLabelMatch = PROFILE_VARIANT_OPTIONS.find((o) => o.label.toLowerCase() === variantLabel);
  if (exactLabelMatch) return exactLabelMatch.code;
  if (variantLabel.includes('flush') && variantLabel.includes('sash')) return 'flush-sash';
  if (variantLabel.includes('ovolo') || variantLabel.includes('sculpt')) return 'sculptured';
  if (variantLabel.includes('chamf') || variantLabel.includes('bevel')) return 'chamfered';

  if (Object.prototype.hasOwnProperty.call(specs || {}, 'variant_code') || Object.prototype.hasOwnProperty.call(specs || {}, 'variant')) {
    return '';
  }

  return 'chamfered';
}

function getStoredBeadingTypeCode(specs: VisualDesignerMetaV1['specs'] | undefined): OptionalProfileVariantCode {
  const storedCode = specs?.beading_type_code;
  if (storedCode === 'chamfered' || storedCode === 'sculptured' || storedCode === 'flush-sash') return storedCode;

  const beadingLabel = String(specs?.beading_type || '').toLowerCase();
  if (beadingLabel.includes('flush') && beadingLabel.includes('sash')) return 'flush-sash';
  if (beadingLabel.includes('ovolo') || beadingLabel.includes('sculpt')) return 'sculptured';
  if (beadingLabel.includes('chamf') || beadingLabel.includes('bevel')) return 'chamfered';

  if (Object.prototype.hasOwnProperty.call(specs || {}, 'beading_type_code') || Object.prototype.hasOwnProperty.call(specs || {}, 'beading_type')) {
    return '';
  }

  const pairedVariant = getStoredVariantCode(specs);
  return pairedVariant || 'chamfered';
}

/** Map modal frame_color_code to WG presentation frame fields. Matches Window Generator FRAME_COLS. */
const FRAME_CODE_TO_WG: Record<string, { frameNameExt: string; frameNameInt: string; frameColExt: string; frameColInt: string }> = {
  'white-smooth': { frameNameExt: 'White Smooth', frameNameInt: 'White Smooth', frameColExt: '#F6F6F6', frameColInt: '#F6F6F6' },
  'white-wood-grain': { frameNameExt: 'White Wood Grain', frameNameInt: 'White Wood Grain', frameColExt: '#F6F6F6', frameColInt: '#F6F6F6' },
  'agate-grey': { frameNameExt: 'Agate Grey', frameNameInt: 'Agate Grey', frameColExt: '#B1B3B3', frameColInt: '#B1B3B3' },
  'black-noir': { frameNameExt: 'Black Noir', frameNameInt: 'Black Noir', frameColExt: '#262626', frameColInt: '#262626' },
  'anthracite-grey-grain': { frameNameExt: 'Anthracite Grey Grain', frameNameInt: 'Anthracite Grey Grain', frameColExt: '#383E42', frameColInt: '#383E42' },
  'anthracite-smooth': { frameNameExt: 'Anthracite Smooth', frameNameInt: 'Anthracite Smooth', frameColExt: '#383E42', frameColInt: '#383E42' },
  'anthracite': { frameNameExt: 'Anthracite', frameNameInt: 'Anthracite', frameColExt: '#383E42', frameColInt: '#383E42' },
  grey: { frameNameExt: 'Grey', frameNameInt: 'Grey', frameColExt: '#383E42', frameColInt: '#383E42' },
  'black': { frameNameExt: 'Black', frameNameInt: 'Black', frameColExt: '#0A0A0A', frameColInt: '#0A0A0A' },
  'chartwell-green': { frameNameExt: 'Chartwell Green', frameNameInt: 'Chartwell Green', frameColExt: '#8E9F8C', frameColInt: '#8E9F8C' },
  'cream': { frameNameExt: 'Cream', frameNameInt: 'Cream', frameColExt: '#F4E3C3', frameColInt: '#F4E3C3' },
  'golden-oak': { frameNameExt: 'Golden Oak', frameNameInt: 'Golden Oak', frameColExt: '#A65E2E', frameColInt: '#A65E2E' },
  'honey-oak': { frameNameExt: 'Honey Oak', frameNameInt: 'Honey Oak', frameColExt: '#794A29', frameColInt: '#794A29' },
  'irish-oak': { frameNameExt: 'Irish Oak', frameNameInt: 'Irish Oak', frameColExt: '#9C6B30', frameColInt: '#9C6B30' },
  'pebble-grey': { frameNameExt: 'Pebble Grey', frameNameInt: 'Pebble Grey', frameColExt: '#999A92', frameColInt: '#999A92' },
  'rosewood': { frameNameExt: 'Rosewood', frameNameInt: 'Rosewood', frameColExt: '#442F29', frameColInt: '#442F29' },
  'silver-grey': { frameNameExt: 'Silver Grey', frameNameInt: 'Silver Grey', frameColExt: '#8F999F', frameColInt: '#8F999F' },
  'natural-untreated': { frameNameExt: 'Natural/Untreated', frameNameInt: 'Natural/Untreated', frameColExt: '#F2EAD8', frameColInt: '#F2EAD8' },
  'primed': { frameNameExt: 'Primed', frameNameInt: 'Primed', frameColExt: '#F0EDE6', frameColInt: '#F0EDE6' },
  'black-on-white-smooth': { frameNameExt: 'Black', frameNameInt: 'White Smooth', frameColExt: '#0A0A0A', frameColInt: '#F6F6F6' },
  'agate-grey-on-white-smooth': { frameNameExt: 'Agate Grey', frameNameInt: 'White Smooth', frameColExt: '#B1B3B3', frameColInt: '#F6F6F6' },
  'anthracite-on-white-smooth': { frameNameExt: 'Anthracite', frameNameInt: 'White Smooth', frameColExt: '#383E42', frameColInt: '#F6F6F6' },
};

/** Map modal handle_color_code to WG handleCol (hex) and handleName. Aligns with WG handle options. */
const HANDLE_CODE_TO_WG: Record<string, { handleCol: string; handleName: string }> = {
  white: { handleCol: '#F0F0F0', handleName: 'White' },
  black: { handleCol: '#1A1A1E', handleName: 'Black' },
  grey: { handleCol: '#383E42', handleName: 'Grey' },
  'anthracite-grey': { handleCol: '#383E42', handleName: 'Anthracite Grey' },
  gold: { handleCol: '#C9A84C', handleName: 'Gold' },
  chrome: { handleCol: '#C0C0C0', handleName: 'Chrome' },
  'satin-chrome': { handleCol: '#A8AEAD', handleName: 'Satin Chrome' },
};

function getStoredFrameSideCodes(specs: VisualDesignerMetaV1['specs'] | undefined): { combinedCode: string; extCode: string; intCode: string } {
  const specsAny = (specs as any) || {};
  const rawCombined = String(specsAny?.frame_color_code || '').trim();
  const rawExtCode = String(specsAny?.frame_color_code_external || '').trim();
  const rawIntCode = String(specsAny?.frame_color_code_internal || '').trim();
  const rawExtName = String(specsAny?.frame_color_external || '').trim();
  const rawIntName = String(specsAny?.frame_color_internal || '').trim();
  const rawExtHex = String(specsAny?.options?.frame_color_hex_external || '').trim();
  const rawIntHex = String(specsAny?.options?.frame_color_hex_internal || '').trim();
  const hasExtCodeKey = Object.prototype.hasOwnProperty.call(specsAny, 'frame_color_code_external');
  const hasIntCodeKey = Object.prototype.hasOwnProperty.call(specsAny, 'frame_color_code_internal');
  const resolveCodeFromRal = (rawRal: string, rawHex: string, side: 'ext' | 'int'): string => {
    const normalizedRal = normalizeRalText(rawRal);
    if (!normalizedRal) return '';
    const normalizedHex = parseHexColour(rawHex);
    for (const [candidateCode, candidateRal] of Object.entries(FRAME_RAL_BY_SIDE_CODE)) {
      if (!FRAME_SIDE_COLOUR_OPTIONS.some((o) => o.code === candidateCode)) continue;
      if (normalizeRalText(candidateRal) !== normalizedRal) continue;
      const frame = FRAME_CODE_TO_WG[candidateCode];
      if (!frame) continue;
      const expectedHex = parseHexColour(side === 'ext' ? frame.frameColExt : frame.frameColInt);
      // Only remap RAL text to a preset when the stored HEX also matches that preset.
      // This avoids collapsing explicit custom RAL selections to presets during rehydration.
      if (!normalizedHex || !expectedHex) continue;
      if (normalizedHex !== expectedHex) continue;
      return candidateCode;
    }
    return '';
  };

  let extCode = FRAME_CODE_TO_WG[rawExtCode] ? rawExtCode : '';
  let intCode = FRAME_CODE_TO_WG[rawIntCode] ? rawIntCode : '';
  if (rawExtCode === CUSTOM_FRAME_COLOUR_CODE) extCode = CUSTOM_FRAME_COLOUR_CODE;
  if (rawIntCode === CUSTOM_FRAME_COLOUR_CODE) intCode = CUSTOM_FRAME_COLOUR_CODE;
  const extPresetFromStored = tryMatchFrameSideColourCodeFromName(rawExtName) || resolveCodeFromRal(rawExtName, rawExtHex, 'ext');
  const intPresetFromStored = tryMatchFrameSideColourCodeFromName(rawIntName) || resolveCodeFromRal(rawIntName, rawIntHex, 'int');
  if (!extCode) extCode = extPresetFromStored;
  if (!intCode) intCode = intPresetFromStored;
  const extLooksCustom = !!(normalizeRalText(rawExtName) || parseHexColour(rawExtHex));
  const intLooksCustom = !!(normalizeRalText(rawIntName) || parseHexColour(rawIntHex));
  // If side code field is explicitly present but blank/null and custom values exist,
  // preserve explicit custom only when we cannot map to a known preset.
  if (!extCode && hasExtCodeKey && !rawExtCode && extLooksCustom) extCode = CUSTOM_FRAME_COLOUR_CODE;
  if (!intCode && hasIntCodeKey && !rawIntCode && intLooksCustom) intCode = CUSTOM_FRAME_COLOUR_CODE;
  if (!extCode && (normalizeRalText(rawExtName) || parseHexColour(rawExtHex))) extCode = CUSTOM_FRAME_COLOUR_CODE;
  if (!intCode && (normalizeRalText(rawIntName) || parseHexColour(rawIntHex))) intCode = CUSTOM_FRAME_COLOUR_CODE;
  const combinedCode = FRAME_CODE_TO_WG[rawCombined]
    ? rawCombined
    : (extCode || intCode || DEFAULT_COMBINED_FRAME_CODE);

  if (extCode || intCode) {
    return {
      combinedCode,
      extCode: extCode || combinedCode || 'white-smooth',
      intCode: intCode || combinedCode || 'white-smooth',
    };
  }

  if (FRAME_CODE_TO_WG[combinedCode]) {
    const frame = FRAME_CODE_TO_WG[combinedCode];
    return {
      combinedCode,
      extCode: tryMatchFrameSideColourCodeFromName(frame.frameNameExt) || combinedCode,
      intCode: tryMatchFrameSideColourCodeFromName(frame.frameNameInt) || combinedCode,
    };
  }

  return { combinedCode, extCode: combinedCode || 'white-smooth', intCode: combinedCode || 'white-smooth' };
}

function getFrameSelectionFromSideCodes(extCode: string, intCode: string): {
  combinedCode: string;
  combinedLabel: string;
  combinedValue: string;
  extCode: string;
  extLabel: string;
  extValue: string;
  intCode: string;
  intLabel: string;
  intValue: string;
} {
  const normalizeRalValue = (raw: unknown): string => {
    const text = String(raw || '').trim();
    if (!text) return '';
    const match = text.match(/^ral\s*[-\s]*([a-z0-9]+)$/i);
    if (match) return `RAL${String(match[1] || '').toUpperCase()}`;
    return text;
  };
  const getFrameValue = (sideCode: string, fallbackLabel: string): string => {
    // For preset side codes, persist user-facing preset labels (e.g. "White Smooth")
    // rather than mapped RAL text (e.g. "RAL9016"). Custom RAL entries are still
    // stored explicitly via the custom-path logic.
    const opt = FRAME_SIDE_COLOUR_OPTIONS.find((x) => x.code === String(sideCode || '').trim());
    return String(opt?.label || '').trim() || normalizeRalValue(fallbackLabel);
  };

  const frameExtOpt = FRAME_SIDE_COLOUR_OPTIONS.find((x) => x.code === extCode) || FRAME_SIDE_COLOUR_OPTIONS[0];
  const frameIntOpt = FRAME_SIDE_COLOUR_OPTIONS.find((x) => x.code === intCode) || FRAME_SIDE_COLOUR_OPTIONS[0];
  const combinedCode =
    frameExtOpt.code === frameIntOpt.code
      ? frameExtOpt.code
      : (tryFindCombinedFrameColourCode(frameExtOpt.code, frameIntOpt.code) || frameExtOpt.code);
  const combinedOpt = combinedCode ? (FRAME_COLOUR_OPTIONS.find((x) => x.code === combinedCode) || null) : null;
  const isDualFinish = frameExtOpt.code !== frameIntOpt.code;
  const combinedLabel = isDualFinish
    ? `${frameExtOpt.label} (external) / ${frameIntOpt.label} (internal)`
    : frameExtOpt.label;
  const extValue = getFrameValue(frameExtOpt.code, frameExtOpt.label);
  const intValue = getFrameValue(frameIntOpt.code, frameIntOpt.label);
  const combinedValue = isDualFinish
    ? `${extValue} (external) / ${intValue} (internal)`
    : extValue;

  return {
    combinedCode,
    // Keep explicit external/internal wording for mixed finishes so downstream
    // outputs (VC/QW/public/supplier) don't collapse to a single ambiguous label.
    combinedLabel: isDualFinish ? combinedLabel : (combinedOpt?.label ?? combinedLabel),
    combinedValue,
    extCode: frameExtOpt.code,
    extLabel: frameExtOpt.label,
    extValue,
    intCode: frameIntOpt.code,
    intLabel: frameIntOpt.label,
    intValue,
  };
}

function getStoredHandleColourCode(raw: unknown): string {
  const hc = String(raw ?? '').trim().toLowerCase();
  if (hc === 'satin') return 'satin-chrome';
  if (hc === 'anthracite') return 'anthracite-grey';
  if (hc === 'n/a' || hc === 'na' || hc === 'not-applicable' || hc === 'not applicable') return 'na';
  return hc || 'white';
}

function coerceLogicalDivsSnapshot(raw: unknown): Record<string, { h: number[]; v: number[] }> | null {
  if (!raw || typeof raw !== 'object') return null;
  const out: Record<string, { h: number[]; v: number[] }> = {};
  const sanitize = (arr: unknown): number[] => {
    const vals = Array.isArray(arr) ? arr : [];
    const coerced = vals
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n))
      .map((n) => {
        // Back-compat: some old snapshots stored logical ratios as percentages (e.g. 50 for 50/50).
        if (n > 1 && n <= 100) return n / 100;
        return n;
      })
      .filter((n) => n >= 0 && n <= 1);
    return Array.from(new Set(coerced)).sort((a, b) => a - b);
  };

  for (const [leafId, cfg] of Object.entries(raw as Record<string, any>)) {
    if (!cfg || typeof cfg !== 'object') continue;
    const h = sanitize((cfg as any).h);
    const v = sanitize((cfg as any).v);
    if (h.length || v.length) out[String(leafId)] = { h, v };
  }
  return Object.keys(out).length ? out : null;
}

/** Persist the chosen style code into WG payloads; renderers can fall back for unsupported visuals. */
function handleStyleCodeToWg(code: string, isDoor: boolean): string {
  const c = String(code || '').trim().toLowerCase();
  // WG uses "upvc" to represent the longer door-handle geometry.
  if (isDoor) {
    if (c === 'teardrop') return 'teardrop';
    return 'upvc';
  }
  return c || 'offset';
}

function getHandleStyleLabel(code: string): string {
  const c = String(code || '').trim().toLowerCase();
  return HANDLE_STYLE_OPTIONS.find((o) => o.code === c)?.label || String(code || '').trim();
}

function parseStoredHandleStyleCode(rawCode: unknown, rawLabel?: unknown): HandleStyleCode {
  const code = String(rawCode ?? '').trim().toLowerCase();
  if (
    code === 'lever' ||
    code === 'teardrop' ||
    code === 'monkey-tail' ||
    code === 'pear-drop' ||
    code === 'bulb-end' ||
    code === 'cockspur' ||
    code === 'inline' ||
    code === 'offset' ||
    code === 'other' ||
    code === 'na'
  ) {
    return code as HandleStyleCode;
  }

  const label = String(rawLabel ?? '').trim().toLowerCase();
  if (label.includes('monkey')) return 'monkey-tail';
  if (label.includes('pear')) return 'pear-drop';
  if (label.includes('bulb')) return 'bulb-end';
  if (label.includes('cockspur')) return 'cockspur';
  if (label.includes('inline')) return 'inline';
  if (label.includes('offset')) return 'offset';
  if (label.includes('tear')) return 'teardrop';
  if (label.includes('lever')) return 'lever';
  if (label.includes('other')) return 'other';
  if (label.includes('n/a') || label === 'na' || label.includes('not applicable')) return 'na';

  return 'offset';
}

function normalizeHandleStyleForType(code: HandleStyleCode, isDoor: boolean): HandleStyleCode {
  if (isDoor) {
    if (code === 'na') return 'na';
    return code === 'teardrop' ? 'teardrop' : 'lever';
  }
  if (code === 'na') return 'na';
  return WINDOW_HANDLE_STYLE_OPTIONS.some((o) => o.code === code) ? code : 'offset';
}

const isDoorTypeId = (typeId: unknown): boolean => {
  const id = String(typeId || '').trim().toLowerCase();
  return isDoorType({ id, name: QUOTE_WINDOW_TYPES.find((t) => t.id === id)?.name });
};

/** Keep the wizard's chosen type. Payload inference defaults to casement and must not replace Door, Flush Sash, or Other. */
export function preserveSelectedWindowTypeId(current: string | null | undefined, inferred: string | null | undefined): string | null {
  const chosen = String(current || '').trim();
  if (chosen) return chosen;
  const next = String(inferred || '').trim();
  return next || null;
}

function mmToPx(mm: number): number {
  return Math.round((Number(mm) || 0) / WINDOW_GENERATOR_PX2MM);
}

function clampFenestrationDimensionMm(value: number | null, kind: 'width' | 'height'): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  const max = kind === 'width' ? MAX_FENESTRATION_WIDTH_MM : MAX_FENESTRATION_HEIGHT_MM;
  return Math.min(max, Math.max(MIN_FENESTRATION_DIM_MM, Number(value)));
}

/**
 * Merges current Visual Designer modal state into a WG payload so that
 * "Configure in Window Generator" opens with the same colour, glass, handles, vents, cill, etc.
 */
export function mergeModalStateIntoPayload(
  base: WindowGeneratorPayload | null,
  state: {
    frameColorCode: string;
    frameColorExtCode?: string;
    frameColorIntCode?: string;
    frameColorExtName?: string;
    frameColorIntName?: string;
    frameColorExtHex?: string;
    frameColorIntHex?: string;
    preferExplicitFrameValues?: boolean;
    designDimsOn?: boolean;
    glassTypeCode: 'clear' | 'obscured';
    widthMm: string;
    heightMm: string;
    handleColorCode: string;
    handleStyleCode: string;
    trickleVents: 'yes' | 'no';
    trickleVentsPosition: TrickleVentsSemanticPosition;
    cillOption: 'none' | '150' | '180' | 'stub';
    extTopMm: string;
    extBottomMm: string;
    extLeftMm: string;
    extRightMm: string;
    georgianOn?: 'yes' | 'no';
    georgianCols: string;
    georgianRows: string;
    georgianWidthMm: string;
    georgianOffXPct: string;
    georgianOffYPct: string;
    glassStyleTilePath: string;
    astragalOn?: 'yes' | 'no';
    astragalCols?: string;
    astragalRows?: string;
    astragalWidthMm?: string;
    astragalOffXPct?: string;
    astragalOffYPct?: string;
    jointStyle?: 'mitre-45' | 'timber-weld';
    windowHingeTypeCode?: WindowHingeTypeCode;
    astragalBarSide?: BarSideCode;
    isDoor?: boolean;
    forceNoOpenings?: boolean;
    logicalDivsBackup?: Record<string, any> | null;
  }
): WindowGeneratorPayload | null {
  if (!base || !base.root || base.w == null || base.h == null) return base;
  // Deep clone: Window Generator editor mutates payload trees in-place.
  // If we pass through shared library payload/root refs, editing one line can affect others.
  const baseCopy = deepCloneJson(base);
  const baseCopyAny = baseCopy as any;
  if (state.forceNoOpenings) {
    // Fixed-style quote items must not inherit template opener markers.
    // Keep the structure, but clear any triangular/sliding opener maps.
    baseCopyAny.opens = {};
    baseCopyAny.slideOpens = {};
  }
  const backupLogicalDivs = coerceLogicalDivsSnapshot(state.logicalDivsBackup);
  if (
    backupLogicalDivs &&
    typeof backupLogicalDivs === 'object'
  ) {
    // Saved line-level options are the source of truth for reopening existing VC/QW items.
    // Prefer them over stale template payload snapshots when present.
    baseCopyAny.logicalDivs = deepCloneJson(backupLogicalDivs);
  }
  const pres = (baseCopy.presentation && typeof baseCopy.presentation === 'object') ? { ...baseCopy.presentation } : {};
  const normalizeHex = (raw: unknown): string | null => {
    const value = String(raw || '').trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(value)) {
      const expanded = value.split('').map((c) => `${c}${c}`).join('');
      return `#${expanded.toUpperCase()}`;
    }
    if (/^[0-9a-f]{6}$/i.test(value)) return `#${value.toUpperCase()}`;
    return null;
  };
  const toNum = (s: string) => (String(s || '').trim() ? Number(s) : null);
  const collectLeafIds = (node: any, out: string[]) => {
    if (!node || typeof node !== 'object') return;
    if (node.split === 'none' && node.id != null) {
      out.push(String(node.id));
      return;
    }
    if (Array.isArray(node.children)) {
      for (const ch of node.children) collectLeafIds(ch, out);
    }
    if (node.type === 'rectUnion' && Array.isArray(node.regions)) {
      for (const rg of node.regions) collectLeafIds(rg?.root, out);
    }
  };

  const explicitExtName = String(state.frameColorExtName || '').trim();
  const explicitIntName = String(state.frameColorIntName || '').trim();
  const explicitExtHex = normalizeHex(state.frameColorExtHex);
  const explicitIntHex = normalizeHex(state.frameColorIntHex);
  const hasExplicitFrameValues = !!(explicitExtName || explicitIntName || explicitExtHex || explicitIntHex);
  if (hasExplicitFrameValues) {
    if (explicitExtName) pres.frameNameExt = explicitExtName;
    if (explicitIntName) pres.frameNameInt = explicitIntName;
    if (explicitExtHex) pres.frameColExt = explicitExtHex;
    if (explicitIntHex) pres.frameColInt = explicitIntHex;
  }

  const shouldApplyCodeMapping = !hasExplicitFrameValues || state.preferExplicitFrameValues !== true;
  if (
    shouldApplyCodeMapping &&
    (state.frameColorExtCode || state.frameColorIntCode) &&
    (state.frameColorExtCode || state.frameColorIntCode)
  ) {
    const extCode = state.frameColorExtCode || state.frameColorCode;
    const intCode = state.frameColorIntCode || state.frameColorCode;
    const fExt = FRAME_CODE_TO_WG[extCode];
    const fInt = FRAME_CODE_TO_WG[intCode];
    if (fExt) {
      pres.frameNameExt = fExt.frameNameExt;
      pres.frameColExt = fExt.frameColExt;
    }
    if (fInt) {
      pres.frameNameInt = fInt.frameNameInt;
      pres.frameColInt = fInt.frameColInt;
    }
  } else if (shouldApplyCodeMapping && state.frameColorCode && FRAME_CODE_TO_WG[state.frameColorCode]) {
    const f = FRAME_CODE_TO_WG[state.frameColorCode];
    pres.frameNameExt = f.frameNameExt;
    pres.frameNameInt = f.frameNameInt;
    pres.frameColExt = f.frameColExt;
    pres.frameColInt = f.frameColInt;
  }
  const tilePath = state.glassStyleTilePath.trim();
  // Window Generator: satin is modelled as glassType='satin'. Patterned glasses use a tile overlay,
  // which only renders when glassType !== 'satin', so force clear when tile is selected.
  pres.glassType = tilePath ? 'clear' : (state.glassTypeCode === 'obscured' ? 'satin' : 'clear');
  pres.glassStyleTile = tilePath ? tilePath : null;
  if (state.designDimsOn != null) pres.designDimsOn = !!state.designDimsOn;
  if (state.jointStyle) pres.jointStyle = state.jointStyle;

  if (state.handleColorCode && HANDLE_CODE_TO_WG[state.handleColorCode]) {
    const h = HANDLE_CODE_TO_WG[state.handleColorCode];
    pres.handleCol = h.handleCol;
    pres.handleName = h.handleName;
  }
  pres.handleStyle = handleStyleCodeToWg(state.handleStyleCode, !!state.isDoor);
  {
    const existingKinds =
      (pres as any).paneHandleKinds && typeof (pres as any).paneHandleKinds === 'object'
        ? { ...(pres as any).paneHandleKinds }
        : {};
    if (state.isDoor && Object.keys(existingKinds).length === 0) {
      const leafIds: string[] = [];
      collectLeafIds((baseCopy as any).root, leafIds);
      const opensMap = ((baseCopy as any).opens && typeof (baseCopy as any).opens === 'object')
        ? ((baseCopy as any).opens as Record<string, any>)
        : {};
      for (const lid of leafIds) {
        const openVal = String(opensMap[lid] ?? '').toLowerCase();
        if (openVal === 'panel' || openVal === 'dummy') continue;
        existingKinds[lid] = 'door';
      }
    }
    if (Object.keys(existingKinds).length) {
      (pres as any).paneHandleKinds = existingKinds;
    }
  }

  const preservePresetVentOverrides = (position: 'sash' | 'jamb') => {
    const source = (pres.paneVents && typeof pres.paneVents === 'object') ? pres.paneVents : {};
    const next: Record<string, any> = {};
    for (const [key, value] of Object.entries(source as Record<string, any>)) {
      if (key === '_centre') {
        if (position === 'jamb' && value === true) next[key] = true;
        continue;
      }
      if (!value || typeof value !== 'object') continue;
      const explicitOff = (value as any)[position] === false;
      if (explicitOff) next[key] = { [position]: false };
    }
    return next;
  };

  if (state.trickleVents === 'no') {
    pres.ventPos = 'none';
    pres.paneVents = {};
  } else if (state.trickleVentsPosition === 'custom') {
    // Preserve per-pane custom vent placements from the base payload.
    pres.ventPos = 'none';
    if (!pres.paneVents || typeof pres.paneVents !== 'object') pres.paneVents = {};
  } else {
    const resolvedPosition = resolveTricklePositionAfterTopExtenderRemoved(
      state.trickleVentsPosition,
      state.extTopMm,
    );
    const nextPreset = isExtenderTrickleVentActive(state.trickleVentsPosition, state.extTopMm)
      ? 'extender'
      : semanticTricklePositionToVentPos(resolvedPosition);
    pres.ventPos = nextPreset;
    // WG supports "all except these panes" for preset vent modes via explicit false
    // paneVents overrides. Preserve those, but drop stale custom true overrides.
    if (nextPreset === 'none') {
      pres.paneVents = {};
    } else {
      pres.paneVents = preservePresetVentOverrides(nextPreset === 'sash' ? 'sash' : 'jamb');
    }
  }

  pres.cillOn = state.cillOption !== 'none';
  pres.cillStub = state.cillOption === 'stub';
  pres.cillDepth = state.cillOption === '150' ? 150 : state.cillOption === '180' ? 180 : 0;

  const extT = toNum(state.extTopMm);
  const extB = toNum(state.extBottomMm);
  const extL = toNum(state.extLeftMm);
  const extR = toNum(state.extRightMm);
  // Use raw form mm during live merge — minimum for Extender trickle is enforced on blur/save/selection only.
  if (extT != null) pres.extTop = extT;
  else if (!isExtenderTrickleVentActive(state.trickleVentsPosition, state.extTopMm)) pres.extTop = 0;
  if (extB != null) pres.extBot = extB;
  if (extL != null) pres.extLeft = extL;
  if (extR != null) pres.extRight = extR;

  if (state.georgianOn != null) pres.gBarOn = state.georgianOn === 'yes';
  const gCols = toNum(state.georgianCols);
  const gRows = toNum(state.georgianRows);
  const gW = toNum(state.georgianWidthMm);
  const gOx = toNum(state.georgianOffXPct);
  const gOy = toNum(state.georgianOffYPct);
  if (gCols != null) pres.gBarCols = gCols;
  if (gRows != null) pres.gBarRows = gRows;
  if (gW != null) pres.gBarWidth = gW;
  if (gOx != null) pres.gBarOffX = gOx;
  if (gOy != null) pres.gBarOffY = gOy;

  if (state.astragalOn != null) pres.aBarOn = state.astragalOn === 'yes';
  const aCols = toNum(state.astragalCols || '');
  const aRows = toNum(state.astragalRows || '');
  const aW = toNum(state.astragalWidthMm || '');
  const aOx = toNum(state.astragalOffXPct || '');
  const aOy = toNum(state.astragalOffYPct || '');
  if (aCols != null) pres.aBarCols = aCols;
  if (aRows != null) pres.aBarRows = aRows;
  if (aW != null) pres.aBarWidth = aW;
  if (aOx != null) pres.aBarOffX = aOx;
  if (aOy != null) pres.aBarOffY = aOy;

  // Spec-only (no visual impact) fields stored in payload.presentation
  if (state.windowHingeTypeCode) pres.windowHingeTypeCode = String(state.windowHingeTypeCode);
  if (state.astragalBarSide) pres.astragalBarSide = String(state.astragalBarSide);

  const wMm = clampFenestrationDimensionMm(toNum(state.widthMm), 'width');
  const hMm = clampFenestrationDimensionMm(toNum(state.heightMm), 'height');
  // `state.widthMm/heightMm` represent overall drawn dimensions (like WG's drawW/drawH).
  // Payload `w/h` represent the base window size (wW/wH) that *may exclude* extenders/cill
  // when extLock/cillLock are unlocked. Convert back accordingly to avoid “skewed” WG opens.
  const extLock = pres.extLock != null ? !!pres.extLock : true;
  const cillLock = pres.cillLock != null ? !!pres.cillLock : true;
  const extLeft = Number(pres.extLeft || 0) || 0;
  const extRight = Number(pres.extRight || 0) || 0;
  const extTop = Number(pres.extTop || 0) || 0;
  const extBot = Number(pres.extBot || 0) || 0;
  const totalExtW = extLeft + extRight;
  const totalExtH = extTop + extBot;
  const cillOn = !!pres.cillOn;
  const cillHeight = Number(pres.cillHeight || 30) || 30;

  const baseWmm = wMm != null ? (extLock ? wMm : Math.max(0, wMm - totalExtW)) : null;
  const baseHmm = hMm != null
    ? Math.max(0, hMm - (extLock ? 0 : totalExtH) - ((cillOn && !cillLock) ? cillHeight : 0))
    : null;

  const wPx = baseWmm != null ? mmToPx(baseWmm) : baseCopy.w;
  const hPx = baseHmm != null ? mmToPx(baseHmm) : baseCopy.h;

  return {
    ...baseCopy,
    w: wPx,
    h: hPx,
    presentation: pres,
  };
}

type VisualDesignerMetaV1 = {
  feature: typeof VISUAL_DESIGNER_FEATURE;
  schema_version: number;
  quote_mode: true;
  /** Allows switching between v1/v2 implementations. */
  designer_version?: 'v1' | 'v2_lite' | 'v2';
  window_type?: {
    id: string;
    name: string;
  } | null;
  window_material?: {
    id: 'upvc' | 'aluminium' | 'timber' | 'composite';
    name: string;
  } | null;
  window_style: {
    id: number | string;
    name: string;
    category: WindowStyleConfig['category'];
    preview_path?: string;
    email_path?: string;
    /** v2: separate internal/external outputs (fallback remains preview_path/email_path). */
    preview_path_external?: string;
    preview_path_internal?: string;
    email_path_external?: string;
    email_path_internal?: string;
    /** v2: 4-up outputs (presentation + design, each external/internal). */
    preview_path_design_external?: string;
    preview_path_design_internal?: string;
    preview_path_presentation_external?: string;
    preview_path_presentation_internal?: string;
    preview_path_bay_schematic?: string;
    preview_path_plan_external?: string;
    email_path_design_external?: string;
    email_path_design_internal?: string;
    email_path_presentation_external?: string;
    email_path_presentation_internal?: string;
    email_path_bay_schematic?: string;
    /** Set when style comes from Window Generator library */
    source?: 'window_generator' | 'upload';
    templatePayload?: WindowGeneratorPayload;
    /** Set when source=upload */
    original_filename?: string | null;
    original_filename_internal?: string | null;
  };
  specs: {
    reference?: string | null;
    width_mm?: number | null;
    height_mm?: number | null;
    variant?: string | null;
    // Stored as user-facing label; code also stored for consistency.
    variant_code?: ProfileVariantCode | null;
    glass_type?: string | null;
    glass_type_code?: 'clear' | 'obscured' | null;
    beading_type?: string | null;
    beading_type_code?: ProfileVariantCode | null;
    frame_color?: string | null;
    frame_color_code?: string | null;
    frame_color_external?: string | null;
    frame_color_code_external?: string | null;
    frame_color_internal?: string | null;
    frame_color_code_internal?: string | null;
    notes?: string | null;
    options: {
      trickle_vents?: boolean;
      trickle_vents_position?: 'sash' | 'head' | 'custom' | 'extender' | null;
      handle_color?: string | null;
      handle_color_code?: string | null;
      handle_style?: string | null;
      handle_style_code?: HandleStyleCode | null;
      design_dims_on?: boolean | null;
      // v2 extras derived from Window Generator (for downstream display/mapping)
      ext_top_mm?: number | null;
      ext_bottom_mm?: number | null;
      ext_left_mm?: number | null;
      ext_right_mm?: number | null;
      ext_lock?: boolean | null;
      georgian_bars?: boolean | null;
      georgian_cols?: number | null;
      georgian_rows?: number | null;
      georgian_width_mm?: number | null;
      georgian_offset_x_pct?: number | null;
      georgian_offset_y_pct?: number | null;
      astragal_bars?: boolean | null;
      astragal_cols?: number | null;
      astragal_rows?: number | null;
      astragal_width_mm?: number | null;
      astragal_offset_x_pct?: number | null;
      astragal_offset_y_pct?: number | null;
      glass_style_tile?: string | null;
      // Spec-only (no visual impact): window hinge type (legacy VD parameter)
      window_hinge_type_code?: WindowHingeTypeCode | null;
      window_hinge_type?: string | null;
      // Spec-only (no visual impact): astragal bar side selection
      astragal_bar_side?: BarSideCode | null;
      astragal_bar_side_label?: string | null;
      joint_style?: 'mitre-45' | 'timber-weld' | null;
      // Door-only options (uPVC doors in quote wizard)
      hinge_placement?: DoorHingePlacementCode | null;
      open_direction?: DoorOpenDirectionCode | null;
      // French Doors, Bi-Folds and Sliders. Sides are viewed from outside.
      master_leaf?: DoorSideCode | null;
      panel_count?: number | null;
      stack_direction?: StackDirectionCode | null;
      traffic_door?: TrafficDoorCode | null;
      // Every door type, including Door & Window. No effect on the drawing.
      threshold?: ThresholdCode | null;
      // Every item. Supplier outputs only.
      glass_specification?: GlassSpecificationCode | null;
      /**
       * Quote mode cill selector (mirrors the simplified choice set).
       * - none: no cill
       * - 150 / 180: included cill sizes
       * - stub: included stub cill
       */
      cill_option?: 'none' | '150' | '180' | 'stub' | null;
      // Back-compat fields (derived from cill_option)
      cill_included?: boolean;
      cill_required?: boolean;
      cill_type?: 'Regular' | 'Stub' | '' | null;
      cill_height_mm?: number | null;
      bay_tangent_width_mm?: number | null;
      bay_legacy_width_mm?: number | null;
      bay_projection_depth_mm?: number | null;
      bay_facet_widths_mm?: number[] | null;
      bay_joint_angles_deg?: number[] | null;
      frame_color_hex_external?: string | null;
      frame_color_hex_internal?: string | null;
    };
  };
  survey?: {
    source?: 'final' | 'first' | 'intermediate';
    first_survey_reference?: {
      width_mm?: number | null;
      height_mm?: number | null;
      specs?: any;
    } | null;
  };
  created_at: string;
};

function isVisualDesignerLine(line: DocumentLineInput): boolean {
  return (line as any)?.metaJson?.feature === VISUAL_DESIGNER_FEATURE;
}

function toPublicImageUrl(path?: string): string {
  if (!path) return '';
  const p = String(path);
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (p.startsWith('/')) return p;
  return `/${p}`;
}

function dedupeStrings(items: string[]): string[] {
  const out: string[] = [];
  for (const i of items) {
    const v = String(i || '').trim();
    if (!v) continue;
    if (!out.includes(v)) out.push(v);
  }
  return out;
}

function buildUpvcDoorImageCandidates(args: {
  stylePreviewPath: string;
  hingePlacement: DoorHingePlacementCode;
  handleColorCode: string;
}): string[] {
  const stylePreviewPath = String(args.stylePreviewPath || '');
  if (!stylePreviewPath) return [];

  // Supports both:
  // - style-n/lh|rh/glazed|panel/...
  // - style-n/glazed|panel/... (no handed folders)
  const m = stylePreviewPath.match(/images\/windows\/Doors\/uPVC\/(style-\d+)\/(?:(lh|rh)\/)?(glazed|panel)\//i);
  if (!m) return [stylePreviewPath];

  const styleKey = m[1]; // style-1, style-2, ...
  const variantDir = m[3].toLowerCase() === 'panel' ? 'panel' : 'glazed';
  const hingeDir = args.hingePlacement === 'right' ? 'rh' : 'lh';

  const baseDirNoHand = `images/windows/Doors/uPVC/${styleKey}/${variantDir}/`;
  const baseDirHanded = `images/windows/Doors/uPVC/${styleKey}/${hingeDir}/${variantDir}/`;

  // If this style doesn't have lh/rh folders, prefer non-handed paths.
  const hasHandedFolders = !!m[2];
  const primaryBaseDir = hasHandedFolders ? baseDirHanded : baseDirNoHand;

  const hc = String(args.handleColorCode || '').toLowerCase();
  const preferredHandle =
    hc.includes('chrome') ? 'chrome'
    : hc.includes('white') ? 'white'
    : '';

  const styleNumber = (styleKey.match(/\d+/) || [])[0] || '';

  const candidates: string[] = [
    preferredHandle ? `${primaryBaseDir}handle-${preferredHandle}.png` : '',
    `${primaryBaseDir}handle-chrome.png`,
    `${primaryBaseDir}handle-white.png`,
    // Seen in some folders as a stopgap filename; harmless if missing.
    `${primaryBaseDir}handle-chrome - white.png`,
    // Style-5 currently has flipped filenames in RH; these help as a fallback.
    styleNumber && variantDir === 'glazed' ? `${primaryBaseDir}uPVC-Door-Style-${styleNumber}---Glass-flipped.png` : '',
    styleNumber ? `${primaryBaseDir}uPVC-Door-Style-${styleNumber}-flipped.png` : '',
  ];

  // If this style *does* have handed folders, we can try swapping hinge.
  if (hasHandedFolders) {
    const swappedHinge = stylePreviewPath.replace(/\/(lh|rh)\//i, `/${hingeDir}/`);
    candidates.push(swappedHinge);
  } else {
    // If not handed, still try the handed path as a fallback (in case the previewPath is unhanded but folders exist).
    candidates.push(
      preferredHandle ? `${baseDirHanded}handle-${preferredHandle}.png` : '',
      `${baseDirHanded}handle-chrome.png`,
      `${baseDirHanded}handle-white.png`,
    );
  }

  candidates.push(stylePreviewPath);

  return dedupeStrings(candidates);
}

function canLoadImage(url: string, timeoutMs = 1500): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const img = new Image();
    const t = window.setTimeout(() => {
      if (done) return;
      done = true;
      resolve(false);
    }, timeoutMs);
    img.onload = () => {
      if (done) return;
      done = true;
      window.clearTimeout(t);
      resolve(true);
    };
    img.onerror = () => {
      if (done) return;
      done = true;
      window.clearTimeout(t);
      resolve(false);
    };
    img.src = url;
  });
}

async function pickFirstLoadableDoorPath(paths: string[]): Promise<string> {
  for (const p of paths) {
    const ok = await canLoadImage(toPublicImageUrl(p));
    if (ok) return p;
  }
  return paths[0] || '';
}

function pxToMm(px: number): number {
  return Math.round((Number(px) || 0) * WINDOW_GENERATOR_PX2MM);
}

function tryMatchFrameColourCode(label: string): string | null {
  const norm = String(label || '').trim().toLowerCase();
  if (!norm) return null;
  const exact = FRAME_COLOUR_OPTIONS.find(o => String(o.label).trim().toLowerCase() === norm);
  if (exact) return exact.code;
  const loose = FRAME_COLOUR_OPTIONS.find(o => norm.includes(String(o.label).trim().toLowerCase().split('(')[0].trim()));
  return loose?.code ?? null;
}

function tryMatchFrameSideColourCodeFromName(name: string): string | null {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return null;
  const match = Object.entries(FRAME_CODE_TO_WG).find(([code, f]) => {
    if (String(code).includes('-on-')) return false;
    return String(f.frameNameExt || '').trim().toLowerCase() === n
      && String(f.frameNameInt || '').trim().toLowerCase() === n;
  });
  return match ? match[0] : null;
}

function tryFindCombinedFrameColourCode(extCode: string, intCode: string): string | null {
  const e = FRAME_CODE_TO_WG[String(extCode || '')];
  const i = FRAME_CODE_TO_WG[String(intCode || '')];
  if (!e || !i) return null;
  const extName = String(e.frameNameExt || '').trim().toLowerCase();
  const intName = String(i.frameNameInt || '').trim().toLowerCase();
  if (!extName || !intName) return null;
  const hit = Object.entries(FRAME_CODE_TO_WG).find(([code, f]) => {
    if (!String(code).includes('-on-')) return false;
    return String(f.frameNameExt || '').trim().toLowerCase() === extName
      && String(f.frameNameInt || '').trim().toLowerCase() === intName;
  });
  return hit ? hit[0] : null;
}

function applyDerivedToUi(args: {
  derived: ReturnType<typeof deriveFromWindowGeneratorPayload>;
  setWidthMm: (v: string) => void;
  setHeightMm: (v: string) => void;
  setGlassTypeCode: (v: 'clear' | 'obscured') => void;
  setFrameColorCode: (v: string) => void;
  setFrameColorExtCode: (v: string) => void;
  setFrameColorIntCode: (v: string) => void;
  setFrameColorExtCustomRal?: (v: string) => void;
  setFrameColorIntCustomRal?: (v: string) => void;
  setFrameColorExtCustomHex?: (v: string) => void;
  setFrameColorIntCustomHex?: (v: string) => void;
  setDesignDimsOn: (v: boolean) => void;
  setHandleColorCode: (v: string) => void;
  setHandleStyleCode: (v: HandleStyleCode) => void;
  setTrickleVents: (v: 'yes' | 'no') => void;
  setTrickleVentsPosition: (v: TrickleVentsSemanticPosition) => void;
  setCillOption: (v: 'none' | '150' | '180' | 'stub') => void;
  setExtTopMm: (v: string) => void;
  setExtBottomMm: (v: string) => void;
  setExtLeftMm: (v: string) => void;
  setExtRightMm: (v: string) => void;
  setGeorgianOn: (v: 'yes' | 'no') => void;
  setGeorgianCols: (v: string) => void;
  setGeorgianRows: (v: string) => void;
  setGeorgianWidthMm: (v: string) => void;
  setGeorgianOffXPct: (v: string) => void;
  setGeorgianOffYPct: (v: string) => void;
  setGlassStyleTilePath: (v: string) => void;
  setAstragalOn: (v: 'yes' | 'no') => void;
  setAstragalCols: (v: string) => void;
  setAstragalRows: (v: string) => void;
  setAstragalWidthMm: (v: string) => void;
  setAstragalOffXPct: (v: string) => void;
  setAstragalOffYPct: (v: string) => void;
  setJointStyle: (v: 'mitre-45' | 'timber-weld') => void;
  setWindowHingeTypeCode?: (v: WindowHingeTypeCode) => void;
  setAstragalBarSide?: (v: BarSideCode) => void;
  setSelectedTypeId?: (next: string | null | ((prev: string | null) => string | null)) => void;
}) {
  const d = args.derived;
  // Payload inference defaults to casement and was replacing Door, Flush Sash, and Other.
  // Keep the type chosen on step 1 / the step 4 selector; only fill it when nothing is selected yet.
  if (args.setSelectedTypeId && d.window_type_id) {
    const inferred = d.window_type_id;
    args.setSelectedTypeId((prev) => preserveSelectedWindowTypeId(prev, inferred));
  }
  args.setWidthMm(d.width_mm != null ? String(d.width_mm) : '');
  args.setHeightMm(d.height_mm != null ? String(d.height_mm) : '');
  args.setGlassTypeCode(d.glass_type_code === 'obscured' ? 'obscured' : 'clear');
  const extCode = tryMatchFrameSideColourCodeFromName(String(d.frame_color_external || '')) || null;
  const intCode = tryMatchFrameSideColourCodeFromName(String(d.frame_color_internal || '')) || null;
  const extCustomRal = normalizeRalText(String(d.frame_color_external || ''));
  const intCustomRal = normalizeRalText(String(d.frame_color_internal || ''));
  const extCustomHex = parseHexColour((d as any)?.extras?.frame_color_hex_external) || '';
  const intCustomHex = parseHexColour((d as any)?.extras?.frame_color_hex_internal) || '';
  const tryMatchSideCodeFromRalValue = (ralValue: string, side: 'ext' | 'int'): string | null => {
    if (!ralValue) return null;
    const hit = Object.entries(FRAME_RAL_BY_SIDE_CODE).find(([code, ral]) => {
      if (!FRAME_SIDE_COLOUR_OPTIONS.some((o) => o.code === code)) return false;
      return normalizeRalText(ral) === ralValue;
    });
    if (!hit) return null;
    const nextCode = hit[0];
    const frame = FRAME_CODE_TO_WG[nextCode];
    if (!frame) return null;
    const expectedHex = parseHexColour(side === 'ext' ? frame.frameColExt : frame.frameColInt);
    const incomingHex = side === 'ext' ? extCustomHex : intCustomHex;
    if (!incomingHex || !expectedHex) return null;
    if (incomingHex !== expectedHex) return null;
    return nextCode;
  };
  const extCodeResolved = extCode || tryMatchSideCodeFromRalValue(extCustomRal, 'ext');
  const intCodeResolved = intCode || tryMatchSideCodeFromRalValue(intCustomRal, 'int');

  if (extCodeResolved) args.setFrameColorExtCode(extCodeResolved);
  if (intCodeResolved) args.setFrameColorIntCode(intCodeResolved);
  if (extCodeResolved) {
    args.setFrameColorExtCustomRal?.('');
    args.setFrameColorExtCustomHex?.('');
  }
  if (intCodeResolved) {
    args.setFrameColorIntCustomRal?.('');
    args.setFrameColorIntCustomHex?.('');
  }
  if (!extCodeResolved && (extCustomRal || extCustomHex)) {
    args.setFrameColorExtCode(CUSTOM_FRAME_COLOUR_CODE);
    args.setFrameColorExtCustomRal?.(extCustomRal);
    args.setFrameColorExtCustomHex?.(extCustomHex);
  }
  if (!intCodeResolved && (intCustomRal || intCustomHex)) {
    args.setFrameColorIntCode(CUSTOM_FRAME_COLOUR_CODE);
    args.setFrameColorIntCustomRal?.(intCustomRal);
    args.setFrameColorIntCustomHex?.(intCustomHex);
  }
  if (extCodeResolved && intCodeResolved) {
    if (extCodeResolved === intCodeResolved) args.setFrameColorCode(extCodeResolved);
    else args.setFrameColorCode(tryFindCombinedFrameColourCode(extCodeResolved, intCodeResolved) || extCodeResolved);
  } else {
    const fcCode = tryMatchFrameColourCode(String(d.frame_color || ''));
    if (fcCode) args.setFrameColorCode(fcCode);
    if (fcCode) {
      if (!extCodeResolved) args.setFrameColorExtCode(String(fcCode));
      if (!intCodeResolved) args.setFrameColorIntCode(String(fcCode));
    }
  }
  if (d.handle_color.code) args.setHandleColorCode(d.handle_color.code);
  if (d.handle_style_label) args.setHandleStyleCode(parseStoredHandleStyleCode(d.handle_style_label, d.handle_style_label));
  args.setDesignDimsOn(d.design_dims_on === true);
  args.setTrickleVents(d.trickle.enabled === true ? 'yes' : 'no');
  args.setTrickleVentsPosition(
    d.trickle.position === 'custom'
      ? 'custom'
      : d.trickle.position === 'head'
        ? 'head'
        : d.trickle.position === 'extender'
          ? 'extender'
          : 'sash'
  );
  args.setCillOption((d.cill_option as any) || 'none');
  args.setExtTopMm(String(d.extras.ext_top_mm || 0));
  args.setExtBottomMm(String(d.extras.ext_bottom_mm || 0));
  args.setExtLeftMm(String(d.extras.ext_left_mm || 0));
  args.setExtRightMm(String(d.extras.ext_right_mm || 0));
  args.setGeorgianOn(d.extras.georgian_bars === true ? 'yes' : 'no');
  args.setGeorgianCols(d.extras.georgian_cols != null ? String(d.extras.georgian_cols) : '3');
  args.setGeorgianRows(d.extras.georgian_rows != null ? String(d.extras.georgian_rows) : '4');
  args.setGeorgianWidthMm(d.extras.georgian_width_mm != null ? String(d.extras.georgian_width_mm) : '18');
  args.setGeorgianOffXPct(d.extras.georgian_offset_x_pct != null ? String(d.extras.georgian_offset_x_pct) : '0');
  args.setGeorgianOffYPct(d.extras.georgian_offset_y_pct != null ? String(d.extras.georgian_offset_y_pct) : '0');
  args.setGlassStyleTilePath(d.extras.glass_style_tile ? String(d.extras.glass_style_tile) : '');
  args.setAstragalOn(d.extras.astragal_bars === true ? 'yes' : 'no');
  args.setAstragalCols(d.extras.astragal_cols != null ? String(d.extras.astragal_cols) : '3');
  args.setAstragalRows(d.extras.astragal_rows != null ? String(d.extras.astragal_rows) : '4');
  args.setAstragalWidthMm(d.extras.astragal_width_mm != null ? String(d.extras.astragal_width_mm) : '18');
  args.setAstragalOffXPct(d.extras.astragal_offset_x_pct != null ? String(d.extras.astragal_offset_x_pct) : '0');
  args.setAstragalOffYPct(d.extras.astragal_offset_y_pct != null ? String(d.extras.astragal_offset_y_pct) : '0');
  args.setJointStyle(d.extras.joint_style === 'timber-weld' ? 'timber-weld' : 'mitre-45');
  if (args.setWindowHingeTypeCode && d.extras.window_hinge_type_code) {
    args.setWindowHingeTypeCode(d.extras.window_hinge_type_code);
  }
  if (args.setAstragalBarSide && d.extras.astragal_bar_side) {
    args.setAstragalBarSide(d.extras.astragal_bar_side);
  }
}

function deriveFromWindowGeneratorPayload(payload: WindowGeneratorPayload): {
  window_type_id: 'casement' | 'tilt-and-turn' | 'sliding-sash' | 'upvc-door' | 'door-window' | null;
  width_mm: number | null;
  height_mm: number | null;
  glass_type: 'Clear' | 'Obscured' | null;
  glass_type_code: 'clear' | 'obscured' | null;
  frame_color: string | null;
  frame_color_external: string | null;
  frame_color_internal: string | null;
  design_dims_on: boolean | null;
  handle_color: { label: string | null; code: string | null };
  handle_style_label: string | null;
  trickle: { enabled: boolean | null; position: 'sash' | 'head' | 'custom' | 'extender' | null };
  cill_option: 'none' | '150' | '180' | 'stub' | null;
  cill_derived: { included: boolean; type: 'Regular' | 'Stub' | null; height_mm: number | null };
  extras: {
    ext_top_mm: number | null;
    ext_bottom_mm: number | null;
    ext_left_mm: number | null;
    ext_right_mm: number | null;
    ext_lock: boolean | null;
    georgian_bars: boolean | null;
    georgian_cols: number | null;
    georgian_rows: number | null;
    georgian_width_mm: number | null;
    georgian_offset_x_pct: number | null;
    georgian_offset_y_pct: number | null;
    astragal_bars: boolean | null;
    astragal_cols: number | null;
    astragal_rows: number | null;
    astragal_width_mm: number | null;
    astragal_offset_x_pct: number | null;
    astragal_offset_y_pct: number | null;
    glass_style_tile: string | null;
    window_hinge_type_code: WindowHingeTypeCode | null;
    window_hinge_type: string | null;
    astragal_bar_side: BarSideCode | null;
    astragal_bar_side_label: string | null;
    joint_style: 'mitre-45' | 'timber-weld' | null;
    bay_tangent_width_mm: number | null;
    bay_legacy_width_mm: number | null;
    bay_projection_depth_mm: number | null;
    bay_facet_widths_mm: number[] | null;
    bay_joint_angles_deg: number[] | null;
    frame_color_hex_external: string | null;
    frame_color_hex_internal: string | null;
  };
} {
  const p: any = payload || {};
  const pres: any = p.presentation || {};
  const isBay = isBayLikePayload(p);
  const windowTypeId: 'casement' | 'tilt-and-turn' | 'sliding-sash' | 'upvc-door' | 'door-window' | null = (() => {
    const opens = (p.opens && typeof p.opens === 'object') ? p.opens : {};
    const slideOpens = (p.slideOpens && typeof p.slideOpens === 'object')
      ? p.slideOpens
      : ((p.slide_openings && typeof p.slide_openings === 'object') ? p.slide_openings : {});
    const openVals = Object.values(opens).map((v) => String(v || '').trim().toLowerCase());
    const slideVals = Object.values(slideOpens).map((v) => String(v || '').trim().toLowerCase());
    if (openVals.some((v) => v === 'tilt-turn-left' || v === 'tilt-turn-right')) return 'tilt-and-turn';
    if (slideVals.some((v) => v === 'slide-up' || v === 'slide-down')) return 'sliding-sash';
    const paneKinds = (pres.paneHandleKinds && typeof pres.paneHandleKinds === 'object') ? pres.paneHandleKinds : {};
    const kinds = Object.values(paneKinds).map((v) => String(v || '').trim().toLowerCase());
    const hasDoor = kinds.some((v) => v === 'door');
    const hasWindow = kinds.some((v) => v === 'window');
    if (hasDoor && hasWindow) return 'door-window';
    if (hasDoor) return 'upvc-door';
    return 'casement';
  })();

  const wPx = Number(p.w);
  const hPx = Number(p.h);
  const extLock = pres.extLock != null ? !!pres.extLock : true;
  const cillLock = pres.cillLock != null ? !!pres.cillLock : true;

  const extTop = Number(pres.extTop || 0) || 0;
  const extBottom = Number(pres.extBot || 0) || 0;
  const extLeft = Number(pres.extLeft || 0) || 0;
  const extRight = Number(pres.extRight || 0) || 0;
  const cillOn = !!pres.cillOn;
  const cillHeight = Number(pres.cillHeight || 30) || 30;
  const cillDepth = Number(pres.cillDepth || 0) || 0;
  const cillStub = !!pres.cillStub;

  const windowHingeTypeCode: WindowHingeTypeCode | null = (() => {
    const raw = String(
      pres.windowHingeTypeCode ||
        pres.windowHingeType ||
        pres.window_hinge_type_code ||
        pres.hingeType ||
        ''
    )
      .trim()
      .toLowerCase();
    if (!raw) return null;
    if (raw.includes('egress')) return 'egress';
    if (raw.includes('ez') && raw.includes('clean')) return 'ez_clean';
    if (raw.includes('fire')) return 'fire_rated';
    if (raw.includes('standard')) return 'standard';
    // accept code values directly
    if (raw === 'egress' || raw === 'ez_clean' || raw === 'fire_rated' || raw === 'standard') return raw as any;
    return null;
  })();
  const windowHingeTypeLabel: string | null =
    windowHingeTypeCode
      ? (WINDOW_HINGE_TYPE_OPTIONS.find(x => x.code === windowHingeTypeCode)?.label || 'Standard (default)')
      : null;

  const astragalBarSideCode: BarSideCode | null = (() => {
    const raw = String(
      pres.astragalBarSide || pres.aBarSide || pres.astragal_bar_side || pres.astragalSide || ''
    )
      .trim()
      .toLowerCase();
    if (!raw) return 'none';
    if (raw === 'none') return 'none';
    if (raw === 'inside' || raw === 'internal') return 'inside';
    if (raw === 'outside' || raw === 'external') return 'outside';
    if (raw === 'both') return 'both';
    return 'none';
  })();
  const astragalBarSideLabel: string | null =
    astragalBarSideCode
      ? (BAR_SIDE_OPTIONS.find(x => x.code === astragalBarSideCode)?.label || 'None')
      : null;

  const legacyWidthMm = Number.isFinite(wPx) ? pxToMm(wPx) + (extLock ? 0 : (extLeft + extRight)) : null;
  const heightMm = Number.isFinite(hPx)
    ? pxToMm(hPx) + (extLock ? 0 : (extTop + extBottom)) + (cillOn && !cillLock ? cillHeight : 0)
    : null;
  const bayDerived = (() => {
    if (!isBay) {
      return {
        tangent: null as number | null,
        projectionDepth: null as number | null,
        facetWidths: null as number[] | null,
        jointAngles: null as number[] | null,
      };
    }
    try {
      const fallback = createDefaultBayConfig({
        tangentWidthMm: Math.max(200, Math.round(Number(legacyWidthMm || 1200))),
        heightMm: Math.max(200, Math.round(Number(heightMm || 1200))),
      });
      const cfg = normalizeBayConfig(pres?.bayConfig || fallback, {
        tangentWidthMm: Math.max(200, Math.round(Number(legacyWidthMm || 1200))),
        heightMm: Math.max(200, Math.round(Number(heightMm || 1200))),
      });
      const geo = computeBayGeometry(cfg);
      const isCurvedBow = String((cfg as any)?.bayType || '').trim().toUpperCase() === 'CURVED_BOW' || geo?.derived?.hasCurvedFace === true;
      const tangent = Math.round(Number(geo?.derived?.tangentWidth || cfg?.tangentWidth || legacyWidthMm || 0)) || null;
      const facetWidths = Array.isArray(cfg?.facets)
        ? cfg.facets.map((f: any) => Math.round(Number(f?.width || 0))).filter((n: number) => Number.isFinite(n) && n > 0)
        : [];
      const jointAngles = Array.isArray(geo?.joints)
        ? geo.joints
            .map((j: any) => Number(j?.manualInternalAngle ?? j?.derivedInternalAngle))
            .filter((n: number) => Number.isFinite(n))
            .map((n: number) => Math.round(n))
        : [];
      return {
        tangent,
        projectionDepth: isCurvedBow ? (Math.round(Number(geo?.derived?.projection || cfg?.projectionDepth || 0)) || null) : null,
        facetWidths: facetWidths.length ? facetWidths : null,
        jointAngles: jointAngles.length ? jointAngles : null,
      };
    } catch {
      return {
        tangent: null as number | null,
        projectionDepth: null as number | null,
        facetWidths: null as number[] | null,
        jointAngles: null as number[] | null,
      };
    }
  })();
  const widthMm = isBay ? (bayDerived.tangent ?? legacyWidthMm) : legacyWidthMm;

  const glassType = String(pres.glassType || '').toLowerCase();
  const glassTile = pres.glassStyleTile ? String(pres.glassStyleTile).trim() : '';
  const glass_type_code: any =
    glassTile ? 'obscured'
    : glassType === 'satin' ? 'obscured'
    : glassType ? 'clear'
    : null;
  const glass_type: any =
    glassTile ? (inferGlassTileNameFromPath(glassTile) || 'Patterned')
    : glassType === 'satin' ? 'Satin'
    : glassType ? 'Clear'
    : null;

  const frameNameExt = String(pres.frameNameExt || '').trim();
  const frameNameInt = String(pres.frameNameInt || '').trim();
  const frameExtCode = tryMatchFrameSideColourCodeFromName(frameNameExt) || '';
  const frameIntCode = tryMatchFrameSideColourCodeFromName(frameNameInt) || '';
  const frameSelection = getFrameSelectionFromSideCodes(
    frameExtCode || DEFAULT_COMBINED_FRAME_CODE,
    frameIntCode || frameExtCode || DEFAULT_COMBINED_FRAME_CODE,
  );
  const normalizeRalValue = (raw: unknown): string => {
    const text = String(raw || '').trim();
    if (!text) return '';
    const match = text.match(/^ral\s*[-\s]*([a-z0-9]+)$/i);
    if (match) return `RAL${String(match[1] || '').toUpperCase()}`;
    return text;
  };
  const explicitExtName = normalizeRalValue(frameNameExt);
  const explicitIntName = normalizeRalValue(frameNameInt);
  // Preserve explicit WG frame names (including custom RAL values) when a side code
  // is not resolvable. This prevents fallback-to-white on WG configure round-trips.
  const frame_color_external = frameExtCode
    ? (frameSelection.extValue || explicitExtName || null)
    : (explicitExtName || frameSelection.extValue || null);
  const frame_color_internal = frameIntCode
    ? (frameSelection.intValue || explicitIntName || null)
    : (explicitIntName || frameSelection.intValue || null);
  const frame_color =
    frame_color_external && frame_color_internal
      ? (frame_color_external === frame_color_internal
        ? `${frame_color_external} (both sides)`
        : `${frame_color_external} (external) / ${frame_color_internal} (internal)`)
      : (frame_color_external || frame_color_internal || null);
  const design_dims_on = pres.designDimsOn != null ? !!pres.designDimsOn : null;

  const handleName = String(pres.handleName || '').trim();
  const hc = handleName.toLowerCase();
  const handle_color_code =
    hc.includes('chrome') ? (hc.includes('satin') ? 'satin-chrome' : 'chrome')
    : hc.includes('white') ? 'white'
    : hc.includes('black') ? 'black'
    : hc.includes('gold') ? 'gold'
    : hc === 'grey' ? 'grey'
    : hc.includes('anthracite') ? 'anthracite-grey'
    : null;
  const handle_color_label =
    handle_color_code === 'satin-chrome' ? 'Satin Chrome'
    : handle_color_code === 'chrome' ? 'Chrome'
    : handle_color_code === 'white' ? 'White'
    : handle_color_code === 'black' ? 'Black'
    : handle_color_code === 'gold' ? 'Gold'
    : handle_color_code === 'grey' ? 'Grey'
    : handle_color_code === 'anthracite-grey' ? 'Anthracite Grey'
    : (handleName || null);

  const hs = String(pres.handleStyle || '').toLowerCase();
  const handle_style_label = hs ? getHandleStyleLabel(hs) : null;

  const ventPos = String(pres.ventPos || '').toLowerCase();
  const paneVents = (pres.paneVents && typeof pres.paneVents === 'object') ? pres.paneVents : {};
  const hasAnyOverrides = Object.keys(paneVents).some(k => k !== '_centre' && paneVents[k] && (paneVents[k].sash === true || paneVents[k].jamb === true));
  const hasCentre = paneVents?._centre === true;
  const hasAnyJamb = hasCentre || Object.keys(paneVents).some(k => k !== '_centre' && paneVents[k] && paneVents[k].jamb === true);
  const hasAnySash = Object.keys(paneVents).some(k => k !== '_centre' && paneVents[k] && paneVents[k].sash === true);
  // Treat any explicit pane-level override (including centre-jamb) as custom placement.
  // Some payloads keep a legacy ventPos while storing the actual custom selection in paneVents.
  const isCustomVentPos = ventPos === 'custom' || hasAnyOverrides || hasCentre;
  const trickleEnabled =
    ventPos === 'sash' ? true
    : isJambLikeVentPos(ventPos) ? true
    : isCustomVentPos ? true
    : null;
  const tricklePos =
    trickleEnabled
      ? isCustomVentPos
        ? 'custom'
        : ventPos === 'extender'
          ? (isExtenderTrickleVentActive('extender', extTop) ? 'extender' : 'head')
        : (hasAnyJamb || ventPos === 'jamb') ? 'head' : (hasAnySash || ventPos === 'sash') ? 'sash' : 'sash'
      : null;

  const cill_option: any =
    !cillOn ? 'none'
    : cillStub ? 'stub'
    : cillDepth >= 180 ? '180'
    : cillDepth >= 150 ? '150'
    : 'none';
  const derivedCillIncluded = cill_option !== 'none';
  const derivedCillType = cill_option === 'stub' ? 'Stub' : (derivedCillIncluded ? 'Regular' : null);
  const derivedCillHeight = cill_option === '150' ? 150 : (cill_option === '180' ? 180 : null);
  const joint_style: 'mitre-45' | 'timber-weld' | null = (() => {
    const raw = String(pres.jointStyle || '').trim().toLowerCase();
    if (raw === 'timber-weld') return 'timber-weld';
    if (raw === 'mitre-45') return 'mitre-45';
    return null;
  })();

  return {
    window_type_id: windowTypeId,
    width_mm: widthMm,
    height_mm: heightMm,
    glass_type,
    glass_type_code,
    frame_color,
    frame_color_external,
    frame_color_internal,
    design_dims_on,
    handle_color: { label: handle_color_label, code: handle_color_code },
    handle_style_label,
    trickle: { enabled: trickleEnabled, position: tricklePos as any },
    cill_option,
    cill_derived: { included: derivedCillIncluded, type: derivedCillType as any, height_mm: derivedCillHeight },
    extras: {
      ext_top_mm: extTop || null,
      ext_bottom_mm: extBottom || null,
      ext_left_mm: extLeft || null,
      ext_right_mm: extRight || null,
      ext_lock: extLock,
      georgian_bars: pres.gBarOn != null ? !!pres.gBarOn : null,
      georgian_cols: pres.gBarCols != null ? Number(pres.gBarCols) : null,
      georgian_rows: pres.gBarRows != null ? Number(pres.gBarRows) : null,
      georgian_width_mm: pres.gBarWidth != null ? Number(pres.gBarWidth) : null,
      georgian_offset_x_pct: pres.gBarOffX != null ? Number(pres.gBarOffX) : null,
      georgian_offset_y_pct: pres.gBarOffY != null ? Number(pres.gBarOffY) : null,
      astragal_bars: pres.aBarOn != null ? !!pres.aBarOn : null,
      astragal_cols: pres.aBarCols != null ? Number(pres.aBarCols) : null,
      astragal_rows: pres.aBarRows != null ? Number(pres.aBarRows) : null,
      astragal_width_mm: pres.aBarWidth != null ? Number(pres.aBarWidth) : null,
      astragal_offset_x_pct: pres.aBarOffX != null ? Number(pres.aBarOffX) : null,
      astragal_offset_y_pct: pres.aBarOffY != null ? Number(pres.aBarOffY) : null,
      glass_style_tile: pres.glassStyleTile ? String(pres.glassStyleTile) : null,
      window_hinge_type_code: windowHingeTypeCode,
      window_hinge_type: windowHingeTypeLabel,
      astragal_bar_side: astragalBarSideCode,
      astragal_bar_side_label: astragalBarSideLabel,
      joint_style,
      bay_tangent_width_mm: bayDerived.tangent,
      bay_legacy_width_mm: legacyWidthMm,
      bay_projection_depth_mm: bayDerived.projectionDepth,
      bay_facet_widths_mm: bayDerived.facetWidths,
      bay_joint_angles_deg: bayDerived.jointAngles,
      frame_color_hex_external: pres.frameColExt ? String(pres.frameColExt).trim() : null,
      frame_color_hex_internal: pres.frameColInt ? String(pres.frameColInt).trim() : null,
    },
  };
}

/** Static style from config or library style from Window Generator */
type UploadStyleOption = {
  id: string;
  name: string;
  category: 'bespoke';
  source: 'upload';
  presentationExternalUrl: string;
  presentationInternalUrl: string;
  designExternalUrl: string;
  designInternalUrl: string;
  originalFilename?: string | null;
  originalFilenameInternal?: string | null;
  originalFilenameDesignExternal?: string | null;
  originalFilenameDesignInternal?: string | null;
};

type ManualUploadSlot = 'presentation-external' | 'presentation-internal' | 'design-external' | 'design-internal';

type VisualDesignerStyleOption =
  | WindowStyleConfig
  | {
      id: string;
      name: string;
      category: 'bespoke';
      source: 'window_generator';
      payload: WindowGeneratorPayload;
      preview_design_external_url?: string | null;
      preview_design_internal_url?: string | null;
      preview_presentation_external_url?: string | null;
      preview_presentation_internal_url?: string | null;
      preview_path_bay_schematic?: string | null;
      preview_path_plan_external?: string | null;
      preview_external_url?: string | null;
      preview_internal_url?: string | null;
    }
  | UploadStyleOption;

type WindowGeneratorStyleOption = Extract<VisualDesignerStyleOption, { source: 'window_generator' }>;

function buildVisualDesignerLine(style: VisualDesignerStyleOption, meta: VisualDesignerMetaV1, qty: number): DocumentLineInput {
  return {
    lineKind: 'other',
    name: `Window Design: ${style.name}`,
    description: buildVisualDesignerLineDescription(meta as DesignerSummaryMeta),
    qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
    unit: 'item',
    unitPrice: 0,
    lineTotal: 0,
    vatInclusive: false,
    metaJson: meta as any,
  };
}

export const QuoteVisualDesignerCard: React.FC<{
  lines: DocumentLineInput[];
  setLines: React.Dispatch<React.SetStateAction<DocumentLineInput[]>>;
  /**
   * Default reference value for new items (prefilled, user can overwrite).
   * Typically derived from customer surname in block capitals.
   */
  defaultReference?: string;
  documentId?: string | null;
  customerId?: string | null;
  currentVersionId?: string | null;
  availableVersions?: Array<{ id: string; versionNumber: number; isActive: boolean }>;
  hideShell?: boolean;
  readOnly?: boolean;
  /** Final Survey: never auto-fill width/height/cill/extenders from drawings. */
  requireSurveyorMeasures?: boolean;
}> = ({ lines, setLines, defaultReference, documentId, customerId, currentVersionId, availableVersions = [], hideShell = false, readOnly = false, requireSurveyorMeasures = false }) => {
  const { windowStyles } = useWindowStylesWatcher();

  const linesRef = useRef(lines);
  const linesRevisionRef = useRef(0);
  const expectedCommitLinesRef = useRef<DocumentLineInput[] | null>(null);
  const bulkOperationTokenRef = useRef(0);
  const isBulkApplyingRef = useRef(false);
  const bulkBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    linesRef.current = lines;
    if (expectedCommitLinesRef.current !== null && lines === expectedCommitLinesRef.current) {
      expectedCommitLinesRef.current = null;
      return;
    }
    linesRevisionRef.current += 1;
  }, [lines]);

  const commitLines = useCallback((updater: React.SetStateAction<DocumentLineInput[]>) => {
    if (readOnly) return;
    setLines((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      linesRef.current = next;
      linesRevisionRef.current += 1;
      expectedCommitLinesRef.current = next;
      return next;
    });
  }, [readOnly, setLines]);

  const visualDesignerLines = useMemo(
    () => lines.map((l, idx) => ({ l, idx })).filter(x => isVisualDesignerLine(x.l)),
    [lines]
  );

  /** Swap with adjacent line in document order; refresh sort_order so public PDF / supplier export match. */
  const moveDocumentLine = (index: number, direction: -1 | 1) => {
    commitLines((prev) => {
      const j = index + direction;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[j];
      next[j] = tmp;
      return next.map((line, i) => ({ ...line, sortOrder: i }));
    });
  };

  const [enabled, setEnabled] = useState<boolean>(hideShell || visualDesignerLines.length > 0);
  const [designerVersion, setDesignerVersion] = useState<'v1' | 'v2_lite' | 'v2'>('v2_lite');

  // If existing data is present (edit/version switch), ensure the UI shows enabled.
  useEffect(() => {
    if (visualDesignerLines.length > 0) setEnabled(true);
  }, [visualDesignerLines.length]);

  useEffect(() => {
    const node = bulkBarRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (!node) return;
    node.inert = readOnly;
    if (readOnly) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && node.contains(active)) active.blur();
    }
  }, [readOnly, visualDesignerLines.length]);

  // Modal state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [copyMode, setCopyMode] = useState<'same_version' | 'related_customer' | 'any_existing'>('same_version');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceIdx, setCopySourceIdx] = useState<number | null>(null);
  const [copyTargetDocumentId, setCopyTargetDocumentId] = useState<string>('');
  const [copyTargetVersionId, setCopyTargetVersionId] = useState<string>('');
  const [copyTargets, setCopyTargets] = useState<Array<{ id: string; label: string }>>([]);
  const [copyTargetVersions, setCopyTargetVersions] = useState<Array<{ id: string; label: string; isActive: boolean }>>([]);
  const [copySameQuoteModeEnabled, setCopySameQuoteModeEnabled] = useState<boolean>(true);
  const [copyLoadingTargets, setCopyLoadingTargets] = useState(false);
  const [copyLoadingVersions, setCopyLoadingVersions] = useState(false);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyAllFenestrationItems, setCopyAllFenestrationItems] = useState(false);

  // Step 1 - window type selection
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  // Step 2 - material selection
  const [selectedMaterialId, setSelectedMaterialId] = useState<'upvc' | 'aluminium' | 'timber' | 'composite' | null>(null);

  // Step 3 - style selection (number = static style id, string = library template id)
  const [styleSearch, setStyleSearch] = useState('');
  const [selectedStyleId, setSelectedStyleId] = useState<number | string | null>(null);
  const uploadPresentationExternalInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadPresentationInternalInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadDesignExternalInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadDesignInternalInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadedStyle, setUploadedStyle] = useState<UploadStyleOption | null>(null);
  const [inlineWgStyle, setInlineWgStyle] = useState<WindowGeneratorStyleOption | null>(null);
  const [isUploadingReference, setIsUploadingReference] = useState(false);
  const [, setUploadingSide] = useState<ManualUploadSlot | null>(null);
  const [referenceUploadError, setReferenceUploadError] = useState<string | null>(null);
  const [libraryTemplates, setLibraryTemplates] = useState<WindowGeneratorTemplate[]>([]);
  const [wgModalOpen, setWgModalOpen] = useState(false);
  const [wgModalSessionKey, setWgModalSessionKey] = useState(0);
  const [wgLiteModalOpen, setWgLiteModalOpen] = useState(false);
  const [workingPayload, setWorkingPayload] = useState<WindowGeneratorPayload | null>(null);
  const workingPayloadRef = useRef<WindowGeneratorPayload | null>(null);
  useEffect(() => {
    workingPayloadRef.current = workingPayload;
  }, [workingPayload]);

  const [bulkApplyStatus, setBulkApplyStatus] = useState<'idle' | 'running' | 'done' | 'failed'>('idle');
  const [bulkApplyProgress, setBulkApplyProgress] = useState<{ total: number; completed: number; phase: 'processing' | 'uploading' }>({
    total: 0,
    completed: 0,
    phase: 'processing',
  });
  const [bulkApplyErrors, setBulkApplyErrors] = useState<BulkApplyError[]>([]);
  // Forces WG SVG preview remount when switching back from WG Lite ("Use Design"),
  // avoiding occasional stale SVG defs/ids in React re-use scenarios.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [favoriteStyleIds, setFavoriteStyleIds] = useState<Set<number>>(new Set());
  const [favoritesLoaded, setFavoritesLoaded] = useState<boolean>(false);
  const [savingFavoriteIds, setSavingFavoriteIds] = useState<Set<number>>(new Set());
  const [glassStyleTiles, setGlassStyleTiles] = useState<Array<{ name: string; path: string }>>([]);
  const [zoomPreview, setZoomPreview] = useState<null | {
    kind: 'wg';
    payload: WindowGeneratorPayload;
    view: 'external' | 'internal';
    mode: 'design' | 'presentation';
    title: string;
  } | {
    kind: 'img';
    url: string;
    title: string;
  }>(null);

  // Load Window Generator library templates when Visual Designer modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    windowGeneratorTemplatesService.list().then(async (list) => {
      if (cancelled) return;
      setLibraryTemplates(list);
      // Backfill preview URLs for templates that have payload but no 4-up images
      for (const t of list) {
        const next = t as any;
        const hasAll4 = next.preview_presentation_external_url && next.preview_presentation_internal_url &&
          next.preview_design_external_url && next.preview_design_internal_url;
        if (hasAll4 || !next.payload?.root) continue;
        try {
          const urls = await uploadVisualDesignerPreviewsAndGetUrls(next.payload);
          const updated = await windowGeneratorTemplatesService.updatePreviews(next.id, urls);
          if (!cancelled) setLibraryTemplates(prev => prev.map(x => x.id === updated.id ? (updated as WindowGeneratorTemplate) : x));
        } catch {
          // Non-fatal; thumbnail will render from payload
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  // Load available glass style tiles from manifest (same as Window Generator)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/images/glass-styles/manifest.json", { cache: "no-store" });
        if (!r.ok) return;
        const data: any = await r.json();
        const tiles = Array.isArray(data) ? data : (Array.isArray(data?.tiles) ? data.tiles : []);
        const normalized = (tiles || [])
          .map((t: any) => ({ name: String(t?.name || '').trim(), path: String(t?.path || '').trim() }))
          .filter((t: any) => !!t.name && !!t.path);
        if (!cancelled) setGlassStyleTiles(normalized);
      } catch {
        // Non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Step 4 - condensed specs
  const [reference, setReference] = useState<string>('');
  const [widthMm, setWidthMm] = useState<string>('');
  const [heightMm, setHeightMm] = useState<string>('');
  const [glassTypeCode, setGlassTypeCode] = useState<'clear' | 'obscured'>('clear');
  const [beadingTypeCode, setBeadingTypeCode] = useState<OptionalProfileVariantCode>('sculptured');
  const [variantCode, setVariantCode] = useState<OptionalProfileVariantCode>('chamfered');
  const [styleFamilyCategory, setStyleFamilyCategory] = useState<WindowStyleConfig['category']>(DEFAULT_STYLE_FAMILY_CATEGORY);
  const [frameColorCode, setFrameColorCode] = useState<string>('white-smooth');
  const [frameColorExtCode, setFrameColorExtCode] = useState<string>('white-smooth');
  const [frameColorIntCode, setFrameColorIntCode] = useState<string>('white-smooth');
  const [frameColorExtCustomRal, setFrameColorExtCustomRal] = useState<string>('');
  const [frameColorIntCustomRal, setFrameColorIntCustomRal] = useState<string>('');
  const [frameColorExtCustomHex, setFrameColorExtCustomHex] = useState<string>('');
  const [frameColorIntCustomHex, setFrameColorIntCustomHex] = useState<string>('');
  const [designDimsOn, setDesignDimsOn] = useState<boolean>(false);
  const [handleColorCode, setHandleColorCode] = useState<string>('white');
  const [handleStyleCode, setHandleStyleCode] = useState<HandleStyleCode>('offset');
  const [doorHingePlacement, setDoorHingePlacement] = useState<DoorHingePlacementCode>('left');
  const [doorOpenDirection, setDoorOpenDirection] = useState<DoorOpenDirectionCode>('out');
  const [doorMasterLeaf, setDoorMasterLeaf] = useState<DoorSideCode>(DEFAULT_MASTER_LEAF);
  const [doorPanelCount, setDoorPanelCount] = useState<number>(BIFOLD_PANEL_COUNT_RANGE.default);
  const [doorStackDirection, setDoorStackDirection] = useState<StackDirectionCode>(DEFAULT_STACK_DIRECTION);
  const [doorTrafficDoor, setDoorTrafficDoor] = useState<TrafficDoorCode>(DEFAULT_TRAFFIC_DOOR);
  const [doorThreshold, setDoorThreshold] = useState<ThresholdCode>(DEFAULT_THRESHOLD);
  const [glassSpecification, setGlassSpecification] = useState<GlassSpecificationCode>(DEFAULT_GLASS_SPECIFICATION);
  const [pendingDoorRedraw, setPendingDoorRedraw] = useState<{ panelCount: number; trafficDoor: TrafficDoorCode } | null>(null);
  const [windowHingeTypeCode, setWindowHingeTypeCode] = useState<WindowHingeTypeCode>('standard');
  const [jointStyle, setJointStyle] = useState<'mitre-45' | 'timber-weld'>('mitre-45');
  const [notes, setNotes] = useState<string>('');
  const [qty, setQty] = useState<string>('1');
  const [trickleVents, setTrickleVents] = useState<'yes' | 'no'>('no');
  const [trickleVentsPosition, setTrickleVentsPosition] = useState<TrickleVentsSemanticPosition>('sash');
  const [cillOption, setCillOption] = useState<'none' | '150' | '180' | 'stub'>('none');
  // v2 (Lite) extras – editable overrides (mirrors WG presentation fields)
  const [extTopMm, setExtTopMm] = useState<string>('');
  const [extBottomMm, setExtBottomMm] = useState<string>('');
  const [extLeftMm, setExtLeftMm] = useState<string>('');
  const [extRightMm, setExtRightMm] = useState<string>('');
  useEffect(() => {
    if (!hasActiveTopFrameExtender(extTopMm) && trickleVentsPosition === 'extender') {
      setTrickleVentsPosition('head');
    }
  }, [extTopMm, trickleVentsPosition]);
  const applyTrickleVentsPositionSelection = useCallback((nextRaw: string) => {
    const next = String(nextRaw || '').toLowerCase();
    if (next === 'none') {
      setTrickleVents('no');
      return;
    }
    setTrickleVents('yes');
    const pos = parseSemanticTricklePosition(next);
    setTrickleVentsPosition(pos);
    if (pos === 'extender') {
      setExtTopMm((prev) => ensureExtenderTopMm(prev));
    }
    // Leaving Extender does not clear frame extender mm — it remains independently editable.
  }, []);
  const [georgianOn, setGeorgianOn] = useState<'yes' | 'no'>('no');
  const [georgianCols, setGeorgianCols] = useState<string>('3');
  const [georgianRows, setGeorgianRows] = useState<string>('4');
  const [georgianWidthMm, setGeorgianWidthMm] = useState<string>('18');
  const [georgianOffXPct, setGeorgianOffXPct] = useState<string>('0');
  const [georgianOffYPct, setGeorgianOffYPct] = useState<string>('0');
  const [astragalOn, setAstragalOn] = useState<'yes' | 'no'>('no');
  const [astragalCols, setAstragalCols] = useState<string>('3');
  const [astragalRows, setAstragalRows] = useState<string>('4');
  const [astragalWidthMm, setAstragalWidthMm] = useState<string>('18');
  const [astragalOffXPct, setAstragalOffXPct] = useState<string>('0');
  const [astragalOffYPct, setAstragalOffYPct] = useState<string>('0');
  const [astragalBarSide, setAstragalBarSide] = useState<BarSideCode>('none');
  const [glassStyleTilePath, setGlassStyleTilePath] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const BULK_NONE = '__none__' as const;

  // Bulk edit controls (apply to existing items)
  const [bulkWindowTypeId, setBulkWindowTypeId] = useState<'' | (typeof QUOTE_WINDOW_TYPES)[number]['id']>('');
  const [bulkMaterialId, setBulkMaterialId] = useState<'' | 'upvc' | 'aluminium' | 'timber'>('');
  const [bulkStyleFamilyCategory, setBulkStyleFamilyCategory] = useState<'' | WindowStyleConfig['category']>('');
  const [bulkGlassTypeCode, setBulkGlassTypeCode] = useState<'' | GlassSelectValue>('');
  const [bulkProfileTypeCode, setBulkProfileTypeCode] = useState<ProfileVariantCode | '' | typeof BULK_NONE>('');
  const [bulkBeadingTypeCode, setBulkBeadingTypeCode] = useState<ProfileVariantCode | '' | typeof BULK_NONE>('');
  const [bulkFrameColorExtCode, setBulkFrameColorExtCode] = useState<string>('');
  const [bulkFrameColorIntCode, setBulkFrameColorIntCode] = useState<string>('');
  const [bulkFrameColorExtCustomRal, setBulkFrameColorExtCustomRal] = useState<string>('');
  const [bulkFrameColorExtCustomHex, setBulkFrameColorExtCustomHex] = useState<string>('');
  const [bulkFrameColorIntCustomRal, setBulkFrameColorIntCustomRal] = useState<string>('');
  const [bulkFrameColorIntCustomHex, setBulkFrameColorIntCustomHex] = useState<string>('');
  const [bulkHandleColorCode, setBulkHandleColorCode] = useState<string | typeof BULK_NONE>('');
  const [bulkHandleStyleCode, setBulkHandleStyleCode] = useState<HandleStyleCode | '' | typeof BULK_NONE>('');
  const [bulkTrickleVents, setBulkTrickleVents] = useState<'' | 'yes' | 'no'>('');
  const [bulkTrickleVentsPosition, setBulkTrickleVentsPosition] = useState<'' | 'sash' | 'head' | 'extender' | 'custom' | 'none'>('');
  const [bulkCillOption, setBulkCillOption] = useState<'' | 'none' | '150' | '180' | 'stub'>('');
  const [bulkDesignDimsOn, setBulkDesignDimsOn] = useState<'' | 'yes' | 'no'>('');
  const [bulkWindowHingeTypeCode, setBulkWindowHingeTypeCode] = useState<WindowHingeTypeCode | ''>('');
  const [bulkJointStyle, setBulkJointStyle] = useState<'' | 'mitre-45' | 'timber-weld'>('');
  const [bulkAstragalBarSide, setBulkAstragalBarSide] = useState<BarSideCode | '' | typeof BULK_NONE>('');
  const [bulkGeorgianBars, setBulkGeorgianBars] = useState<'' | typeof BULK_NONE>('');
  const [bulkAstragalBars, setBulkAstragalBars] = useState<'' | typeof BULK_NONE>('');
  const [hasHydratedFromMeta, setHasHydratedFromMeta] = useState<boolean>(false);
  const lastV2LiteSeedStyleIdRef = useRef<number | string | null>(null);
  const [showRalFieldValidation, setShowRalFieldValidation] = useState<boolean>(false);
  const [showBulkRalFieldValidation, setShowBulkRalFieldValidation] = useState<boolean>(false);
  const [showReferenceFieldValidation, setShowReferenceFieldValidation] = useState<boolean>(false);
  const showReferenceError = showReferenceFieldValidation && !reference.trim();
  const [showDimensionFieldValidation, setShowDimensionFieldValidation] = useState<boolean>(false);
  const showWidthError = requireSurveyorMeasures && showDimensionFieldValidation && !widthMm.trim();
  const showHeightError = requireSurveyorMeasures && showDimensionFieldValidation && !heightMm.trim();

  const extCustomRalMissing = frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && !normalizeRalText(frameColorExtCustomRal);
  const extCustomHexMissingOrInvalid = frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && !parseHexColour(frameColorExtCustomHex);
  const intCustomRalMissing = frameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && !normalizeRalText(frameColorIntCustomRal);
  const intCustomHexMissingOrInvalid = frameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && !parseHexColour(frameColorIntCustomHex);
  const showExtCustomRalError = showRalFieldValidation && extCustomRalMissing;
  const showExtCustomHexError = showRalFieldValidation && extCustomHexMissingOrInvalid;
  const showIntCustomRalError = showRalFieldValidation && intCustomRalMissing;
  const showIntCustomHexError = showRalFieldValidation && intCustomHexMissingOrInvalid;

  const bulkExtCustomRalMissing = bulkFrameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && !normalizeRalText(bulkFrameColorExtCustomRal);
  const bulkExtCustomHexMissingOrInvalid = bulkFrameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && !parseHexColour(bulkFrameColorExtCustomHex);
  const bulkIntCustomRalMissing = bulkFrameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && !normalizeRalText(bulkFrameColorIntCustomRal);
  const bulkIntCustomHexMissingOrInvalid = bulkFrameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && !parseHexColour(bulkFrameColorIntCustomHex);
  const showBulkExtCustomRalError = showBulkRalFieldValidation && bulkExtCustomRalMissing;
  const showBulkExtCustomHexError = showBulkRalFieldValidation && bulkExtCustomHexMissingOrInvalid;
  const showBulkIntCustomRalError = showBulkRalFieldValidation && bulkIntCustomRalMissing;
  const showBulkIntCustomHexError = showBulkRalFieldValidation && bulkIntCustomHexMissingOrInvalid;

  const applyBulkChanges = () => {
    if (readOnly) return;
    const bulkExtRal = normalizeRalText(bulkFrameColorExtCustomRal);
    const bulkExtHex = parseHexColour(bulkFrameColorExtCustomHex);
    const bulkIntRal = normalizeRalText(bulkFrameColorIntCustomRal);
    const bulkIntHex = parseHexColour(bulkFrameColorIntCustomHex);
    const bulkExtCustomRequested =
      bulkFrameColorExtCode === CUSTOM_FRAME_COLOUR_CODE ||
      (!!String(bulkFrameColorExtCustomRal || '').trim() || !!String(bulkFrameColorExtCustomHex || '').trim());
    const bulkIntCustomRequested =
      bulkFrameColorIntCode === CUSTOM_FRAME_COLOUR_CODE ||
      (!!String(bulkFrameColorIntCustomRal || '').trim() || !!String(bulkFrameColorIntCustomHex || '').trim());
    if (bulkExtCustomRequested && (!bulkExtRal || !bulkExtHex)) {
      setShowBulkRalFieldValidation(true);
      return;
    }
    if (bulkIntCustomRequested && (!bulkIntRal || !bulkIntHex)) {
      setShowBulkRalFieldValidation(true);
      return;
    }
    setShowBulkRalFieldValidation(false);

    const hasAny =
      !!bulkWindowTypeId ||
      !!bulkMaterialId ||
      !!bulkStyleFamilyCategory ||
      !!bulkGlassTypeCode ||
      !!bulkProfileTypeCode ||
      !!bulkBeadingTypeCode ||
      !!bulkFrameColorExtCode ||
      !!bulkFrameColorIntCode ||
      !!bulkFrameColorExtCustomRal ||
      !!bulkFrameColorExtCustomHex ||
      !!bulkFrameColorIntCustomRal ||
      !!bulkFrameColorIntCustomHex ||
      !!bulkHandleColorCode ||
      !!bulkHandleStyleCode ||
      !!bulkTrickleVents ||
      !!bulkTrickleVentsPosition ||
      !!bulkCillOption ||
      !!bulkDesignDimsOn ||
      !!bulkWindowHingeTypeCode ||
      !!bulkJointStyle ||
      !!bulkAstragalBarSide ||
      !!bulkGeorgianBars ||
      !!bulkAstragalBars;
    if (!hasAny) return;
    if (isBulkApplyingRef.current) return;

    const startRevision = linesRevisionRef.current;
    const snapshotToken = ++bulkOperationTokenRef.current;
    const snapshotLines = linesRef.current;
    const entries = snapshotLines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => isVisualDesignerLine(line))
      .map(({ line, index }) => ({
        index,
        lineId: String((line as any)?.id || '').trim() || null,
        reference: String(((line as any).metaJson as VisualDesignerMetaV1 | undefined)?.specs?.reference || '').trim(),
        isV2LiteWg: qualifiesForV2LiteWgBulk((line as any).metaJson as VisualDesignerMetaV1, libraryTemplates),
        metaClone: deepCloneJson((line as any).metaJson || {}) as VisualDesignerMetaV1,
        qty: Number((line as any)?.qty ?? 1) || 1,
      }));

    if (!entries.length) return;

    isBulkApplyingRef.current = true;
    setBulkApplyStatus('running');
    setBulkApplyProgress({ total: entries.length, completed: 0, phase: 'processing' });
    setBulkApplyErrors([]);

    const processLegacyBulkLine = (l: DocumentLineInput): DocumentLineInput => {
      if (!isVisualDesignerLine(l)) return l;
      const meta = ((l as any).metaJson || {}) as VisualDesignerMetaV1;

      const nextMeta: VisualDesignerMetaV1 = {
        ...(meta as any),
        window_type: meta.window_type ? { ...meta.window_type } : null,
        window_material: meta.window_material ? { ...meta.window_material } : null,
        window_style: meta.window_style ? { ...meta.window_style } as any : (meta.window_style as any),
        specs: {
          ...(meta.specs as any),
          options: {
            ...((meta.specs as any)?.options || {}),
          },
        },
      };
      // Back-compat: older WG lines may have specs but no payload snapshot stored.
      // Seed from library template so bulk edits can repaint previews immediately.
      {
        const wsAny: any = nextMeta.window_style as any;
        const styleId = wsAny?.id;
        const fromLibrary = libraryTemplates.find((t: any) => String(t?.id) === String(styleId));
        const isWindowGeneratorStyle =
          String(wsAny?.source || '').trim().toLowerCase() === 'window_generator' || !!fromLibrary;
        if (isWindowGeneratorStyle) {
          wsAny.source = 'window_generator';
          const payloadCandidate =
            (wsAny?.templatePayload as WindowGeneratorPayload | undefined) ||
            (wsAny?.template_payload as WindowGeneratorPayload | undefined) ||
            (wsAny?.payload as WindowGeneratorPayload | undefined) ||
            null;
          const hasPayload =
            !!(payloadCandidate && (payloadCandidate as any).root && payloadCandidate.w != null && payloadCandidate.h != null);
          if (!hasPayload) {
            const fallbackPayload = fromLibrary?.payload as WindowGeneratorPayload | undefined;
            if (fallbackPayload && (fallbackPayload as any).root && fallbackPayload.w != null && fallbackPayload.h != null) {
              wsAny.templatePayload = deepCloneJson(fallbackPayload);
            }
          }
        }
      }

      // Window type chip
      if (bulkWindowTypeId) {
        const t = QUOTE_WINDOW_TYPES.find(x => x.id === bulkWindowTypeId) || null;
        nextMeta.window_type = t ? { id: t.id, name: t.name } : nextMeta.window_type;
      }

      // Material (stored separately from type in quote mode)
      if (bulkMaterialId) {
        const m = MATERIAL_OPTIONS.find(x => x.id === bulkMaterialId) || null;
        nextMeta.window_material = m ? { id: m.id, name: m.name } : nextMeta.window_material;
      }

      // Style family chip (category)
      if (bulkStyleFamilyCategory) {
        (nextMeta.window_style as any).category = bulkStyleFamilyCategory as any;
      }

      // Glass type
      if (bulkGlassTypeCode) {
        const v = String(bulkGlassTypeCode || '');
        if (v.startsWith('tile:')) {
          const p = v.slice('tile:'.length);
          (nextMeta.specs as any).glass_type_code = 'obscured';
          (nextMeta.specs as any).glass_type = inferGlassTileNameFromPath(p) || 'Patterned';
          (nextMeta.specs.options as any).glass_style_tile = p || null;
        } else if (v === 'satin') {
          (nextMeta.specs as any).glass_type_code = 'obscured';
          (nextMeta.specs as any).glass_type = 'Satin';
          (nextMeta.specs.options as any).glass_style_tile = null;
        } else {
          (nextMeta.specs as any).glass_type_code = 'clear';
          (nextMeta.specs as any).glass_type = 'Clear';
          (nextMeta.specs.options as any).glass_style_tile = null;
        }
      }

      // Profile Type
      if (bulkProfileTypeCode) {
        if (bulkProfileTypeCode === BULK_NONE) {
          (nextMeta.specs as any).variant_code = '';
          (nextMeta.specs as any).variant = '';
        } else {
          const v = PROFILE_VARIANT_OPTIONS.find(x => x.code === bulkProfileTypeCode) || null;
          (nextMeta.specs as any).variant_code = v?.code ?? bulkProfileTypeCode;
          (nextMeta.specs as any).variant = v?.label ?? '';
        }
      }

      // Beading type
      if (bulkBeadingTypeCode) {
        if (bulkBeadingTypeCode === BULK_NONE) {
          (nextMeta.specs as any).beading_type_code = '';
          (nextMeta.specs as any).beading_type = '';
        } else {
          const b = BEADING_TYPE_OPTIONS.find(x => x.code === bulkBeadingTypeCode) || null;
          (nextMeta.specs as any).beading_type_code = b?.code ?? bulkBeadingTypeCode;
          (nextMeta.specs as any).beading_type = b?.label ?? '';
        }
      }

      // External/Internal colour
      if (
        bulkFrameColorExtCode ||
        bulkFrameColorIntCode ||
        String(bulkFrameColorExtCustomRal || '').trim() ||
        String(bulkFrameColorExtCustomHex || '').trim() ||
        String(bulkFrameColorIntCustomRal || '').trim() ||
        String(bulkFrameColorIntCustomHex || '').trim()
      ) {
        const currentFrame = getStoredFrameSideCodes(nextMeta.specs);
        const currentExtName = normalizeRalText(String((nextMeta.specs as any)?.frame_color_external || ''));
        const currentIntName = normalizeRalText(String((nextMeta.specs as any)?.frame_color_internal || ''));
        const currentExtHex = parseHexColour((nextMeta.specs.options as any)?.frame_color_hex_external);
        const currentIntHex = parseHexColour((nextMeta.specs.options as any)?.frame_color_hex_internal);

        const hasExtCustomInput = !!(
          String(bulkFrameColorExtCustomRal || '').trim() ||
          String(bulkFrameColorExtCustomHex || '').trim()
        );
        const hasIntCustomInput = !!(
          String(bulkFrameColorIntCustomRal || '').trim() ||
          String(bulkFrameColorIntCustomHex || '').trim()
        );

        const nextExtCode = bulkFrameColorExtCode || (hasExtCustomInput ? CUSTOM_FRAME_COLOUR_CODE : currentFrame.extCode);
        const nextIntCode = bulkFrameColorIntCode || (hasIntCustomInput ? CUSTOM_FRAME_COLOUR_CODE : currentFrame.intCode);
        const isExtCustom = nextExtCode === CUSTOM_FRAME_COLOUR_CODE;
        const isIntCustom = nextIntCode === CUSTOM_FRAME_COLOUR_CODE;

        const frameSelection = getFrameSelectionFromSideCodes(
          isExtCustom ? (isIntCustom ? currentFrame.extCode : nextIntCode) : nextExtCode,
          isIntCustom ? (isExtCustom ? currentFrame.intCode : nextExtCode) : nextIntCode
        );

        const extValue = isExtCustom
          ? (normalizeRalText(bulkFrameColorExtCustomRal) || currentExtName || 'RAL Custom')
          : frameSelection.extValue;
        const intValue = isIntCustom
          ? (normalizeRalText(bulkFrameColorIntCustomRal) || currentIntName || 'RAL Custom')
          : frameSelection.intValue;
        const combinedCode = (isExtCustom || isIntCustom)
          ? null
          : (nextExtCode === nextIntCode ? nextExtCode : (tryFindCombinedFrameColourCode(nextExtCode, nextIntCode) || nextExtCode));

        (nextMeta.specs as any).frame_color_code = combinedCode;
        (nextMeta.specs as any).frame_color = extValue === intValue ? extValue : `${extValue} (external) / ${intValue} (internal)`;
        (nextMeta.specs as any).frame_color_code_external = isExtCustom ? null : nextExtCode;
        (nextMeta.specs as any).frame_color_external = extValue;
        (nextMeta.specs as any).frame_color_code_internal = isIntCustom ? null : nextIntCode;
        (nextMeta.specs as any).frame_color_internal = intValue;
        (nextMeta.specs.options as any).frame_color_hex_external = isExtCustom
          ? (parseHexColour(bulkFrameColorExtCustomHex) || currentExtHex || null)
          : null;
        (nextMeta.specs.options as any).frame_color_hex_internal = isIntCustom
          ? (parseHexColour(bulkFrameColorIntCustomHex) || currentIntHex || null)
          : null;

        // Keep WG payload presentation frame colours in sync so existing items repaint immediately.
        const wsAny: any = nextMeta.window_style as any;
        const tp = wsAny?.templatePayload as WindowGeneratorPayload | undefined;
        if (tp && (tp as any).root && tp.w != null && tp.h != null) {
          const tpNext = deepCloneJson(tp) as any;
          const pres = (tpNext.presentation && typeof tpNext.presentation === 'object')
            ? { ...tpNext.presentation }
            : {};

          const mappedExt = isExtCustom ? null : FRAME_CODE_TO_WG[nextExtCode];
          const mappedInt = isIntCustom ? null : FRAME_CODE_TO_WG[nextIntCode];

          const nextExtHexForPayload = isExtCustom
            ? (parseHexColour(bulkFrameColorExtCustomHex) || currentExtHex || parseHexColour(pres.frameColExt))
            : (mappedExt?.frameColExt || parseHexColour(pres.frameColExt));
          const nextIntHexForPayload = isIntCustom
            ? (parseHexColour(bulkFrameColorIntCustomHex) || currentIntHex || parseHexColour(pres.frameColInt))
            : (mappedInt?.frameColInt || parseHexColour(pres.frameColInt));

          pres.frameNameExt = isExtCustom
            ? extValue
            : (mappedExt?.frameNameExt || extValue || pres.frameNameExt);
          pres.frameNameInt = isIntCustom
            ? intValue
            : (mappedInt?.frameNameInt || intValue || pres.frameNameInt);
          if (nextExtHexForPayload) pres.frameColExt = nextExtHexForPayload;
          if (nextIntHexForPayload) pres.frameColInt = nextIntHexForPayload;

          tpNext.presentation = pres;
          wsAny.templatePayload = tpNext;
        }
      }

      // Handle colour
      if (bulkHandleColorCode) {
        if (bulkHandleColorCode === BULK_NONE) {
          (nextMeta.specs.options as any).handle_color_code = '';
          (nextMeta.specs.options as any).handle_color = '';
        } else {
          const handleOpt = HANDLE_COLOUR_OPTIONS.find(x => x.code === bulkHandleColorCode) || null;
          (nextMeta.specs.options as any).handle_color_code = handleOpt?.code ?? bulkHandleColorCode;
          (nextMeta.specs.options as any).handle_color = handleOpt?.label ?? '';
        }
      }

      // Handle style
      if (bulkHandleStyleCode) {
        if (bulkHandleStyleCode === BULK_NONE) {
          (nextMeta.specs.options as any).handle_style_code = '';
          (nextMeta.specs.options as any).handle_style = '';
        } else {
          const hs = HANDLE_STYLE_OPTIONS.find(x => x.code === bulkHandleStyleCode) || null;
          (nextMeta.specs.options as any).handle_style_code = hs?.code ?? bulkHandleStyleCode;
          (nextMeta.specs.options as any).handle_style = hs?.label ?? '';
        }
      }

      // Trickle vents
      if (bulkTrickleVents) {
        const enabled = bulkTrickleVents === 'yes';
        (nextMeta.specs.options as any).trickle_vents = enabled;
        (nextMeta.specs.options as any).trickle_vents_position = enabled
          ? ((bulkTrickleVentsPosition || (nextMeta.specs.options as any).trickle_vents_position || 'sash') as any)
          : null;
      } else if (bulkTrickleVentsPosition) {
        if (bulkTrickleVentsPosition === 'none') {
          (nextMeta.specs.options as any).trickle_vents = false;
          (nextMeta.specs.options as any).trickle_vents_position = null;
        } else if ((nextMeta.specs.options as any).trickle_vents === true) {
          // Only apply position when vents are enabled
          (nextMeta.specs.options as any).trickle_vents_position = bulkTrickleVentsPosition;
        }
      }
      if (bulkTrickleVents || bulkTrickleVentsPosition) {
        const wsAny: any = nextMeta.window_style as any;
        const tp = wsAny?.templatePayload as WindowGeneratorPayload | undefined;
        if (tp && (tp as any).root && tp.w != null && tp.h != null) {
          const tpNext = deepCloneJson(tp) as any;
          const pres = (tpNext.presentation && typeof tpNext.presentation === 'object')
            ? { ...tpNext.presentation }
            : {};
          const ventsEnabled = (nextMeta.specs.options as any).trickle_vents === true;
          const ventsPos = String((nextMeta.specs.options as any).trickle_vents_position || '').toLowerCase();
          if (!ventsEnabled) {
            pres.ventPos = 'none';
            pres.paneVents = {};
          } else if (ventsPos === 'custom') {
            // Keep per-pane custom vent placements when custom mode is selected.
            pres.ventPos = 'none';
            if (!pres.paneVents || typeof pres.paneVents !== 'object') pres.paneVents = {};
          } else {
            pres.ventPos = semanticTricklePositionToVentPos(ventsPos);
            if (ventsPos === 'extender') {
              const top = Number((nextMeta.specs.options as any).ext_top_mm ?? 0);
              if (!Number.isFinite(top) || top < 20) {
                (nextMeta.specs.options as any).ext_top_mm = 20;
              }
              pres.extTop = Number((nextMeta.specs.options as any).ext_top_mm ?? 20);
            }
            // Non-custom modes should not carry stale per-pane vent overrides.
            pres.paneVents = {};
          }
          tpNext.presentation = pres;
          wsAny.templatePayload = tpNext;
        }
      }

      // Cills
      if (bulkCillOption) {
        const co = bulkCillOption as any;
        const derivedCillIncluded = co !== 'none';
        const derivedCillType = co === 'stub' ? 'Stub' : (derivedCillIncluded ? 'Regular' : null);
        const derivedCillHeight = co === '150' ? 150 : (co === '180' ? 180 : null);

        (nextMeta.specs.options as any).cill_option = co;
        // Back-compat fields
        (nextMeta.specs.options as any).cill_included = derivedCillIncluded;
        (nextMeta.specs.options as any).cill_required = false;
        (nextMeta.specs.options as any).cill_type = derivedCillType;
        (nextMeta.specs.options as any).cill_height_mm = derivedCillHeight;
      }

      // Design dims overlay (v2 Lite)
      if (bulkDesignDimsOn) {
        (nextMeta.specs.options as any).design_dims_on = bulkDesignDimsOn === 'yes';
      }

      if (bulkWindowHingeTypeCode) {
        const ht = WINDOW_HINGE_TYPE_OPTIONS.find(x => x.code === bulkWindowHingeTypeCode) || null;
        (nextMeta.specs.options as any).window_hinge_type_code = ht?.code ?? bulkWindowHingeTypeCode;
        (nextMeta.specs.options as any).window_hinge_type = ht?.label ?? null;
      }

      if (bulkJointStyle) {
        // Timber Weld is persisted explicitly; 45° Mitre clears back to default behavior.
        (nextMeta.specs.options as any).joint_style = bulkJointStyle === 'timber-weld' ? 'timber-weld' : '';
      }

      if (bulkAstragalBarSide) {
        if (bulkAstragalBarSide === BULK_NONE) {
          (nextMeta.specs.options as any).astragal_bar_side = '';
          (nextMeta.specs.options as any).astragal_bar_side_label = '';
        } else {
          const s = BAR_SIDE_OPTIONS.find(x => x.code === bulkAstragalBarSide) || null;
          (nextMeta.specs.options as any).astragal_bar_side = s?.code ?? bulkAstragalBarSide;
          (nextMeta.specs.options as any).astragal_bar_side_label = s?.label ?? '';
        }
      }

      // Georgian / Astragal bars (v2/v2_lite WG extras stored in specs.options)
      if (bulkGeorgianBars === BULK_NONE) {
        (nextMeta.specs.options as any).georgian_bars = null;
        (nextMeta.specs.options as any).georgian_cols = null;
        (nextMeta.specs.options as any).georgian_rows = null;
        (nextMeta.specs.options as any).georgian_width_mm = null;
        (nextMeta.specs.options as any).georgian_offset_x_pct = null;
        (nextMeta.specs.options as any).georgian_offset_y_pct = null;
      }
      if (bulkAstragalBars === BULK_NONE) {
        (nextMeta.specs.options as any).astragal_bars = null;
        (nextMeta.specs.options as any).astragal_cols = null;
        (nextMeta.specs.options as any).astragal_rows = null;
        (nextMeta.specs.options as any).astragal_width_mm = null;
        (nextMeta.specs.options as any).astragal_offset_x_pct = null;
        (nextMeta.specs.options as any).astragal_offset_y_pct = null;
        // Side selection becomes irrelevant; clear it too so templates/docs hide it.
        (nextMeta.specs.options as any).astragal_bar_side = '';
        (nextMeta.specs.options as any).astragal_bar_side_label = '';
      }

      // Keep system auto-notes in sync with bulk-updated WG extras.
      const autoParts: string[] = [];
      const o: any = nextMeta.specs.options || {};
      if (o.georgian_bars === true) {
        const grid = (o.georgian_cols && o.georgian_rows) ? `${o.georgian_cols}×${o.georgian_rows}` : '';
        autoParts.push(`Georgian bars ${[grid, o.georgian_width_mm ? `${o.georgian_width_mm}mm` : ''].filter(Boolean).join(' ')}`.trim());
      }
      if (o.astragal_bars === true) {
        const grid = (o.astragal_cols && o.astragal_rows) ? `${o.astragal_cols}×${o.astragal_rows}` : '';
        autoParts.push(`Astragal bars ${[grid, o.astragal_width_mm ? `${o.astragal_width_mm}mm` : ''].filter(Boolean).join(' ')}`.trim());
      }
      if (String(o.joint_style || '').trim().toLowerCase() === 'timber-weld') {
        autoParts.push('Corner joint: Timber Weld');
      }
      const userNotes = stripAutoDerivedNotes(nextMeta.specs.notes || '').trim();
      const autoNotes = autoParts.filter(Boolean).join(' • ').trim();
      nextMeta.specs.notes = [userNotes, autoNotes].filter(Boolean).join(userNotes && autoNotes ? '\n' : '') || null;

      // Rebuild line so name/description reflect updated meta
      const styleId = Number(nextMeta.window_style?.id ?? 0) || 0;
      const styleFromBank =
        windowStyles.find(s => Number(s.id) === styleId) ||
        (windowStylesByType['casement'] || []).find(s => Number(s.id) === styleId) ||
        null;
      const ws: any = nextMeta.window_style as any;
      const style: WindowStyleConfig = styleFromBank || {
        id: styleId,
        name: String(nextMeta.window_style?.name || (l as any).name || 'Window style'),
        category: (nextMeta.window_style?.category as any) || 'traditional',
        previewPath: String(ws?.preview_path || ws?.previewPath || ''),
        emailPath: String(ws?.email_path || ws?.emailPath || ''),
      };

      const qty = Number((l as any)?.qty ?? 1) || 1;
      const rebuilt = buildVisualDesignerLine(style, nextMeta, qty);
      return rebuilt;
    };

    void (async () => {
      try {
        const { results, errors } = await runBulkApplyOrchestration({
          snapshotToken,
          startRevision,
          entries,
          getOperationToken: () => bulkOperationTokenRef.current,
          canCommit: () =>
            bulkOperationTokenRef.current === snapshotToken &&
            linesRevisionRef.current === startRevision,
          onProgress: setBulkApplyProgress,
          processEntry: async (entry) => {
            if (entry.isV2LiteWg) {
              let nextMeta: VisualDesignerMetaV1 = {
                ...(entry.metaClone as any),
                window_type: entry.metaClone.window_type ? { ...entry.metaClone.window_type } : null,
                window_material: entry.metaClone.window_material ? { ...entry.metaClone.window_material } : null,
                window_style: entry.metaClone.window_style ? { ...entry.metaClone.window_style } as any : (entry.metaClone.window_style as any),
                specs: {
                  ...(entry.metaClone.specs as any),
                  options: { ...((entry.metaClone.specs as any)?.options || {}) },
                },
              };
              const legacyLine: DocumentLineInput = {
                lineKind: 'other',
                name: '',
                qty: entry.qty,
                unit: 'item',
                unitPrice: 0,
                lineTotal: 0,
                vatInclusive: false,
                metaJson: nextMeta as any,
              };
              nextMeta = (processLegacyBulkLine(legacyLine).metaJson as VisualDesignerMetaV1) || nextMeta;

              const payloadRaw = resolveCanonicalTemplatePayload(nextMeta, libraryTemplates);
              if (!payloadRaw) throw new Error('No Window Generator payload');

              const specs = nextMeta.specs as any;
              const opts = specs?.options || {};
              const frameCodes = getStoredFrameSideCodes(specs);
              const mergeState = {
                frameColorCode: String(specs?.frame_color_code || frameCodes.combinedCode || 'white-smooth'),
                frameColorExtCode: frameCodes.extCode,
                frameColorIntCode: frameCodes.intCode,
                frameColorExtName: String(specs?.frame_color_external || ''),
                frameColorIntName: String(specs?.frame_color_internal || ''),
                frameColorExtHex: String(opts?.frame_color_hex_external || ''),
                frameColorIntHex: String(opts?.frame_color_hex_internal || ''),
                preferExplicitFrameValues: true as const,
                designDimsOn: opts?.design_dims_on === true,
                glassTypeCode: (String(specs?.glass_type_code || '').toLowerCase() === 'obscured' ? 'obscured' : 'clear') as 'clear' | 'obscured',
                widthMm: specs?.width_mm != null ? String(specs.width_mm) : '',
                heightMm: specs?.height_mm != null ? String(specs.height_mm) : '',
                handleColorCode: String(opts?.handle_color_code || 'white'),
                handleStyleCode: String(opts?.handle_style_code || 'offset'),
                trickleVents: opts?.trickle_vents === true ? 'yes' as const : 'no' as const,
                trickleVentsPosition: parseSemanticTricklePosition(opts?.trickle_vents_position),
                cillOption: (opts?.cill_option || 'none') as 'none' | '150' | '180' | 'stub',
                extTopMm: String(opts?.ext_top_mm ?? ''),
                extBottomMm: String(opts?.ext_bottom_mm ?? ''),
                extLeftMm: String(opts?.ext_left_mm ?? ''),
                extRightMm: String(opts?.ext_right_mm ?? ''),
                georgianOn: opts?.georgian_bars === true ? 'yes' as const : opts?.georgian_bars === false ? 'no' as const : undefined,
                georgianCols: String(opts?.georgian_cols ?? ''),
                georgianRows: String(opts?.georgian_rows ?? ''),
                georgianWidthMm: String(opts?.georgian_width_mm ?? ''),
                georgianOffXPct: String(opts?.georgian_offset_x_pct ?? ''),
                georgianOffYPct: String(opts?.georgian_offset_y_pct ?? ''),
                glassStyleTilePath: String(opts?.glass_style_tile ?? ''),
                astragalOn: opts?.astragal_bars === true ? 'yes' as const : opts?.astragal_bars === false ? 'no' as const : undefined,
                astragalCols: String(opts?.astragal_cols ?? ''),
                astragalRows: String(opts?.astragal_rows ?? ''),
                astragalWidthMm: String(opts?.astragal_width_mm ?? ''),
                astragalOffXPct: String(opts?.astragal_offset_x_pct ?? ''),
                astragalOffYPct: String(opts?.astragal_offset_y_pct ?? ''),
                jointStyle: (opts?.joint_style === 'timber-weld' ? 'timber-weld' : 'mitre-45') as 'mitre-45' | 'timber-weld',
                windowHingeTypeCode: (opts?.window_hinge_type_code || 'standard') as WindowHingeTypeCode,
                astragalBarSide: (opts?.astragal_bar_side || 'none') as BarSideCode,
                isDoor: isDoorTypeId(String(nextMeta?.window_type?.id || '')),
                forceNoOpenings: String(nextMeta?.window_style?.category || '').toLowerCase() === 'fixed',
                logicalDivsBackup: (opts?.logical_divs && typeof opts.logical_divs === 'object') ? opts.logical_divs : null,
              };

              const patchedPayload = mergeModalStateIntoPayload(deepCloneJson(payloadRaw), mergeState);
              if (!patchedPayload) throw new Error('Failed to patch payload');

              const derived = deriveFromWindowGeneratorPayload(patchedPayload);
              (nextMeta.specs as any).width_mm = derived.width_mm;
              (nextMeta.specs as any).height_mm = derived.height_mm;
              (nextMeta.specs as any).glass_type_code = derived.glass_type_code;
              (nextMeta.specs as any).glass_type = derived.glass_type;
              (nextMeta.specs as any).frame_color = derived.frame_color;
              (nextMeta.specs as any).frame_color_external = derived.frame_color_external;
              (nextMeta.specs as any).frame_color_internal = derived.frame_color_internal;
              (nextMeta.specs.options as any).handle_color = derived.handle_color.label;
              (nextMeta.specs.options as any).handle_color_code = derived.handle_color.code;
              (nextMeta.specs.options as any).handle_style = derived.handle_style_label;
              (nextMeta.specs.options as any).trickle_vents = derived.trickle.enabled;
              (nextMeta.specs.options as any).trickle_vents_position = derived.trickle.enabled ? derived.trickle.position : null;
              (nextMeta.specs.options as any).cill_option = derived.cill_option;
              (nextMeta.specs.options as any).logical_divs =
                (patchedPayload as any).logicalDivs && typeof (patchedPayload as any).logicalDivs === 'object'
                  ? deepCloneJson((patchedPayload as any).logicalDivs)
                  : null;
              Object.assign(nextMeta.specs.options as any, derived.extras);

              (nextMeta.window_style as any).source = 'window_generator';
              (nextMeta.window_style as any).templatePayload = deepCloneJson(patchedPayload);

              setBulkApplyProgress((p) => ({ ...p, phase: 'uploading' }));
              const urls = await uploadVisualDesignerPreviewsAndGetUrls(patchedPayload);
              nextMeta.window_style = attachV2LitePreviewUrls(nextMeta.window_style as any, urls) as any;

              const styleId = Number(nextMeta.window_style?.id ?? 0) || 0;
              const styleFromBank =
                windowStyles.find(s => Number(s.id) === styleId) ||
                (windowStylesByType['casement'] || []).find(s => Number(s.id) === styleId) ||
                null;
              const ws: any = nextMeta.window_style as any;
              const style: WindowStyleConfig = styleFromBank || {
                id: styleId,
                name: String(nextMeta.window_style?.name || 'Window style'),
                category: (nextMeta.window_style?.category as any) || 'traditional',
                previewPath: String(ws?.preview_path || ws?.previewPath || ''),
                emailPath: String(ws?.email_path || ws?.emailPath || ''),
              };
              return buildVisualDesignerLine(style, nextMeta, entry.qty);
            }

            const legacyLine: DocumentLineInput = {
              ...(snapshotLines[entry.index] as DocumentLineInput),
              metaJson: entry.metaClone as any,
              qty: entry.qty,
            };
            return processLegacyBulkLine(legacyLine);
          },
        });

        commitLines(mergeBulkResultsIntoLines(linesRef.current, results));
        setBulkApplyErrors(errors);
        setBulkApplyStatus(errors.length > 0 && results.size === 0 ? 'failed' : 'done');
      } catch (err) {
        const message = err instanceof BulkRunConcurrentEditError
          ? err.message
          : (err instanceof Error ? err.message : 'Bulk edit failed.');
        setBulkApplyErrors([{ lineIndex: -1, reference: '', message }]);
        setBulkApplyStatus('failed');
      } finally {
        isBulkApplyingRef.current = false;
        setBulkApplyProgress({ total: 0, completed: 0, phase: 'processing' });
      }
    })();
  };

  const selectedDoorKind: DoorKind | null = useMemo(
    () => (selectedTypeId ? getDoorKind({ id: selectedTypeId, name: QUOTE_WINDOW_TYPES.find((t) => t.id === selectedTypeId)?.name }) : null),
    [selectedTypeId]
  );
  const isMultiLeafDoorSelection = selectedDoorKind === 'french' || selectedDoorKind === 'bifold' || selectedDoorKind === 'slider';
  const effectiveDoorPanelCount =
    selectedDoorKind === 'bifold' || selectedDoorKind === 'slider' ? clampPanelCount(selectedDoorKind, doorPanelCount) : null;

  const buildDoorPresetPayload = useCallback(
    (panelCount: number | null, trafficDoor: TrafficDoorCode): WindowGeneratorPayload | null => {
      if (selectedDoorKind === 'french') return buildFrenchDoorsPayload(doorMasterLeaf) as unknown as WindowGeneratorPayload;
      if (selectedDoorKind === 'bifold') return buildBifoldDoorsPayload(panelCount ?? undefined, trafficDoor) as unknown as WindowGeneratorPayload;
      if (selectedDoorKind === 'slider') return buildSlidingDoorsPayload(panelCount ?? undefined) as unknown as WindowGeneratorPayload;
      return null;
    },
    [selectedDoorKind, doorMasterLeaf]
  );

  const doorPresetStyle = useMemo((): WindowGeneratorStyleOption | null => {
    const payload = buildDoorPresetPayload(effectiveDoorPanelCount, doorTrafficDoor);
    if (!payload || !selectedTypeId) return null;
    return {
      id: `door-preset:${selectedTypeId}`,
      name: DOOR_TYPE_LABELS[selectedTypeId] || 'Door',
      category: 'bespoke',
      source: 'window_generator',
      payload,
      preview_design_external_url: null,
      preview_design_internal_url: null,
      preview_presentation_external_url: null,
      preview_presentation_internal_url: null,
      preview_external_url: null,
      preview_internal_url: null,
    };
  }, [buildDoorPresetPayload, effectiveDoorPanelCount, doorTrafficDoor, selectedTypeId]);

  const applyDoorRedraw = (panelCount: number, trafficDoor: TrafficDoorCode) => {
    const panelCountChanged = panelCount !== effectiveDoorPanelCount;
    setDoorPanelCount(panelCount);
    setDoorTrafficDoor(trafficDoor);
    const next = buildDoorPresetPayload(panelCount, trafficDoor);
    if (!next) return;
    setWorkingPayload(deepCloneJson(next));
    if (panelCountChanged && next.w != null) setWidthMm(String(Math.round(Number(next.w) * WINDOW_GENERATOR_PX2MM)));
  };

  /** Redraws Bi-Folds / Sliders for a new panel count or traffic door, asking first when the drawing was edited. */
  const requestDoorRedraw = (panelCount: number | null, trafficDoor: TrafficDoorCode) => {
    if (selectedDoorKind !== 'bifold' && selectedDoorKind !== 'slider') return;
    const count = clampPanelCount(selectedDoorKind, panelCount);
    const shape = (p: any) => JSON.stringify({ root: p?.root ?? null, opens: p?.opens ?? {}, slideOpens: p?.slideOpens ?? {} });
    const current = workingPayloadRef.current;
    const lastPreset = buildDoorPresetPayload(effectiveDoorPanelCount, doorTrafficDoor);
    const edited = !!(current as any)?.root && (!lastPreset || shape(current) !== shape(lastPreset));
    if (edited) {
      setPendingDoorRedraw({ panelCount: count, trafficDoor });
      return;
    }
    applyDoorRedraw(count, trafficDoor);
  };

  const stylePoolKey = useMemo(() => {
    if (!selectedTypeId) return null;
    // For these types, the style set is fixed (material still captured separately)
    if (selectedTypeId === 'tilt-and-turn') return 'tilt-and-turn';
    if (selectedTypeId === 'sliding-sash') return 'sliding-sash';
    if (selectedTypeId === 'door-window') return 'upvc-door';
    if (selectedTypeId === 'upvc-door') return 'upvc-door';
    if (selectedDoorKind === 'french' || selectedDoorKind === 'bifold' || selectedDoorKind === 'slider') return 'upvc-door';
    // For casement/flush: material determines the style pool
    if (selectedMaterialId === 'aluminium') return 'aluminium';
    if (selectedMaterialId === 'timber') return 'timber';
    // uPVC uses the base type mapping
    return selectedTypeId;
  }, [selectedTypeId, selectedMaterialId, selectedDoorKind]);

  const libraryStylesAsOptions = useMemo((): VisualDesignerStyleOption[] => {
    return libraryTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      category: 'bespoke' as const,
      source: 'window_generator' as const,
      payload: t.payload,
      preview_design_external_url: (t as any).preview_design_external_url ?? null,
      preview_design_internal_url: (t as any).preview_design_internal_url ?? null,
      preview_presentation_external_url: (t as any).preview_presentation_external_url ?? null,
      preview_presentation_internal_url: (t as any).preview_presentation_internal_url ?? null,
      preview_external_url: (t as any).preview_external_url ?? null,
      preview_internal_url: (t as any).preview_internal_url ?? null,
    }));
  }, [libraryTemplates]);

  const stylesForSelectedType = useMemo(() => {
    const staticStyles = stylePoolKey ? (windowStylesByType[stylePoolKey] || []) : [];
    // v2 + v2 Lite: styles come from Window Generator library (and "Create in Window Generator")
    if (designerVersion === 'v2' || designerVersion === 'v2_lite') {
      // Filter library templates by metadata tags (if present).
      // If a template has no tags, it is considered applicable everywhere.
      if (!selectedTypeId || !selectedMaterialId) return [];
      const filtered = libraryStylesAsOptions.filter((s: any) => {
        const payload = (s as any)?.payload || null;
        const pres = payload?.presentation || {};
        const meta = pres?.library_meta || pres?.libraryMeta || {};
        const typeIds = Array.isArray(meta.window_type_ids) ? meta.window_type_ids : (Array.isArray(meta.windowTypeIds) ? meta.windowTypeIds : []);
        const matIds = Array.isArray(meta.window_material_ids) ? meta.window_material_ids : (Array.isArray(meta.windowMaterialIds) ? meta.windowMaterialIds : []);
        const acceptsDoorTags = selectedTypeId === 'door-window' || isMultiLeafDoorSelection;
        const typeOk = !typeIds.length || typeIds.includes(selectedTypeId) || (acceptsDoorTags && typeIds.includes('upvc-door'));
        const matOk = !matIds.length || matIds.includes(selectedMaterialId);
        return typeOk && matOk;
      });
      const withPreset = doorPresetStyle ? [doorPresetStyle, ...filtered] : filtered;
      return inlineWgStyle ? [inlineWgStyle, ...withPreset] : withPreset;
    }
    if (!stylePoolKey) return [];
    return [...libraryStylesAsOptions, ...staticStyles];
  }, [stylePoolKey, libraryStylesAsOptions, designerVersion, selectedTypeId, selectedMaterialId, inlineWgStyle, doorPresetStyle, isMultiLeafDoorSelection]);

  const selectedStyle = useMemo((): VisualDesignerStyleOption | null => {
    if (selectedStyleId == null) return null;
    if (typeof selectedStyleId === 'string') {
      if (uploadedStyle && uploadedStyle.id === selectedStyleId) return uploadedStyle;
      if (inlineWgStyle && inlineWgStyle.id === selectedStyleId) return inlineWgStyle;
      if (doorPresetStyle && doorPresetStyle.id === selectedStyleId) return doorPresetStyle;
      const fromLibrary = libraryStylesAsOptions.find(s => s.id === selectedStyleId);
      if (fromLibrary) return fromLibrary;
      // Edit-mode fallback: if a saved item references a style no longer present in the
      // current library, reconstruct a usable style object from that item's saved metadata.
      const fallbackLine = visualDesignerLines.find(({ l }) => String((l as any)?.metaJson?.window_style?.id || '') === selectedStyleId)?.l as any;
      const ws = fallbackLine?.metaJson?.window_style as any;
      if (ws && String(ws.source || '').toLowerCase() === 'window_generator') {
        const payload = ws.templatePayload as WindowGeneratorPayload | undefined;
        if (payload && (payload as any).root && (payload as any).w != null && (payload as any).h != null) {
          return {
            id: selectedStyleId,
            name: String(ws.name || 'Window style'),
            category: 'bespoke',
            source: 'window_generator',
            payload: deepCloneJson(payload),
            preview_design_external_url: ws.preview_path_design_external ?? null,
            preview_design_internal_url: ws.preview_path_design_internal ?? null,
            preview_presentation_external_url: ws.preview_path_presentation_external ?? null,
            preview_presentation_internal_url: ws.preview_path_presentation_internal ?? null,
            preview_external_url: ws.preview_path_external ?? null,
            preview_internal_url: ws.preview_path_internal ?? null,
          } as VisualDesignerStyleOption;
        }
      }
      if (ws) {
        const isUploadWs = String(ws.source || '').toLowerCase() === 'upload';
        const presExt = String(ws.preview_path_presentation_external || ws.preview_path_external || (isUploadWs ? '' : ws.preview_path) || '').trim();
        const presInt = String(ws.preview_path_presentation_internal || ws.preview_path_internal || '').trim();
        const desExt = String(ws.preview_path_design_external || (isUploadWs ? '' : presExt)).trim();
        const desInt = String(ws.preview_path_design_internal || (isUploadWs ? '' : presInt)).trim();
        return {
          id: selectedStyleId,
          name: String(ws.name || 'Window style'),
          category: 'bespoke',
          source: 'upload',
          presentationExternalUrl: presExt,
          presentationInternalUrl: presInt,
          designExternalUrl: desExt,
          designInternalUrl: desInt,
          originalFilename: ws.original_filename ?? null,
          originalFilenameInternal: ws.original_filename_internal ?? null,
        } as UploadStyleOption;
      }
      return null;
    }
    return windowStyles.find(s => s.id === selectedStyleId) || null;
  }, [windowStyles, selectedStyleId, libraryStylesAsOptions, uploadedStyle, inlineWgStyle, doorPresetStyle, visualDesignerLines]);

  useEffect(() => {
    if (!selectedStyle || designerVersion !== 'v2_lite') return;
    const isLibraryStyle = 'source' in (selectedStyle as any) && (selectedStyle as any).source === 'window_generator';
    if (!isLibraryStyle || !('payload' in (selectedStyle as any))) return;
    const selectedId = selectedStyleId ?? null;
    if (lastV2LiteSeedStyleIdRef.current !== selectedId) {
      lastV2LiteSeedStyleIdRef.current = selectedId;
    } else if (workingPayload && (workingPayload as any).root) {
      // User already has an active payload snapshot/override for this style; do not overwrite while editing fields.
      return;
    }
    // When re-opening/editing an existing quote line, keep its saved per-line payload snapshot
    // (includes custom vent maps like paneVents._centre) instead of re-seeding from library.
    if (hasHydratedFromMeta && workingPayload && (workingPayload as any).root) return;
    const p = (selectedStyle as any).payload as WindowGeneratorPayload | undefined;
    if (!p || !(p as any).root || p.w == null || p.h == null) return;
    setWorkingPayload(deepCloneJson(p));
  }, [designerVersion, selectedStyleId, selectedStyle, hasHydratedFromMeta, workingPayload]);

  const v2PayloadToUse = useMemo((): WindowGeneratorPayload | null => {
    const isLibraryStyle = !!selectedStyle && 'source' in (selectedStyle as any) && (selectedStyle as any).source === 'window_generator';
    if (!isLibraryStyle || !selectedStyle || !('payload' in (selectedStyle as any))) return null;
    // v2 Lite: use configured payload if available, else fall back to template payload
    if (designerVersion === 'v2_lite') return workingPayload || (selectedStyle as any).payload;
    // v2 Full: always use the library template payload
    return (selectedStyle as any).payload;
  }, [selectedStyle, designerVersion, workingPayload]);

  const buildModalMergeStateFromForm = useCallback(() => {
    const existingPres: any = (workingPayloadRef.current as any)?.presentation || {};
    const resolvedExtCode = String(frameColorExtCode || frameColorCode || '').trim();
    const resolvedIntCode = String(frameColorIntCode || frameColorCode || resolvedExtCode || '').trim();
    const extMapped = FRAME_CODE_TO_WG[resolvedExtCode];
    const intMapped = FRAME_CODE_TO_WG[resolvedIntCode];
    const extName = resolvedExtCode === CUSTOM_FRAME_COLOUR_CODE
      ? (normalizeRalText(frameColorExtCustomRal) || String(existingPres?.frameNameExt || 'RAL Custom'))
      : (extMapped?.frameNameExt || String(existingPres?.frameNameExt || 'White Smooth'));
    const intName = resolvedIntCode === CUSTOM_FRAME_COLOUR_CODE
      ? (normalizeRalText(frameColorIntCustomRal) || String(existingPres?.frameNameInt || 'RAL Custom'))
      : (intMapped?.frameNameInt || String(existingPres?.frameNameInt || 'White Smooth'));
    const extHex = resolvedExtCode === CUSTOM_FRAME_COLOUR_CODE
      ? (parseHexColour(frameColorExtCustomHex) || parseHexColour(existingPres?.frameColExt) || '#808080')
      : (extMapped?.frameColExt || parseHexColour(existingPres?.frameColExt) || '#F6F6F6');
    const intHex = resolvedIntCode === CUSTOM_FRAME_COLOUR_CODE
      ? (parseHexColour(frameColorIntCustomHex) || parseHexColour(existingPres?.frameColInt) || '#808080')
      : (intMapped?.frameColInt || parseHexColour(existingPres?.frameColInt) || '#F6F6F6');
    return {
      frameColorCode,
      frameColorExtCode,
      frameColorIntCode,
      frameColorExtName: extName,
      frameColorIntName: intName,
      frameColorExtHex: extHex,
      frameColorIntHex: intHex,
      preferExplicitFrameValues: true as const,
      designDimsOn,
      glassTypeCode,
      widthMm,
      heightMm,
      handleColorCode,
      handleStyleCode,
      trickleVents,
      trickleVentsPosition,
      cillOption,
      extTopMm,
      extBottomMm,
      extLeftMm,
      extRightMm,
      georgianOn,
      georgianCols,
      georgianRows,
      georgianWidthMm,
      georgianOffXPct,
      georgianOffYPct,
      glassStyleTilePath,
      astragalOn,
      astragalCols,
      astragalRows,
      astragalWidthMm,
      astragalOffXPct,
      astragalOffYPct,
      jointStyle,
      windowHingeTypeCode,
      astragalBarSide,
    };
  }, [
    frameColorCode,
    frameColorExtCode,
    frameColorIntCode,
    frameColorExtCustomRal,
    frameColorIntCustomRal,
    frameColorExtCustomHex,
    frameColorIntCustomHex,
    designDimsOn,
    glassTypeCode,
    widthMm,
    heightMm,
    handleColorCode,
    handleStyleCode,
    trickleVents,
    trickleVentsPosition,
    cillOption,
    extTopMm,
    extBottomMm,
    extLeftMm,
    extRightMm,
    georgianOn,
    georgianCols,
    georgianRows,
    georgianWidthMm,
    georgianOffXPct,
    georgianOffYPct,
    glassStyleTilePath,
    astragalOn,
    astragalCols,
    astragalRows,
    astragalWidthMm,
    astragalOffXPct,
    astragalOffYPct,
    jointStyle,
    windowHingeTypeCode,
    astragalBarSide,
  ]);

  // Keep session workingPayload in sync with v2_lite quick-edit form fields.
  useEffect(() => {
    if (designerVersion !== 'v2_lite') return;
    const base = workingPayloadRef.current;
    if (!base || !(base as any).root) return;
    const selectedCategory = String((selectedStyle as any)?.category || '').toLowerCase();
    const merged = mergeModalStateIntoPayload(base, {
      ...buildModalMergeStateFromForm(),
      isDoor: isDoorTypeId(selectedTypeId),
      forceNoOpenings: selectedCategory === 'fixed',
    });
    if (!merged) return;
    setWorkingPayload(merged);
  }, [buildModalMergeStateFromForm, designerVersion, selectedStyle, selectedTypeId]);

  const isBaySpecsMode = useMemo(() => {
    const p: any = workingPayload || v2PayloadToUse || (selectedStyle as any)?.payload || null;
    return isBayLikePayload(p);
  }, [workingPayload, v2PayloadToUse, selectedStyle]);
  const createWindowGeneratorInitialPayload = useMemo<WindowGeneratorPayload>(() => ({
    root: {
      type: 'rectUnion',
      preset: 'rectangle',
      shapeParams: {},
      regions: [
        {
          id: 'r0',
          x: 0,
          y: 0,
          w: 1,
          h: 1,
          root: { id: 'c0', split: 'none' },
        },
      ],
    },
    w: 420,
    h: 380,
    opens: {},
    slideOpens: {},
    logicalDivs: {},
    presentation: {
      cillUnionIncludeUpper: false,
    },
  }), []);

  // Prefill modal fields when a WG library style is first selected — not on every workingPayload merge.
  useEffect(() => {
    if ((designerVersion !== 'v2' && designerVersion !== 'v2_lite') || !v2PayloadToUse) return;
    if (hasHydratedFromMeta) return;
    const derived = deriveFromWindowGeneratorPayload(v2PayloadToUse);
    applyDerivedToUi({
      derived,
      setWidthMm,
      setHeightMm,
      setGlassTypeCode,
      setFrameColorCode,
      setFrameColorExtCode,
      setFrameColorIntCode,
      setFrameColorExtCustomRal,
      setFrameColorIntCustomRal,
      setFrameColorExtCustomHex,
      setFrameColorIntCustomHex,
      setDesignDimsOn,
      setHandleColorCode,
      setHandleStyleCode,
      setTrickleVents,
      setTrickleVentsPosition,
      setCillOption,
      setExtTopMm,
      setExtBottomMm,
      setExtLeftMm,
      setExtRightMm,
      setGeorgianOn,
      setGeorgianCols,
      setGeorgianRows,
      setGeorgianWidthMm,
      setGeorgianOffXPct,
      setGeorgianOffYPct,
      setGlassStyleTilePath,
      setAstragalOn,
      setAstragalCols,
      setAstragalRows,
      setAstragalWidthMm,
      setAstragalOffXPct,
      setAstragalOffYPct,
      setJointStyle,
      setWindowHingeTypeCode,
      setAstragalBarSide,
      setSelectedTypeId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designerVersion, selectedStyleId, hasHydratedFromMeta]);

  const selectedType = useMemo(
    () => QUOTE_WINDOW_TYPES.find(t => t.id === selectedTypeId) || null,
    [selectedTypeId]
  );
  const isDoorSelection = isDoorTypeId(selectedTypeId);
  const availableHandleStyleOptions = isDoorSelection ? DOOR_HANDLE_STYLE_OPTIONS : WINDOW_HANDLE_STYLE_OPTIONS;

  const selectedDoorPreviewUrls = useMemo(() => {
    if (!isDoorTypeId(selectedTypeId)) return null;
    const style = selectedStyle as WindowStyleConfig | undefined;
    if (!style || !('previewPath' in style) || !style.previewPath) return null;
    const paths = buildUpvcDoorImageCandidates({
      stylePreviewPath: style.previewPath,
      hingePlacement: doorHingePlacement,
      handleColorCode,
    });
    const urls = paths.map(p => toPublicImageUrl(p));
    return urls.length ? urls : null;
  }, [selectedTypeId, selectedStyle, doorHingePlacement, handleColorCode]);

  useEffect(() => {
    setHandleStyleCode((prev) => normalizeHandleStyleForType(prev, isDoorSelection));
  }, [isDoorSelection]);

  const filteredStyles = useMemo(() => {
    const normalize = (s: string) =>
      String(s || '')
        .toLowerCase()
        // Treat punctuation/&/hyphens as spaces so "top-hung" matches "top hung"
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const term = normalize(styleSearch);
    const base = stylesForSelectedType;
    if (!term) return base;

    // Search on style name, but with token matching so "fixed top fan" works in any word order.
    const tokens = term.split(' ').filter(Boolean);
    const hayFor = (s: VisualDesignerStyleOption) => normalize(String(s.name || ''));

    const allTokenMatches = base.filter((s) => {
      const hay = hayFor(s);
      return tokens.every(t => hay.includes(t));
    });

    // If the user types multiple tokens and nothing matches all of them,
    // fall back to any-token matching (more forgiving, feels "searchy").
    if (tokens.length > 1 && allTokenMatches.length === 0) {
      return base.filter((s) => {
        const hay = hayFor(s);
        return tokens.some(t => hay.includes(t));
      });
    }

    return allTokenMatches;
  }, [stylesForSelectedType, styleSearch]);

  const sortedStyles = useMemo(() => {
    // Favorites pinned to top "rows"
    const preset: VisualDesignerStyleOption[] = [];
    const fav: VisualDesignerStyleOption[] = [];
    const rest: VisualDesignerStyleOption[] = [];
    for (const s of filteredStyles) {
      if (doorPresetStyle && s.id === doorPresetStyle.id) preset.push(s);
      else if (favoriteStyleIds.has(Number(s.id))) fav.push(s);
      else rest.push(s);
    }
    // Keep stable ordering by name for readability
    const byName = (a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''));
    fav.sort(byName);
    rest.sort(byName);
    return [...preset, ...fav, ...rest];
  }, [filteredStyles, favoriteStyleIds, doorPresetStyle]);

  const resolveEditableDesignerVersion = (
    meta: VisualDesignerMetaV1 | undefined,
    line: DocumentLineInput | undefined
  ): 'v1' | 'v2_lite' | 'v2' => {
    const raw = meta?.designer_version;
    // Unsaved quote-wizard rows should always remain editable in v2_lite mode.
    // Some older rows may carry "v2" in-memory metadata, which otherwise locks controls.
    const hasPersistedLineId = !!String((line as any)?.id || '').trim();
    if (raw === 'v2_lite') return 'v2_lite';
    if (raw === 'v2') return hasPersistedLineId ? 'v2' : 'v2_lite';
    return 'v1';
  };

  const resetModalState = () => {
    setStep(1);
    setDesignerVersion('v2_lite');
    setEditingIdx(null);
    setHasHydratedFromMeta(false);
    setSelectedTypeId(null);
    setSelectedMaterialId(null);
    setStyleSearch('');
    setSelectedStyleId(null);
    setUploadedStyle(null);
    setInlineWgStyle(null);
    setIsUploadingReference(false);
    setUploadingSide(null);
    setReferenceUploadError(null);
    setWorkingPayload(null);
    // favorites persist across opens; do not reset favoriteStyleIds
    // Reference must be explicitly entered per item (no surname defaults).
    setReference('');
    setWidthMm('');
    setHeightMm('');
    setGlassTypeCode('clear');
    setBeadingTypeCode('sculptured');
    setVariantCode('chamfered');
    setStyleFamilyCategory(DEFAULT_STYLE_FAMILY_CATEGORY);
    setFrameColorCode('white-smooth');
    setFrameColorExtCode('white-smooth');
    setFrameColorIntCode('white-smooth');
    setFrameColorExtCustomRal('');
    setFrameColorIntCustomRal('');
    setFrameColorExtCustomHex('');
    setFrameColorIntCustomHex('');
    setShowRalFieldValidation(false);
    setShowBulkRalFieldValidation(false);
    setShowReferenceFieldValidation(false);
    setShowDimensionFieldValidation(false);
    setDesignDimsOn(false);
    setHandleColorCode('white');
    setHandleStyleCode('offset');
    setDoorHingePlacement('left');
    setDoorOpenDirection('out');
    setDoorMasterLeaf(DEFAULT_MASTER_LEAF);
    setDoorPanelCount(BIFOLD_PANEL_COUNT_RANGE.default);
    setDoorStackDirection(DEFAULT_STACK_DIRECTION);
    setDoorTrafficDoor(DEFAULT_TRAFFIC_DOOR);
    setDoorThreshold(DEFAULT_THRESHOLD);
    setGlassSpecification(DEFAULT_GLASS_SPECIFICATION);
    setPendingDoorRedraw(null);
    setWindowHingeTypeCode('standard');
    setJointStyle('mitre-45');
    setNotes('');
    setQty('1');
    setTrickleVents('no');
    setTrickleVentsPosition('sash');
    setCillOption('none');
    setExtTopMm('');
    setExtBottomMm('');
    setExtLeftMm('');
    setExtRightMm('');
    setGeorgianOn('no');
    setGeorgianCols('3');
    setGeorgianRows('4');
    setGeorgianWidthMm('18');
    setGeorgianOffXPct('0');
    setGeorgianOffYPct('0');
    setAstragalOn('no');
    setAstragalCols('3');
    setAstragalRows('4');
    setAstragalWidthMm('18');
    setAstragalOffXPct('0');
    setAstragalOffYPct('0');
    setAstragalBarSide('none');
    setGlassStyleTilePath('');
    setDesignDimsOn(false);
  };

  const openNew = () => {
    resetModalState();
    setOpen(true);
  };

  const openCopyModal = async (idx: number) => {
    setCopySourceIdx(idx);
    setCopyModalOpen(true);
    setCopyMode(documentId ? 'same_version' : (customerId ? 'related_customer' : 'any_existing'));
    setCopyError(null);
    setCopySuccess(null);
    setCopyTargetDocumentId('');
    setCopyTargetVersionId('');
    setCopyTargets([]);
    setCopyTargetVersions([]);
    setCopySameQuoteModeEnabled(true);
    setCopyAllFenestrationItems(false);
    setCopyLoadingTargets(false);
    setCopyLoadingVersions(false);
  };

  const submitCopyToQuote = async () => {
    if (copySourceIdx == null) return;
    const targetVersionId = String(copyTargetVersionId || '').trim();
    if (!targetVersionId) {
      setCopyError('Please select a target version.');
      return;
    }
    if (
      documentId &&
      currentVersionId &&
      String(copyTargetDocumentId || '') === String(documentId) &&
      String(targetVersionId) === String(currentVersionId)
    ) {
      setCopyError('Use Clone to duplicate in this active version. Copy is for other versions/documents.');
      return;
    }
    const sourceLine = lines[copySourceIdx];
    if (!sourceLine || !isVisualDesignerLine(sourceLine)) {
      setCopyError('Selected Visual Designer item is unavailable.');
      return;
    }
    const sourceLinesToCopy = copyAllFenestrationItems
      ? lines.filter((line) => isVisualDesignerLine(line))
      : [sourceLine];
    if (sourceLinesToCopy.length === 0) {
      setCopyError('No Visual Designer items found to copy.');
      return;
    }
    setCopySubmitting(true);
    setCopyError(null);
    setCopySuccess(null);
    try {
      const copied = await documentsService.copyVisualDesignerLinesToDocument(
        '',
        sourceLinesToCopy,
        targetVersionId,
        currentVersionId || null
      );
      if (copied <= 0) {
        setCopyError('Nothing was copied.');
        return;
      }
      const targetLabel = copyTargetVersions.find((t) => t.id === targetVersionId)?.label || 'target version';
      setCopySuccess(`Copied ${copied} item${copied === 1 ? '' : 's'} to ${targetLabel}.`);
    } catch (e: any) {
      setCopyError(e?.message || 'Failed to copy to target quote');
    } finally {
      setCopySubmitting(false);
    }
  };

  useEffect(() => {
    if (!copyModalOpen) return;
    if (!documentId || !currentVersionId) {
      setCopySameQuoteModeEnabled(false);
      return;
    }
    const localSameDocVersions = (availableVersions || []).filter(v => String(v.id || '').trim().length > 0);
    if (localSameDocVersions.length > 0) {
      const hasOtherVersion = localSameDocVersions.some((v) => String(v.id) !== String(currentVersionId));
      setCopySameQuoteModeEnabled(hasOtherVersion);
      if (!hasOtherVersion && copyMode === 'same_version') {
        setCopyMode(customerId ? 'related_customer' : 'any_existing');
      }
      return;
    }
    let cancelled = false;
    const loadCurrentDocVersionAvailability = async () => {
      try {
        const versions = await documentsService.listVersions(String(documentId));
        const hasOtherVersion = (versions || []).some((v) => String(v.id) !== String(currentVersionId));
        if (!cancelled) {
          setCopySameQuoteModeEnabled(hasOtherVersion);
          if (!hasOtherVersion && copyMode === 'same_version') {
            setCopyMode(customerId ? 'related_customer' : 'any_existing');
          }
        }
      } catch {
        if (!cancelled) {
          setCopySameQuoteModeEnabled(false);
          if (copyMode === 'same_version') {
            setCopyMode(customerId ? 'related_customer' : 'any_existing');
          }
        }
      }
    };
    void loadCurrentDocVersionAvailability();
    return () => { cancelled = true; };
  }, [copyModalOpen, documentId, currentVersionId, customerId, copyMode, availableVersions]);

  useEffect(() => {
    if (!copyModalOpen) return;
    let cancelled = false;

    const loadDocumentsForMode = async () => {
      if (copyMode === 'same_version') {
        if (!documentId) {
          if (!cancelled) {
            setCopyTargets([]);
            setCopyTargetDocumentId('');
            setCopyLoadingTargets(false);
            setCopyError('Current document context is unavailable.');
          }
          return;
        }
        const currentLabel = 'This quote / estimate';
        if (!cancelled) {
          setCopyTargets([{ id: String(documentId), label: currentLabel }]);
          setCopyTargetDocumentId(String(documentId));
          setCopyLoadingTargets(false);
        }
        return;
      }

      setCopyLoadingTargets(true);
      try {
        if (copyMode === 'related_customer' && !customerId) {
          throw new Error('Customer context is unavailable for related copy mode.');
        }
        const docs = copyMode === 'related_customer'
          ? await documentsService.getDocumentsForCustomer(String(customerId || ''))
          : await documentsService.getAllDocumentsForLinking();
        const targetDocs = (docs || [])
          .filter((d: any) => String(d.kind || '') !== 'Invoice')
          .filter((d: any) => String(d.status || '').toLowerCase() !== 'void')
          .filter((d: any) => copyMode !== 'related_customer' || String(d.id || '') !== String(documentId || ''))
          .map((d: any) => ({
            id: String(d.id),
            label: `${String(d.lifecycleRef || '(no ref)')} - ${String(d.title || d.kind || 'Document')}`,
          }));
        if (!cancelled) {
          setCopyTargets(targetDocs);
          setCopyTargetDocumentId(targetDocs[0]?.id || '');
        }
      } catch (e: any) {
        if (!cancelled) {
          setCopyTargets([]);
          setCopyTargetDocumentId('');
          setCopyError(e?.message || 'Failed to load target documents');
        }
      } finally {
        if (!cancelled) setCopyLoadingTargets(false);
      }
    };

    void loadDocumentsForMode();
    return () => { cancelled = true; };
  }, [copyModalOpen, copyMode, documentId, customerId]);

  useEffect(() => {
    if (!copyModalOpen) return;
    const targetDocId = String(copyTargetDocumentId || '').trim();
    if (!targetDocId) {
      setCopyTargetVersions([]);
      setCopyTargetVersionId('');
      return;
    }

    let cancelled = false;
    const loadVersions = async () => {
      setCopyLoadingVersions(true);
      try {
        const versions = (String(targetDocId) === String(documentId) && (availableVersions || []).length > 0)
          ? (availableVersions || [])
          : await documentsService.listVersions(targetDocId);
        const mapped = (versions || [])
          .map((v) => ({
            id: String((v as any).id),
            label: `Version ${Number((v as any).versionNumber)}${(v as any).isActive ? ' (active)' : ''}`,
            isActive: !!(v as any).isActive,
          }))
          .filter((v) => {
            if (!documentId || !currentVersionId) return true;
            if (String(targetDocId) !== String(documentId)) return true;
            return String(v.id) !== String(currentVersionId);
          });
        if (!cancelled) {
          setCopyTargetVersions(mapped);
          const preferred = mapped.find((v) => v.isActive)?.id || mapped[0]?.id || '';
          setCopyTargetVersionId(preferred);
        }
      } catch (e: any) {
        if (!cancelled) {
          setCopyTargetVersions([]);
          setCopyTargetVersionId('');
          setCopyError(e?.message || 'Failed to load target versions');
        }
      } finally {
        if (!cancelled) setCopyLoadingVersions(false);
      }
    };
    void loadVersions();
    return () => { cancelled = true; };
  }, [copyModalOpen, copyTargetDocumentId, documentId, currentVersionId, availableVersions]);

  useEffect(() => {
    if (!open) return;
    // Lazy-load favorites once per mount/session (and keep them in memory)
    if (favoritesLoaded) return;
    (async () => {
      try {
        const ids = await windowStyleFavoritesService.listFavoriteStyleIds();
        setFavoriteStyleIds(new Set(ids));
      } catch (e) {
        // Non-fatal
        console.warn('Failed to load window style favorites', e);
      } finally {
        setFavoritesLoaded(true);
      }
    })();
  }, [open, favoritesLoaded]);

  const toggleFavorite = async (styleId: number) => {
    const id = Number(styleId);
    const nextIsFavorite = !favoriteStyleIds.has(id);

    // Optimistic update
    setFavoriteStyleIds(prev => {
      const n = new Set(prev);
      if (nextIsFavorite) n.add(id);
      else n.delete(id);
      return n;
    });

    setSavingFavoriteIds(prev => new Set(prev).add(id));
    try {
      await windowStyleFavoritesService.setFavorite(id, nextIsFavorite);
    } catch (e) {
      // Revert on failure
      setFavoriteStyleIds(prev => {
        const n = new Set(prev);
        if (nextIsFavorite) n.delete(id);
        else n.add(id);
        return n;
      });
      console.warn('Failed to save favorite', e);
    } finally {
      setSavingFavoriteIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const openEdit = (idx: number) => {
    const line = lines[idx];
    const meta = (line as any).metaJson as VisualDesignerMetaV1 | undefined;
    if (!meta) return;

    setDesignerVersion(resolveEditableDesignerVersion(meta, line));
    setEditingIdx(idx);
    setHasHydratedFromMeta(true);
    setShowRalFieldValidation(false);
    setShowBulkRalFieldValidation(false);
    setShowReferenceFieldValidation(false);
    setShowDimensionFieldValidation(false);
    setStyleSearch('');
    setUploadedStyle(null);
    setReferenceUploadError(null);
    // v2 Lite: restore the saved WG payload snapshot for this quote line (do NOT fall back to library template).
    if (meta.designer_version === 'v2_lite') {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      const tpCopy = tp && (tp as any).root ? deepCloneJson(tp) : null;
      const tpCopyAny = tpCopy as any;
      const optLogical = coerceLogicalDivsSnapshot((meta as any)?.specs?.options?.logical_divs);
      if (tpCopyAny && optLogical) {
        tpCopyAny.logicalDivs = deepCloneJson(optLogical as any);
      }
      setWorkingPayload(tpCopy);
    } else {
      setWorkingPayload(null);
    }
    // Back-compat: if older meta stored aluminium/timber as "type", infer into material + default type
    const storedTypeId = (meta as any).window_type?.id ?? null;
    const storedMaterialId = (meta as any).window_material?.id ?? null;
    if (storedTypeId === 'aluminium' || storedTypeId === 'timber') {
      setSelectedTypeId('casement');
      setSelectedMaterialId(storedTypeId);
    } else {
      setSelectedTypeId(storedTypeId);
      setSelectedMaterialId(storedMaterialId);
    }
    {
      const styleId = (meta as any)?.window_style?.id;
      const src = String((meta as any)?.window_style?.source || '').toLowerCase();
      if (typeof styleId === 'string' && (src === 'upload' || styleId.startsWith('upload:'))) {
        const presExt =
          (meta as any)?.window_style?.preview_path_presentation_external ||
          (meta as any)?.window_style?.preview_path_external ||
          '';
        const presInt =
          (meta as any)?.window_style?.preview_path_presentation_internal ||
          (meta as any)?.window_style?.preview_path_internal ||
          '';
        const desExt =
          (meta as any)?.window_style?.preview_path_design_external ||
          '';
        const desInt =
          (meta as any)?.window_style?.preview_path_design_internal ||
          '';
        setUploadedStyle({
          id: styleId,
          name: String((meta as any)?.window_style?.name || 'Uploaded reference'),
          category: 'bespoke',
          source: 'upload',
          presentationExternalUrl: String(presExt || ''),
          presentationInternalUrl: String(presInt || ''),
          designExternalUrl: String(desExt || ''),
          designInternalUrl: String(desInt || ''),
          originalFilename: (meta as any)?.window_style?.original_filename ?? null,
          originalFilenameInternal: (meta as any)?.window_style?.original_filename_internal ?? null,
        });
      }
      setSelectedStyleId(styleId as any);
    }
    setReference(meta.specs.reference ?? '');
    const finalEmptyMeasures = requireSurveyorMeasures || finalSurveyUsesEmptyMeasureFields(meta);
    const templateDimsForEdit = (() => {
      if (finalEmptyMeasures) return null;
      if (meta.designer_version !== 'v2_lite') return null;
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        const d = deriveFromWindowGeneratorPayload(tp);
        return {
          width: d?.width_mm ?? null,
          height: d?.height_mm ?? null,
        };
      } catch {
        return null;
      }
    })();
    const widthForEdit = finalEmptyMeasures
      ? (meta.specs.width_mm ?? null)
      : (
        meta.specs.width_mm ??
        (meta.specs.options as any)?.bay_tangent_width_mm ??
        templateDimsForEdit?.width ??
        null
      );
    const heightForEdit = finalEmptyMeasures
      ? (meta.specs.height_mm ?? null)
      : (
        meta.specs.height_mm ??
        templateDimsForEdit?.height ??
        null
      );
    setWidthMm(widthForEdit != null ? String(widthForEdit) : '');
    setHeightMm(heightForEdit != null ? String(heightForEdit) : '');
    {
      const o: any = (meta.specs as any)?.options || {};
      const tile = String(o.glass_style_tile ?? '').trim();
      setGlassStyleTilePath(tile);
      const gt = String((meta.specs as any)?.glass_type_code || '').toLowerCase();
      if (tile) setGlassTypeCode('obscured');
      else if (gt === 'obscured') setGlassTypeCode('obscured');
      else if (gt === 'clear') setGlassTypeCode('clear');
      else {
        const lbl = String((meta.specs as any)?.glass_type || '').toLowerCase();
        setGlassTypeCode(lbl.includes('obsc') ? 'obscured' : 'clear');
      }
    }
    setVariantCode(getStoredVariantCode(meta.specs));
    setBeadingTypeCode(getStoredBeadingTypeCode(meta.specs));
    setStyleFamilyCategory(normalizeStyleFamilyCategory(meta?.window_style?.category));
    {
      const frameState = getStoredFrameSideCodes(meta.specs);
      setFrameColorCode(frameState.combinedCode);
      setFrameColorExtCode(frameState.extCode);
      setFrameColorIntCode(frameState.intCode);
      const frameExtName = normalizeRalText(String((meta.specs as any)?.frame_color_external || ''));
      const frameIntName = normalizeRalText(String((meta.specs as any)?.frame_color_internal || ''));
      const frameExtHex = parseHexColour((meta.specs as any)?.options?.frame_color_hex_external) || '';
      const frameIntHex = parseHexColour((meta.specs as any)?.options?.frame_color_hex_internal) || '';
      setFrameColorExtCustomRal(frameState.extCode === CUSTOM_FRAME_COLOUR_CODE ? frameExtName : '');
      setFrameColorIntCustomRal(frameState.intCode === CUSTOM_FRAME_COLOUR_CODE ? frameIntName : '');
      setFrameColorExtCustomHex(frameState.extCode === CUSTOM_FRAME_COLOUR_CODE ? frameExtHex : '');
      setFrameColorIntCustomHex(frameState.intCode === CUSTOM_FRAME_COLOUR_CODE ? frameIntHex : '');
    }
    const hc = (meta.specs?.options as any)?.handle_color_code;
    setHandleColorCode(getStoredHandleColourCode(hc));
    setGlassSpecification(parseGlassSpecificationCode((meta.specs?.options as any)?.glass_specification));
    setDesignDimsOn(!!(meta.specs?.options as any)?.design_dims_on);
    {
      const hs = (meta.specs?.options as any)?.handle_style_code;
      const lbl = (meta.specs?.options as any)?.handle_style;
      setHandleStyleCode(normalizeHandleStyleForType(parseStoredHandleStyleCode(hs, lbl), isDoorTypeId(storedTypeId)));
    }
    {
      const hp = (meta.specs?.options as any)?.hinge_placement;
      setDoorHingePlacement(hp === 'right' ? 'right' : 'left');
      const od = String((meta.specs?.options as any)?.open_direction || '').toLowerCase();
      setDoorOpenDirection(od === 'in' ? 'in' : od === 'bi-directional' ? 'bi-directional' : 'out');
      const o: any = meta.specs?.options || {};
      const ml = String(o.master_leaf || '').toLowerCase();
      setDoorMasterLeaf(ml === 'left' || ml === 'right' ? ml : DEFAULT_MASTER_LEAF);
      const storedKind = getDoorKind((meta as any).window_type);
      setDoorPanelCount(clampPanelCount(storedKind === 'slider' ? 'slider' : 'bifold', o.panel_count));
      const sd = String(o.stack_direction || '').toLowerCase();
      setDoorStackDirection(sd === 'left' || sd === 'right' || sd === 'split' ? sd : DEFAULT_STACK_DIRECTION);
      const td = String(o.traffic_door || '').toLowerCase();
      setDoorTrafficDoor(td === 'none' || td === 'left' || td === 'right' ? td : DEFAULT_TRAFFIC_DOOR);
      setDoorThreshold(parseThresholdCode(o.threshold));
    }
    {
      const o: any = meta.specs?.options || {};
      const raw = String(o.window_hinge_type_code || o.hinge_type_code || o.window_hinge_type || o.hinge_type || 'standard').toLowerCase();
      const next: WindowHingeTypeCode =
        raw.includes('egress') ? 'egress'
        : (raw.includes('ez') && raw.includes('clean')) ? 'ez_clean'
        : raw.includes('fire') ? 'fire_rated'
        : 'standard';
      setWindowHingeTypeCode(next);
    }
    {
      const o: any = meta.specs?.options || {};
      setJointStyle(String(o.joint_style || '').toLowerCase() === 'timber-weld' ? 'timber-weld' : 'mitre-45');
    }
    setNotes(stripAutoDerivedNotes(meta.specs.notes ?? ''));
    setQty(String(line.qty ?? 1));
    const payloadTrickleForEdit = (() => {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        return deriveFromWindowGeneratorPayload(tp).trickle;
      } catch {
        return null;
      }
    })();
    setTrickleVents(
      meta.specs.options?.trickle_vents === true
        ? 'yes'
        : meta.specs.options?.trickle_vents === false
          ? 'no'
          : payloadTrickleForEdit?.enabled === true
            ? 'yes'
            : 'no'
    );
    setTrickleVentsPosition(
      (meta.specs.options as any)?.trickle_vents_position
        ? parseSemanticTricklePosition((meta.specs.options as any)?.trickle_vents_position)
        : payloadTrickleForEdit?.position
          ? parseSemanticTricklePosition(payloadTrickleForEdit.position)
          : 'sash'
    );
    setDesignDimsOn((meta.specs.options as any)?.design_dims_on === true);
    const optFromMeta = (meta.specs.options as any)?.cill_option;
    if (optFromMeta === 'none' || optFromMeta === '150' || optFromMeta === '180' || optFromMeta === 'stub') {
      setCillOption(optFromMeta);
    } else if (finalEmptyMeasures) {
      // Final Survey starts with cill cleared; do not infer from drawing payload.
      setCillOption('none');
    } else {
      // Back-compat inference
      const ct = (meta.specs.options as any)?.cill_type;
      const ch = (meta.specs.options as any)?.cill_height_mm;
      const ci = (meta.specs.options as any)?.cill_included;
      if (ct === 'Stub') setCillOption('stub');
      else if (Number(ch) === 150) setCillOption('150');
      else if (Number(ch) === 180) setCillOption('180');
      else if (ci) setCillOption('150');
      else setCillOption('none');
    }

    const payloadDerivedForMeta = (() => {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        return deriveFromWindowGeneratorPayload(tp);
      } catch {
        return null;
      }
    })();
    const payloadExtrasForMeta = payloadDerivedForMeta?.extras as any;

    // v2/v2_lite WG extras (extenders + bars + glass style)
    {
      const o: any = meta.specs.options || {};
      setExtTopMm(String(o.ext_top_mm ?? ''));
      setExtBottomMm(String(o.ext_bottom_mm ?? ''));
      setExtLeftMm(String(o.ext_left_mm ?? ''));
      setExtRightMm(String(o.ext_right_mm ?? ''));
      setGeorgianOn(
        o.georgian_bars === true
          ? 'yes'
          : o.georgian_bars === false
            ? 'no'
            : payloadExtrasForMeta?.georgian_on === true
              ? 'yes'
              : 'no'
      );
      setGeorgianCols(String(o.georgian_cols ?? payloadExtrasForMeta?.georgian_cols ?? '3'));
      setGeorgianRows(String(o.georgian_rows ?? payloadExtrasForMeta?.georgian_rows ?? '4'));
      setGeorgianWidthMm(String(o.georgian_width_mm ?? payloadExtrasForMeta?.georgian_width_mm ?? '18'));
      setGeorgianOffXPct(String(o.georgian_offset_x_pct ?? payloadExtrasForMeta?.georgian_offset_x_pct ?? '0'));
      setGeorgianOffYPct(String(o.georgian_offset_y_pct ?? payloadExtrasForMeta?.georgian_offset_y_pct ?? '0'));
      setAstragalOn(
        o.astragal_bars === true
          ? 'yes'
          : o.astragal_bars === false
            ? 'no'
            : payloadExtrasForMeta?.astragal_on === true
              ? 'yes'
              : 'no'
      );
      setAstragalCols(String(o.astragal_cols ?? payloadExtrasForMeta?.astragal_cols ?? '3'));
      setAstragalRows(String(o.astragal_rows ?? payloadExtrasForMeta?.astragal_rows ?? '4'));
      setAstragalWidthMm(String(o.astragal_width_mm ?? payloadExtrasForMeta?.astragal_width_mm ?? '18'));
      setAstragalOffXPct(String(o.astragal_offset_x_pct ?? payloadExtrasForMeta?.astragal_offset_x_pct ?? '0'));
      setAstragalOffYPct(String(o.astragal_offset_y_pct ?? payloadExtrasForMeta?.astragal_offset_y_pct ?? '0'));
      setGlassStyleTilePath(String(o.glass_style_tile ?? ''));
      {
        const raw = String(o.astragal_bar_side || o.astragal_side || 'none').toLowerCase();
        const side: BarSideCode =
          raw === 'none' ? 'none'
          : raw === 'inside' || raw === 'internal' ? 'inside'
          : raw === 'outside' || raw === 'external' ? 'outside'
          : raw === 'both' ? 'both'
          : 'none';
        setAstragalBarSide(side);
      }
    }
    setStep(4);
    setOpen(true);
  };

  const openClone = (idx: number) => {
    const line = lines[idx];
    const meta = (line as any).metaJson as VisualDesignerMetaV1 | undefined;
    if (!meta) return;

    setDesignerVersion(resolveEditableDesignerVersion(meta, line));
    // Hydrate state from existing item, but keep editingIdx null so Save adds a new line.
    setEditingIdx(null);
    setHasHydratedFromMeta(true);
    setShowRalFieldValidation(false);
    setShowBulkRalFieldValidation(false);
    setShowReferenceFieldValidation(false);
    setShowDimensionFieldValidation(false);
    setStyleSearch('');
    setUploadedStyle(null);
    setReferenceUploadError(null);
    // v2 Lite: clone should also start from the saved payload snapshot for this quote line (unique entity).
    if (meta.designer_version === 'v2_lite') {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      const tpCopy = tp && (tp as any).root ? deepCloneJson(tp) : null;
      const tpCopyAny = tpCopy as any;
      const optLogical = coerceLogicalDivsSnapshot((meta as any)?.specs?.options?.logical_divs);
      if (tpCopyAny && optLogical) {
        tpCopyAny.logicalDivs = deepCloneJson(optLogical as any);
      }
      setWorkingPayload(tpCopy);
    } else {
      setWorkingPayload(null);
    }

    const storedTypeId = (meta as any).window_type?.id ?? null;
    const storedMaterialId = (meta as any).window_material?.id ?? null;
    if (storedTypeId === 'aluminium' || storedTypeId === 'timber') {
      setSelectedTypeId('casement');
      setSelectedMaterialId(storedTypeId);
    } else {
      setSelectedTypeId(storedTypeId);
      setSelectedMaterialId(storedMaterialId);
    }

    {
      const styleId = (meta as any)?.window_style?.id;
      const src = String((meta as any)?.window_style?.source || '').toLowerCase();
      if (typeof styleId === 'string' && (src === 'upload' || styleId.startsWith('upload:'))) {
        const presExt =
          (meta as any)?.window_style?.preview_path_presentation_external ||
          (meta as any)?.window_style?.preview_path_external ||
          '';
        const presInt =
          (meta as any)?.window_style?.preview_path_presentation_internal ||
          (meta as any)?.window_style?.preview_path_internal ||
          '';
        const desExt =
          (meta as any)?.window_style?.preview_path_design_external ||
          '';
        const desInt =
          (meta as any)?.window_style?.preview_path_design_internal ||
          '';
        setUploadedStyle({
          id: styleId,
          name: String((meta as any)?.window_style?.name || 'Uploaded reference'),
          category: 'bespoke',
          source: 'upload',
          presentationExternalUrl: String(presExt || ''),
          presentationInternalUrl: String(presInt || ''),
          designExternalUrl: String(desExt || ''),
          designInternalUrl: String(desInt || ''),
          originalFilename: (meta as any)?.window_style?.original_filename ?? null,
          originalFilenameInternal: (meta as any)?.window_style?.original_filename_internal ?? null,
        });
      }
      setSelectedStyleId(styleId as any);
    }
    // Cloned items should require a new reference (avoid silently reusing surname/default).
    setReference('');
    const templateDimsForClone = (() => {
      if (requireSurveyorMeasures) return null;
      if (meta.designer_version !== 'v2_lite') return null;
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        const d = deriveFromWindowGeneratorPayload(tp);
        return {
          width: d?.width_mm ?? null,
          height: d?.height_mm ?? null,
        };
      } catch {
        return null;
      }
    })();
    const widthForClone = requireSurveyorMeasures
      ? null
      : (
        meta.specs.width_mm ??
        (meta.specs.options as any)?.bay_tangent_width_mm ??
        templateDimsForClone?.width ??
        null
      );
    const heightForClone = requireSurveyorMeasures
      ? null
      : (
        meta.specs.height_mm ??
        templateDimsForClone?.height ??
        null
      );
    setWidthMm(widthForClone != null ? String(widthForClone) : '');
    setHeightMm(heightForClone != null ? String(heightForClone) : '');
    {
      const o: any = (meta.specs as any)?.options || {};
      const tile = String(o.glass_style_tile ?? '').trim();
      setGlassStyleTilePath(tile);
      const gt = String((meta.specs as any)?.glass_type_code || '').toLowerCase();
      if (tile) setGlassTypeCode('obscured');
      else if (gt === 'obscured') setGlassTypeCode('obscured');
      else if (gt === 'clear') setGlassTypeCode('clear');
      else {
        const lbl = String((meta.specs as any)?.glass_type || '').toLowerCase();
        setGlassTypeCode(lbl.includes('obsc') ? 'obscured' : 'clear');
      }
    }

    setVariantCode(getStoredVariantCode(meta.specs));
    setBeadingTypeCode(getStoredBeadingTypeCode(meta.specs));
    setStyleFamilyCategory(normalizeStyleFamilyCategory(meta?.window_style?.category));

    {
      const frameState = getStoredFrameSideCodes(meta.specs);
      setFrameColorCode(frameState.combinedCode);
      setFrameColorExtCode(frameState.extCode);
      setFrameColorIntCode(frameState.intCode);
      const frameExtName = normalizeRalText(String((meta.specs as any)?.frame_color_external || ''));
      const frameIntName = normalizeRalText(String((meta.specs as any)?.frame_color_internal || ''));
      const frameExtHex = parseHexColour((meta.specs as any)?.options?.frame_color_hex_external) || '';
      const frameIntHex = parseHexColour((meta.specs as any)?.options?.frame_color_hex_internal) || '';
      setFrameColorExtCustomRal(frameState.extCode === CUSTOM_FRAME_COLOUR_CODE ? frameExtName : '');
      setFrameColorIntCustomRal(frameState.intCode === CUSTOM_FRAME_COLOUR_CODE ? frameIntName : '');
      setFrameColorExtCustomHex(frameState.extCode === CUSTOM_FRAME_COLOUR_CODE ? frameExtHex : '');
      setFrameColorIntCustomHex(frameState.intCode === CUSTOM_FRAME_COLOUR_CODE ? frameIntHex : '');
    }
    const hcClone = (meta.specs?.options as any)?.handle_color_code;
    setHandleColorCode(getStoredHandleColourCode(hcClone));
    setGlassSpecification(parseGlassSpecificationCode((meta.specs?.options as any)?.glass_specification));
    setDesignDimsOn(!!(meta.specs?.options as any)?.design_dims_on);
    {
      const hs = (meta.specs?.options as any)?.handle_style_code;
      const lbl = (meta.specs?.options as any)?.handle_style;
      setHandleStyleCode(normalizeHandleStyleForType(parseStoredHandleStyleCode(hs, lbl), isDoorTypeId(storedTypeId)));
    }
    {
      const hp = (meta.specs?.options as any)?.hinge_placement;
      setDoorHingePlacement(hp === 'right' ? 'right' : 'left');
      const od = String((meta.specs?.options as any)?.open_direction || '').toLowerCase();
      setDoorOpenDirection(od === 'in' ? 'in' : od === 'bi-directional' ? 'bi-directional' : 'out');
      const o: any = meta.specs?.options || {};
      const ml = String(o.master_leaf || '').toLowerCase();
      setDoorMasterLeaf(ml === 'left' || ml === 'right' ? ml : DEFAULT_MASTER_LEAF);
      const storedKind = getDoorKind((meta as any).window_type);
      setDoorPanelCount(clampPanelCount(storedKind === 'slider' ? 'slider' : 'bifold', o.panel_count));
      const sd = String(o.stack_direction || '').toLowerCase();
      setDoorStackDirection(sd === 'left' || sd === 'right' || sd === 'split' ? sd : DEFAULT_STACK_DIRECTION);
      const td = String(o.traffic_door || '').toLowerCase();
      setDoorTrafficDoor(td === 'none' || td === 'left' || td === 'right' ? td : DEFAULT_TRAFFIC_DOOR);
      setDoorThreshold(parseThresholdCode(o.threshold));
    }
    {
      const o: any = meta.specs?.options || {};
      const raw = String(o.window_hinge_type_code || o.hinge_type_code || o.window_hinge_type || o.hinge_type || 'standard').toLowerCase();
      const next: WindowHingeTypeCode =
        raw.includes('egress') ? 'egress'
        : (raw.includes('ez') && raw.includes('clean')) ? 'ez_clean'
        : raw.includes('fire') ? 'fire_rated'
        : 'standard';
      setWindowHingeTypeCode(next);
    }
    {
      const o: any = meta.specs?.options || {};
      setJointStyle(String(o.joint_style || '').toLowerCase() === 'timber-weld' ? 'timber-weld' : 'mitre-45');
    }

    setNotes(stripAutoDerivedNotes(meta.specs.notes ?? ''));
    setQty(String(line.qty ?? 1));
    const payloadTrickleForClone = (() => {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        return deriveFromWindowGeneratorPayload(tp).trickle;
      } catch {
        return null;
      }
    })();
    setTrickleVents(
      meta.specs.options?.trickle_vents === true
        ? 'yes'
        : meta.specs.options?.trickle_vents === false
          ? 'no'
          : payloadTrickleForClone?.enabled === true
            ? 'yes'
            : 'no'
    );
    setTrickleVentsPosition(
      (meta.specs.options as any)?.trickle_vents_position
        ? parseSemanticTricklePosition((meta.specs.options as any)?.trickle_vents_position)
        : payloadTrickleForClone?.position
          ? parseSemanticTricklePosition(payloadTrickleForClone.position)
          : 'sash'
    );

    const optFromMeta = (meta.specs.options as any)?.cill_option;
    if (requireSurveyorMeasures) {
      setCillOption('none');
    } else if (optFromMeta === 'none' || optFromMeta === '150' || optFromMeta === '180' || optFromMeta === 'stub') {
      setCillOption(optFromMeta);
    } else {
      const ct = (meta.specs.options as any)?.cill_type;
      const ch = (meta.specs.options as any)?.cill_height_mm;
      const ci = (meta.specs.options as any)?.cill_included;
      if (ct === 'Stub') setCillOption('stub');
      else if (Number(ch) === 150) setCillOption('150');
      else if (Number(ch) === 180) setCillOption('180');
      else if (ci) setCillOption('150');
      else setCillOption('none');
    }

    const payloadDerivedForClone = (() => {
      const tp = (meta as any)?.window_style?.templatePayload as WindowGeneratorPayload | undefined;
      if (!tp || !(tp as any).root || tp.w == null || tp.h == null) return null;
      try {
        return deriveFromWindowGeneratorPayload(tp);
      } catch {
        return null;
      }
    })();
    const payloadExtrasForClone = payloadDerivedForClone?.extras as any;

    // v2/v2_lite WG extras (extenders + bars + glass style)
    {
      const o: any = meta.specs.options || {};
      setExtTopMm(requireSurveyorMeasures ? '' : String(o.ext_top_mm ?? ''));
      setExtBottomMm(requireSurveyorMeasures ? '' : String(o.ext_bottom_mm ?? ''));
      setExtLeftMm(requireSurveyorMeasures ? '' : String(o.ext_left_mm ?? ''));
      setExtRightMm(requireSurveyorMeasures ? '' : String(o.ext_right_mm ?? ''));
      setGeorgianOn(
        o.georgian_bars === true
          ? 'yes'
          : o.georgian_bars === false
            ? 'no'
            : payloadExtrasForClone?.georgian_on === true
              ? 'yes'
              : 'no'
      );
      setGeorgianCols(String(o.georgian_cols ?? payloadExtrasForClone?.georgian_cols ?? '3'));
      setGeorgianRows(String(o.georgian_rows ?? payloadExtrasForClone?.georgian_rows ?? '4'));
      setGeorgianWidthMm(String(o.georgian_width_mm ?? payloadExtrasForClone?.georgian_width_mm ?? '18'));
      setGeorgianOffXPct(String(o.georgian_offset_x_pct ?? payloadExtrasForClone?.georgian_offset_x_pct ?? '0'));
      setGeorgianOffYPct(String(o.georgian_offset_y_pct ?? payloadExtrasForClone?.georgian_offset_y_pct ?? '0'));
      setAstragalOn(
        o.astragal_bars === true
          ? 'yes'
          : o.astragal_bars === false
            ? 'no'
            : payloadExtrasForClone?.astragal_on === true
              ? 'yes'
              : 'no'
      );
      setAstragalCols(String(o.astragal_cols ?? payloadExtrasForClone?.astragal_cols ?? '3'));
      setAstragalRows(String(o.astragal_rows ?? payloadExtrasForClone?.astragal_rows ?? '4'));
      setAstragalWidthMm(String(o.astragal_width_mm ?? payloadExtrasForClone?.astragal_width_mm ?? '18'));
      setAstragalOffXPct(String(o.astragal_offset_x_pct ?? payloadExtrasForClone?.astragal_offset_x_pct ?? '0'));
      setAstragalOffYPct(String(o.astragal_offset_y_pct ?? payloadExtrasForClone?.astragal_offset_y_pct ?? '0'));
      setGlassStyleTilePath(String(o.glass_style_tile ?? ''));
      {
        const raw = String(o.astragal_bar_side || o.astragal_side || 'none').toLowerCase();
        const side: BarSideCode =
          raw === 'none' ? 'none'
          : raw === 'inside' || raw === 'internal' ? 'inside'
          : raw === 'outside' || raw === 'external' ? 'outside'
          : raw === 'both' ? 'both'
          : 'none';
        setAstragalBarSide(side);
      }
    }

    setStep(4);
    setOpen(true);
  };

  const removeAll = () => {
    commitLines(prev => prev.filter(l => !isVisualDesignerLine(l)));
  };

  const onToggleChange = (value: 'off' | 'v2_lite') => {
    if (value === 'off') {
      setEnabled(false);
      removeAll();
      return;
    }
    setDesignerVersion('v2_lite');
    setEnabled(true);
    // Only auto-open the modal when enabling from OFF.
    if (!enabled) openNew();
  };

  const uploadReferenceImageVariant = async (variant: ManualUploadSlot, file: File) => {
    if (designerVersion !== 'v2_lite') return;
    setReferenceUploadError(null);
    setIsUploadingReference(true);
    setUploadingSide(variant);
    try {
      const maxBytes = 20 * 1024 * 1024; // compressed client-side before storage upload
      if (file.size > maxBytes) {
        throw new Error('File too large. Please upload an image under 20MB.');
      }

      const { baseId, publicUrl } = await uploadVisualDesignerReferenceImageVariantAndGetUrl({
        file,
        // Always create a fresh object (avoid overwrite/update policies).
        // We allow saving with a single image, so we don't need a shared baseId between variants.
        baseId: undefined,
        variant,
      });
      const styleId = `upload:${baseId}`;
      const baseName = String(file.name || '').replace(/\.[^/.]+$/, '').trim();

      setUploadedStyle((prev) => {
        const name = (prev?.name || '').trim() || baseName || 'Reference image';
        const prevExt = String(prev?.presentationExternalUrl || '').trim();
        const prevInt = String(prev?.presentationInternalUrl || '').trim();
        const prevDesignExt = String(prev?.designExternalUrl || '').trim();
        const prevDesignInt = String(prev?.designInternalUrl || '').trim();
        const next: UploadStyleOption = {
          id: styleId,
          name,
          category: 'bespoke',
          source: 'upload',
          presentationExternalUrl: variant === 'presentation-external' ? publicUrl : prevExt,
          presentationInternalUrl: variant === 'presentation-internal' ? publicUrl : prevInt,
          designExternalUrl: variant === 'design-external' ? publicUrl : prevDesignExt,
          designInternalUrl: variant === 'design-internal' ? publicUrl : prevDesignInt,
          originalFilename: variant === 'presentation-external' ? (file.name || null) : (prev?.originalFilename ?? null),
          originalFilenameInternal: variant === 'presentation-internal' ? (file.name || null) : (prev?.originalFilenameInternal ?? null),
          originalFilenameDesignExternal: variant === 'design-external' ? (file.name || null) : (prev?.originalFilenameDesignExternal ?? null),
          originalFilenameDesignInternal: variant === 'design-internal' ? (file.name || null) : (prev?.originalFilenameDesignInternal ?? null),
        };
        return next;
      });
    } catch (e) {
      setReferenceUploadError(e instanceof Error ? e.message : 'Failed to upload image');
    } finally {
      setIsUploadingReference(false);
      setUploadingSide(null);
    }
  };

  const hasUploadedReferenceImage = (style: UploadStyleOption | null): boolean => !!(
    style?.presentationExternalUrl ||
    style?.presentationInternalUrl ||
    style?.designExternalUrl ||
    style?.designInternalUrl
  );

  const removeReferenceImageVariant = (variant: ManualUploadSlot) => {
    setReferenceUploadError(null);
    const clearSlot = (style: UploadStyleOption): UploadStyleOption => {
      const next: UploadStyleOption = { ...style };
      if (variant === 'presentation-external') {
        next.presentationExternalUrl = '';
        next.originalFilename = null;
      } else if (variant === 'presentation-internal') {
        next.presentationInternalUrl = '';
        next.originalFilenameInternal = null;
      } else if (variant === 'design-external') {
        next.designExternalUrl = '';
        next.originalFilenameDesignExternal = null;
      } else {
        next.designInternalUrl = '';
        next.originalFilenameDesignInternal = null;
      }
      return next;
    };
    if (uploadedStyle && selectedStyleId === uploadedStyle.id && !hasUploadedReferenceImage(clearSlot(uploadedStyle))) {
      setSelectedStyleId(null);
    }
    setUploadedStyle((prev) => {
      if (!prev) return prev;
      return clearSlot(prev);
    });
  };

  const save = async (mode: 'close' | 'add_another') => {
    if (!selectedTypeId) {
      alert('Please select a window type');
      setStep(1);
      return;
    }
    if (!selectedMaterialId) {
      alert('Please select a material');
      setStep(2);
      return;
    }
    if (!selectedStyle) {
      alert('Please select a window style');
      setStep(3);
      return;
    }
    if (!reference.trim()) {
      setShowReferenceFieldValidation(true);
      setStep(4);
      return;
    }

    const w = clampFenestrationDimensionMm(widthMm.trim() ? Number(widthMm) : null, 'width');
    const h = clampFenestrationDimensionMm(heightMm.trim() ? Number(heightMm) : null, 'height');
    const keepFinalEmptyMeasures = requireSurveyorMeasures || finalSurveyUsesEmptyMeasureFields(
      editingIdx != null ? (lines[editingIdx] as any)?.metaJson : null,
    );
    if (requireSurveyorMeasures && (w == null || h == null || !widthMm.trim() || !heightMm.trim())) {
      setShowDimensionFieldValidation(true);
      setStep(4);
      return;
    }
    setShowDimensionFieldValidation(false);
    const persistBayDerived = !keepFinalEmptyMeasures || Boolean(widthMm.trim() && heightMm.trim());
    const q = qty.trim() ? Number(qty) : 1;
    const isExtCustom = frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE;
    const isIntCustom = frameColorIntCode === CUSTOM_FRAME_COLOUR_CODE;
    const extCustomRalValue = normalizeRalText(frameColorExtCustomRal);
    const intCustomRalValue = normalizeRalText(frameColorIntCustomRal);
    const extCustomHexValue = parseHexColour(frameColorExtCustomHex);
    const intCustomHexValue = parseHexColour(frameColorIntCustomHex);
    if (isExtCustom && (!extCustomRalValue || !extCustomHexValue)) {
      setShowRalFieldValidation(true);
      setStep(4);
      return;
    }
    if (isIntCustom && (!intCustomRalValue || !intCustomHexValue)) {
      setShowRalFieldValidation(true);
      setStep(4);
      return;
    }
    setShowRalFieldValidation(false);
    const frameSelection = getFrameSelectionFromSideCodes(frameColorExtCode, frameColorIntCode);
    const extFrameValue = isExtCustom ? extCustomRalValue : frameSelection.extValue;
    const intFrameValue = isIntCustom ? intCustomRalValue : frameSelection.intValue;
    const combinedFrameCode = (isExtCustom || isIntCustom) ? null : (frameSelection.combinedCode || frameSelection.extCode);
    const frameColourLabel = extFrameValue === intFrameValue
      ? extFrameValue
      : `${extFrameValue} (external) / ${intFrameValue} (internal)`;
    const effectiveFrameCodeExt = isExtCustom ? null : frameSelection.extCode;
    const effectiveFrameCodeInt = isIntCustom ? null : frameSelection.intCode;
    const derivedCillIncluded = cillOption !== 'none';
    const derivedCillType = cillOption === 'stub' ? 'Stub' : (derivedCillIncluded ? 'Regular' : null);
    const derivedCillHeight = cillOption === '150' ? 150 : (cillOption === '180' ? 180 : null);
    const variantOpt = variantCode ? (PROFILE_VARIANT_OPTIONS.find(v => v.code === variantCode) || null) : null;
    const beadingOpt = beadingTypeCode ? (BEADING_TYPE_OPTIONS.find(v => v.code === beadingTypeCode) || null) : null;
    const trickleEnabled = trickleVents === 'yes';
    const handleOpt = HANDLE_COLOUR_OPTIONS.find(x => x.code === handleColorCode) || HANDLE_COLOUR_OPTIONS[0];
    const handleStyleOpt = HANDLE_STYLE_OPTIONS.find(x => x.code === handleStyleCode) || HANDLE_STYLE_OPTIONS[0];
    const isDoor = isDoorTypeId(selectedTypeId);

    setIsSaving(true);
    try {
      const isLibraryStyle = 'source' in selectedStyle && selectedStyle.source === 'window_generator';
      const isUploadStyle = 'source' in selectedStyle && selectedStyle.source === 'upload';
      if (isUploadStyle) {
        const up = selectedStyle as UploadStyleOption;
        if (
          !String(up.presentationExternalUrl || '').trim() &&
          !String(up.presentationInternalUrl || '').trim() &&
          !String(up.designExternalUrl || '').trim() &&
          !String(up.designInternalUrl || '').trim()
        ) {
          alert('Please upload at least one reference image.');
          setStep(3);
          return;
        }
      }
      let resolvedStylePreviewPath: string | undefined;
      let resolvedStyleEmailPath: string | undefined;
      let v2Urls: {
        designExternalUrl: string;
        designInternalUrl: string;
        presentationExternalUrl: string;
        presentationInternalUrl: string;
        baySchematicUrl: string;
      } | null = null;
      if (isLibraryStyle && 'payload' in selectedStyle) {
        // Library style: generate PNG from payload, upload to storage, use URL for email/PDF.
        try {
          if (designerVersion === 'v2') {
            const expectsBaySchematic = isBayLikePayload((selectedStyle as any).payload);
            const hasStored = !!(
              (selectedStyle as any).preview_presentation_external_url &&
              (selectedStyle as any).preview_presentation_internal_url &&
              (selectedStyle as any).preview_design_external_url &&
              (selectedStyle as any).preview_design_internal_url &&
              (!expectsBaySchematic || ((selectedStyle as any).preview_path_bay_schematic || (selectedStyle as any).preview_path_plan_external))
            );
            if (hasStored) {
              v2Urls = {
                designExternalUrl: String((selectedStyle as any).preview_design_external_url),
                designInternalUrl: String((selectedStyle as any).preview_design_internal_url),
                presentationExternalUrl: String((selectedStyle as any).preview_presentation_external_url),
                presentationInternalUrl: String((selectedStyle as any).preview_presentation_internal_url),
                baySchematicUrl: String((selectedStyle as any).preview_path_bay_schematic || (selectedStyle as any).preview_path_plan_external || ''),
              };
              // Default “primary” image becomes presentation-outside (most customer-facing)
              resolvedStylePreviewPath = v2Urls.presentationExternalUrl;
              resolvedStyleEmailPath = v2Urls.presentationExternalUrl;
            } else {
              const urls = await uploadVisualDesignerPreviewsAndGetUrls(selectedStyle.payload);
              // Persist on the template for future reuse
              try {
                await windowGeneratorTemplatesService.updatePreviews(selectedStyle.id, urls);
                setLibraryTemplates(prev => prev.map(t => (t.id === selectedStyle.id ? ({ ...t, ...({
                  preview_design_external_url: urls.designExternalUrl,
                  preview_design_internal_url: urls.designInternalUrl,
                  preview_presentation_external_url: urls.presentationExternalUrl,
                  preview_presentation_internal_url: urls.presentationInternalUrl,
                  preview_path_bay_schematic: urls.baySchematicUrl,
                  preview_path_plan_external: urls.baySchematicUrl,
                  preview_external_url: urls.presentationExternalUrl,
                  preview_internal_url: urls.presentationInternalUrl,
                } as any) } as any) : t)));
              } catch {}
              resolvedStylePreviewPath = urls.presentationExternalUrl;
              resolvedStyleEmailPath = urls.presentationExternalUrl;
              v2Urls = urls;
            }
          } else if (designerVersion === 'v2_lite') {
            // v2 Lite: always generate previews for the configured payload; do NOT persist on the library template
            // Prefer the latest WG-exported payload snapshot to avoid stale width/height UI races.
            const payload = workingPayload || v2PayloadToUse || selectedStyle.payload;
            const urls = await uploadVisualDesignerPreviewsAndGetUrls(payload);
            resolvedStylePreviewPath = urls.presentationExternalUrl;
            resolvedStyleEmailPath = urls.presentationExternalUrl;
            v2Urls = urls;
          } else {
            const previewUrl = await uploadVisualDesignerPreviewAndGetUrl(selectedStyle.payload);
            resolvedStylePreviewPath = previewUrl;
            resolvedStyleEmailPath = previewUrl;
          }
        } catch (err) {
          console.error('Failed to generate/upload WG preview image', err);
          // Leave undefined so UI still works; email will show warning / use default
        }
      } else {
        if (isUploadStyle) {
          const up = selectedStyle as UploadStyleOption;
          const ext = String(up.presentationExternalUrl || '').trim();
          const int = String(up.presentationInternalUrl || '').trim();
          const designExt = String(up.designExternalUrl || '').trim();
          const designInt = String(up.designInternalUrl || '').trim();
          // Prefer presentation outside as primary, but allow design-only custom uploads.
          resolvedStylePreviewPath = ext || designExt || int || designInt;
          resolvedStyleEmailPath = resolvedStylePreviewPath;
        } else {
          const staticStyle = selectedStyle as WindowStyleConfig;
          resolvedStylePreviewPath = staticStyle.previewPath;
          resolvedStyleEmailPath = staticStyle.emailPath;
          if (isDoor) {
            const candidates = buildUpvcDoorImageCandidates({
              stylePreviewPath: staticStyle.previewPath || '',
              hingePlacement: doorHingePlacement,
              handleColorCode,
            });
            const resolved = await pickFirstLoadableDoorPath(candidates);
            resolvedStylePreviewPath = resolved || staticStyle.previewPath;
            resolvedStyleEmailPath = resolved || staticStyle.emailPath;
          }
        }
      }

      const meta: VisualDesignerMetaV1 = {
        feature: VISUAL_DESIGNER_FEATURE,
        schema_version: VISUAL_DESIGNER_SCHEMA_VERSION,
        quote_mode: true,
        designer_version: designerVersion,
        window_type: selectedType ? { id: selectedType.id, name: selectedType.name } : null,
        window_material: (() => {
          const m = MATERIAL_OPTIONS.find(x => x.id === selectedMaterialId);
          return m ? { id: m.id, name: m.name } : null;
        })(),
        window_style: {
          id: selectedStyle.id,
          name: selectedStyle.name,
          category: designerVersion === 'v2_lite' ? styleFamilyCategory : selectedStyle.category,
          preview_path: resolvedStylePreviewPath,
          email_path: resolvedStyleEmailPath,
          ...((designerVersion === 'v2' || designerVersion === 'v2_lite') && isLibraryStyle && 'payload' in selectedStyle && v2Urls
            ? {
                // Legacy two-image fields (presentation)
                preview_path_external: v2Urls.presentationExternalUrl,
                preview_path_internal: v2Urls.presentationInternalUrl,
                email_path_external: v2Urls.presentationExternalUrl,
                email_path_internal: v2Urls.presentationInternalUrl,
                // New four-image fields
                preview_path_design_external: v2Urls.designExternalUrl,
                preview_path_design_internal: v2Urls.designInternalUrl,
                preview_path_presentation_external: v2Urls.presentationExternalUrl,
                preview_path_presentation_internal: v2Urls.presentationInternalUrl,
                preview_path_bay_schematic: v2Urls.baySchematicUrl,
                preview_path_plan_external: v2Urls.baySchematicUrl,
                email_path_design_external: v2Urls.designExternalUrl,
                email_path_design_internal: v2Urls.designInternalUrl,
                email_path_presentation_external: v2Urls.presentationExternalUrl,
                email_path_presentation_internal: v2Urls.presentationInternalUrl,
                email_path_bay_schematic: v2Urls.baySchematicUrl,
              }
            : {}),
          ...(isUploadStyle
            ? {
                source: 'upload' as const,
                original_filename: (selectedStyle as UploadStyleOption).originalFilename ?? null,
                original_filename_internal: (selectedStyle as UploadStyleOption).originalFilenameInternal ?? null,
                original_filename_design_external: (selectedStyle as UploadStyleOption).originalFilenameDesignExternal ?? null,
                original_filename_design_internal: (selectedStyle as UploadStyleOption).originalFilenameDesignInternal ?? null,
                preview_path_external: String((selectedStyle as UploadStyleOption).presentationExternalUrl || '').trim(),
                preview_path_internal: String((selectedStyle as UploadStyleOption).presentationInternalUrl || '').trim(),
                email_path_external: String((selectedStyle as UploadStyleOption).presentationExternalUrl || '').trim(),
                email_path_internal: String((selectedStyle as UploadStyleOption).presentationInternalUrl || '').trim(),
                preview_path_design_external: String((selectedStyle as UploadStyleOption).designExternalUrl || '').trim(),
                preview_path_design_internal: String((selectedStyle as UploadStyleOption).designInternalUrl || '').trim(),
                preview_path_presentation_external: String((selectedStyle as UploadStyleOption).presentationExternalUrl || '').trim(),
                preview_path_presentation_internal: String((selectedStyle as UploadStyleOption).presentationInternalUrl || '').trim(),
                email_path_design_external: String((selectedStyle as UploadStyleOption).designExternalUrl || '').trim(),
                email_path_design_internal: String((selectedStyle as UploadStyleOption).designInternalUrl || '').trim(),
                email_path_presentation_external: String((selectedStyle as UploadStyleOption).presentationExternalUrl || '').trim(),
                email_path_presentation_internal: String((selectedStyle as UploadStyleOption).presentationInternalUrl || '').trim(),
              }
            : {}),
          ...(('payload' in (selectedStyle as any))
            ? {
                source: 'window_generator' as const,
                // Persist the merged quote-line payload for v2_lite so supplier emails,
                // reopened edits, and regenerated previews use the current overrides.
                templatePayload: deepCloneJson(
                  ((designerVersion === 'v2_lite'
                    ? (workingPayload || v2PayloadToUse || (selectedStyle as any).payload)
                    : (workingPayload || v2PayloadToUse || (selectedStyle as any).payload)) as any)
                ),
              }
            : {}),
        },
        specs: {
          reference: reference.trim(),
          width_mm: Number.isFinite(w as any) ? w : null,
          height_mm: Number.isFinite(h as any) ? h : null,
          glass_type: (() => {
            const p = glassStyleTilePath.trim();
            if (p) {
              const named = glassStyleTiles.find(t => toPublicImageUrl(t.path) === p || t.path === p)?.name;
              return named || inferGlassTileNameFromPath(p) || 'Patterned';
            }
            return glassTypeCode === 'obscured' ? 'Satin' : 'Clear';
          })(),
          glass_type_code: (glassStyleTilePath.trim() ? 'obscured' : glassTypeCode),
          beading_type: beadingOpt?.label ?? null,
          beading_type_code: beadingOpt?.code ?? null,
          variant: variantOpt?.label ?? null,
          variant_code: variantOpt?.code ?? null,
          frame_color: frameColourLabel ?? null,
          frame_color_code: combinedFrameCode || null,
          frame_color_external: extFrameValue ?? null,
          frame_color_code_external: effectiveFrameCodeExt ?? null,
          frame_color_internal: intFrameValue ?? null,
          frame_color_code_internal: effectiveFrameCodeInt ?? null,
          notes: stripAutoDerivedNotes(notes).trim() || null,
          options: {
            glass_style_tile: glassStyleTilePath.trim() ? glassStyleTilePath.trim() : null,
            glass_specification: glassSpecification,
            trickle_vents: trickleEnabled,
            trickle_vents_position: trickleEnabled
              ? resolveTricklePositionAfterTopExtenderRemoved(trickleVentsPosition, extTopMm)
              : null,
            handle_color: handleOpt?.label ?? null,
            handle_color_code: handleOpt?.code ?? null,
            handle_style: handleStyleOpt?.label ?? null,
            handle_style_code: handleStyleOpt?.code ?? null,
            design_dims_on: designDimsOn,
            hinge_placement: isDoor && doorKindUsesHingePlacement(selectedDoorKind) ? doorHingePlacement : null,
            open_direction: isDoor && doorKindUsesOpenDirection(selectedDoorKind) ? doorOpenDirection : null,
            master_leaf: selectedDoorKind === 'french' ? doorMasterLeaf : null,
            panel_count: effectiveDoorPanelCount,
            stack_direction: selectedDoorKind === 'bifold' ? doorStackDirection : null,
            traffic_door: selectedDoorKind === 'bifold' ? doorTrafficDoor : null,
            threshold: isDoor ? doorThreshold : null,
            window_hinge_type_code: isDoor ? null : windowHingeTypeCode,
            window_hinge_type: isDoor
              ? null
              : (WINDOW_HINGE_TYPE_OPTIONS.find(x => x.code === windowHingeTypeCode)?.label || 'Standard'),
            cill_option: cillOption,
            cill_included: derivedCillIncluded,
            cill_required: false,
            cill_type: (derivedCillType as any) ?? null,
            cill_height_mm: derivedCillHeight,
            bay_tangent_width_mm: null,
            bay_legacy_width_mm: null,
            bay_projection_depth_mm: null,
            bay_facet_widths_mm: null,
            bay_joint_angles_deg: null,
            astragal_bar_side: astragalBarSide,
            astragal_bar_side_label: (BAR_SIDE_OPTIONS.find(x => x.code === astragalBarSide)?.label || 'None'),
            joint_style: jointStyle === 'timber-weld' ? 'timber-weld' : null,
            frame_color_hex_external: extCustomHexValue ?? null,
            frame_color_hex_internal: intCustomHexValue ?? null,
          },
        },
        created_at: new Date().toISOString(),
      };
      const payloadCandidate: WindowGeneratorPayload | null = (() => {
        const fromSelected = ('payload' in (selectedStyle as any))
          ? ((selectedStyle as any).payload as WindowGeneratorPayload)
          : null;
        return (workingPayload || v2PayloadToUse || fromSelected || null) as WindowGeneratorPayload | null;
      })();
      if (payloadCandidate && payloadCandidate.root && payloadCandidate.w != null && payloadCandidate.h != null) {
        (meta.window_style as any).source = 'window_generator';
        (meta.window_style as any).templatePayload = deepCloneJson(payloadCandidate);
        const payloadPres: any = (payloadCandidate as any)?.presentation || {};
        (meta.specs.options as any).frame_color_hex_external = String(payloadPres.frameColExt || '').trim() || null;
        (meta.specs.options as any).frame_color_hex_internal = String(payloadPres.frameColInt || '').trim() || null;
      }
      if (payloadCandidate && payloadCandidate.root && payloadCandidate.w != null && payloadCandidate.h != null && isBayLikePayload(payloadCandidate) && persistBayDerived) {
        const bayDerived = deriveFromWindowGeneratorPayload(payloadCandidate);
        const enteredTangent = widthMm.trim() ? Number(widthMm) : null;
        (meta.specs.options as any).bay_tangent_width_mm = Number.isFinite(enteredTangent as number)
          ? enteredTangent
          : (bayDerived as any)?.extras?.bay_tangent_width_mm ?? null;
        (meta.specs.options as any).bay_legacy_width_mm = (bayDerived as any)?.extras?.bay_legacy_width_mm ?? null;
        (meta.specs.options as any).bay_projection_depth_mm = (bayDerived as any)?.extras?.bay_projection_depth_mm ?? null;
        (meta.specs.options as any).bay_facet_widths_mm = (bayDerived as any)?.extras?.bay_facet_widths_mm ?? null;
        (meta.specs.options as any).bay_joint_angles_deg = (bayDerived as any)?.extras?.bay_joint_angles_deg ?? null;
      }

      // v2: derive as much as possible from the Window Generator payload
      if ((designerVersion === 'v2' || designerVersion === 'v2_lite') && isLibraryStyle && 'payload' in selectedStyle) {
        const payload =
          designerVersion === 'v2_lite'
            ? (workingPayload || v2PayloadToUse || selectedStyle.payload)
            : (workingPayload || v2PayloadToUse || selectedStyle.payload);
        (meta.specs.options as any).logical_divs =
          payload && (payload as any).logicalDivs && typeof (payload as any).logicalDivs === 'object'
            ? deepCloneJson((payload as any).logicalDivs)
            : null;
        const derived = deriveFromWindowGeneratorPayload(payload);
        // Dimensions & glass
        // v2 Full: always use WG-derived values. v2 Lite: user can override (keep their modal values).
        // Final Survey: empty measure fields stay empty (drawings still use templatePayload).
        const overrideDims = designerVersion === 'v2_lite';
        const keepFinalEmptyMeasures = requireSurveyorMeasures || finalSurveyUsesEmptyMeasureFields(
          editingIdx != null ? (lines[editingIdx] as any)?.metaJson : null,
        );
        if (keepFinalEmptyMeasures && overrideDims) {
          meta.specs.width_mm = widthMm.trim() ? Number(widthMm) : null;
          meta.specs.height_mm = heightMm.trim() ? Number(heightMm) : null;
        } else {
          meta.specs.width_mm = overrideDims ? (widthMm.trim() ? Number(widthMm) : derived.width_mm) : derived.width_mm;
          meta.specs.height_mm = overrideDims ? (heightMm.trim() ? Number(heightMm) : derived.height_mm) : derived.height_mm;
        }
        if (overrideDims) {
          const p = glassStyleTilePath.trim();
          meta.specs.glass_type_code = (p ? 'obscured' : glassTypeCode) as any;
          meta.specs.glass_type = p
            ? (glassStyleTiles.find(t => toPublicImageUrl(t.path) === p || t.path === p)?.name || inferGlassTileNameFromPath(p) || 'Patterned')
            : (glassTypeCode === 'obscured' ? 'Satin' : 'Clear');
          (meta.specs.options as any).glass_style_tile = p ? p : null;
        } else {
          meta.specs.glass_type_code = derived.glass_type_code as any;
          meta.specs.glass_type = derived.glass_type;
          (meta.specs.options as any).glass_style_tile = (derived as any)?.extras?.glass_style_tile ?? null;
        }
        // Colour/finish
        if (designerVersion === 'v2_lite') {
          meta.specs.frame_color = frameColourLabel ?? derived.frame_color;
          meta.specs.frame_color_code = combinedFrameCode || null;
          (meta.specs as any).frame_color_external = extFrameValue ?? null;
          (meta.specs as any).frame_color_code_external = effectiveFrameCodeExt ?? null;
          (meta.specs as any).frame_color_internal = intFrameValue ?? null;
          (meta.specs as any).frame_color_code_internal = effectiveFrameCodeInt ?? null;
          (meta.specs.options as any).frame_color_hex_external = extCustomHexValue ?? (derived as any)?.extras?.frame_color_hex_external ?? null;
          (meta.specs.options as any).frame_color_hex_internal = intCustomHexValue ?? (derived as any)?.extras?.frame_color_hex_internal ?? null;
        } else {
          meta.specs.frame_color = derived.frame_color;
          meta.specs.frame_color_code = meta.specs.frame_color_code || null;
          (meta.specs as any).frame_color_external = derived.frame_color_external ?? null;
          (meta.specs as any).frame_color_internal = derived.frame_color_internal ?? null;
          (meta.specs.options as any).frame_color_hex_external = (derived as any)?.extras?.frame_color_hex_external ?? null;
          (meta.specs.options as any).frame_color_hex_internal = (derived as any)?.extras?.frame_color_hex_internal ?? null;
        }
        // Handles
        if (designerVersion === 'v2_lite') {
          const handleOpt2 = HANDLE_COLOUR_OPTIONS.find(x => x.code === handleColorCode) || HANDLE_COLOUR_OPTIONS[0];
          const handleStyleOpt2 = HANDLE_STYLE_OPTIONS.find(x => x.code === handleStyleCode) || HANDLE_STYLE_OPTIONS[0];
          meta.specs.options.handle_color = handleOpt2?.label ?? derived.handle_color.label;
          meta.specs.options.handle_color_code = handleOpt2?.code ?? derived.handle_color.code;
          meta.specs.options.handle_style = handleStyleOpt2?.label ?? derived.handle_style_label;
          meta.specs.options.handle_style_code = handleStyleOpt2?.code ?? null;
        } else {
          meta.specs.options.handle_color = derived.handle_color.label;
          meta.specs.options.handle_color_code = derived.handle_color.code;
          meta.specs.options.handle_style = derived.handle_style_label;
          meta.specs.options.handle_style_code = meta.specs.options.handle_style_code || null;
        }
        // Trickle vents
        if (designerVersion === 'v2_lite') {
          const enabled = trickleVents === 'yes';
          meta.specs.options.trickle_vents = enabled;
          meta.specs.options.trickle_vents_position = enabled
            ? resolveTricklePositionAfterTopExtenderRemoved(trickleVentsPosition, extTopMm)
            : null;
        } else {
          meta.specs.options.trickle_vents = derived.trickle.enabled ?? undefined;
          meta.specs.options.trickle_vents_position =
            derived.trickle.enabled === true ? derived.trickle.position : null;
        }
        // Cill
        if (designerVersion === 'v2_lite') {
          const co = cillOption as any;
          const derivedCillIncluded2 = co !== 'none';
          const derivedCillType2 = co === 'stub' ? 'Stub' : (derivedCillIncluded2 ? 'Regular' : null);
          const derivedCillHeight2 = co === '150' ? 150 : (co === '180' ? 180 : null);
          meta.specs.options.cill_option = co;
          meta.specs.options.cill_included = derivedCillIncluded2;
          meta.specs.options.cill_type = derivedCillType2 as any;
          meta.specs.options.cill_height_mm = derivedCillHeight2;
        } else {
          meta.specs.options.cill_option = derived.cill_option;
          meta.specs.options.cill_included = derived.cill_derived.included;
          meta.specs.options.cill_type = derived.cill_derived.type as any;
          meta.specs.options.cill_height_mm = derived.cill_derived.height_mm;
        }
        {
          const presAny: any = (payload as any)?.presentation || {};
          const isDoorWindowShape = String((derived as any)?.window_type_id || '') === 'door-window';
          const payloadRoot: any = (payload as any)?.root;
          const isUnionShapePayload = !!(payloadRoot && payloadRoot.type === 'rectUnion' && Array.isArray(payloadRoot.regions) && payloadRoot.regions.length > 1);
          if (isDoorWindowShape && isUnionShapePayload) {
            const rawMode = String(presAny?.cillUnionMode ?? presAny?.cill_union_mode ?? '').trim().toLowerCase();
            const includeUpper = presAny?.cillUnionIncludeUpper === true;
            const unionMode =
              rawMode === 'window_only' || rawMode === 'window-only' || rawMode === 'window only' ? 'window_only'
              : rawMode === 'both' || rawMode === 'door_window' || rawMode === 'door+window' ? 'both'
              : rawMode === 'door_only' || rawMode === 'door-only' || rawMode === 'door only' ? 'door_only'
              : (includeUpper ? 'both' : 'door_only');
            const unionModeLabel =
              unionMode === 'window_only' ? 'Window Only'
              : unionMode === 'both' ? 'Door & Window'
              : 'Door Only';
            (meta.specs.options as any).cill_union_mode = unionMode;
            (meta.specs.options as any).cill_union_include_upper = unionMode === 'both';
            (meta.specs.options as any).cill_union_mode_label = unionModeLabel;
          } else {
            (meta.specs.options as any).cill_union_mode = null;
            (meta.specs.options as any).cill_union_include_upper = null;
            (meta.specs.options as any).cill_union_mode_label = null;
          }
        }
        // Extras (extenders, Georgian bars, astragal bars, glass tile)
        if (designerVersion === 'v2_lite') {
          const toNumOrNull = (s: string) => (String(s || '').trim() ? Number(s) : null);
          const keepFinalEmptyMeasures = requireSurveyorMeasures || finalSurveyUsesEmptyMeasureFields(
            editingIdx != null ? (lines[editingIdx] as any)?.metaJson : null,
          );
          const extTopSaved =
            isExtenderTrickleVentActive(trickleVentsPosition, extTopMm)
              ? toNumOrNull(ensureExtenderTopMm(extTopMm))
              : keepFinalEmptyMeasures
                ? toNumOrNull(extTopMm)
                : toNumOrNull(extTopMm) ?? derived.extras.ext_top_mm;
          (meta.specs.options as any).ext_top_mm = extTopSaved;
          (meta.specs.options as any).ext_bottom_mm = keepFinalEmptyMeasures
            ? toNumOrNull(extBottomMm)
            : toNumOrNull(extBottomMm) ?? derived.extras.ext_bottom_mm;
          (meta.specs.options as any).ext_left_mm = keepFinalEmptyMeasures
            ? toNumOrNull(extLeftMm)
            : toNumOrNull(extLeftMm) ?? derived.extras.ext_left_mm;
          (meta.specs.options as any).ext_right_mm = keepFinalEmptyMeasures
            ? toNumOrNull(extRightMm)
            : toNumOrNull(extRightMm) ?? derived.extras.ext_right_mm;
          (meta.specs.options as any).georgian_bars = georgianOn === 'yes';
          (meta.specs.options as any).georgian_cols = toNumOrNull(georgianCols) ?? derived.extras.georgian_cols;
          (meta.specs.options as any).georgian_rows = toNumOrNull(georgianRows) ?? derived.extras.georgian_rows;
          (meta.specs.options as any).georgian_width_mm = toNumOrNull(georgianWidthMm) ?? derived.extras.georgian_width_mm;
          (meta.specs.options as any).georgian_offset_x_pct = toNumOrNull(georgianOffXPct) ?? derived.extras.georgian_offset_x_pct;
          (meta.specs.options as any).georgian_offset_y_pct = toNumOrNull(georgianOffYPct) ?? derived.extras.georgian_offset_y_pct;
          (meta.specs.options as any).astragal_bars = astragalOn === 'yes';
          (meta.specs.options as any).astragal_cols = toNumOrNull(astragalCols) ?? derived.extras.astragal_cols;
          (meta.specs.options as any).astragal_rows = toNumOrNull(astragalRows) ?? derived.extras.astragal_rows;
          (meta.specs.options as any).astragal_width_mm = toNumOrNull(astragalWidthMm) ?? derived.extras.astragal_width_mm;
          (meta.specs.options as any).astragal_offset_x_pct = toNumOrNull(astragalOffXPct) ?? derived.extras.astragal_offset_x_pct;
          (meta.specs.options as any).astragal_offset_y_pct = toNumOrNull(astragalOffYPct) ?? derived.extras.astragal_offset_y_pct;
          (meta.specs.options as any).joint_style = jointStyle === 'timber-weld' ? 'timber-weld' : null;
          // Glass style is driven by the Glass type selector; persist only the chosen tile (if any).
          (meta.specs.options as any).glass_style_tile = glassStyleTilePath.trim() ? glassStyleTilePath.trim() : null;
          if (persistBayDerived && isBayLikePayload(payload)) {
            const enteredTangent = widthMm.trim() ? Number(widthMm) : null;
            (meta.specs.options as any).bay_tangent_width_mm = Number.isFinite(enteredTangent as number)
              ? enteredTangent
              : (derived as any).extras?.bay_tangent_width_mm ?? null;
            (meta.specs.options as any).bay_legacy_width_mm = (derived as any).extras?.bay_legacy_width_mm ?? null;
            (meta.specs.options as any).bay_projection_depth_mm = (derived as any).extras?.bay_projection_depth_mm ?? null;
            (meta.specs.options as any).bay_facet_widths_mm = (derived as any).extras?.bay_facet_widths_mm ?? null;
            (meta.specs.options as any).bay_joint_angles_deg = (derived as any).extras?.bay_joint_angles_deg ?? null;
          }
        } else {
          Object.assign(meta.specs.options as any, derived.extras);
        }

        // Append auto-derived notes (keeps any user notes too)
        const autoParts: string[] = [];
        const o: any = meta.specs.options || {};
        if (o.georgian_bars === true) {
          const grid = (o.georgian_cols && o.georgian_rows) ? `${o.georgian_cols}×${o.georgian_rows}` : '';
          autoParts.push(`Georgian bars ${[grid, o.georgian_width_mm ? `${o.georgian_width_mm}mm` : ''].filter(Boolean).join(' ')}`.trim());
        }
        if (o.astragal_bars === true) {
          const grid = (o.astragal_cols && o.astragal_rows) ? `${o.astragal_cols}×${o.astragal_rows}` : '';
          autoParts.push(`Astragal bars ${[grid, o.astragal_width_mm ? `${o.astragal_width_mm}mm` : ''].filter(Boolean).join(' ')}`.trim());
        }
        if (String(o.joint_style || '').trim().toLowerCase() === 'timber-weld') {
          autoParts.push('Corner joint: Timber Weld');
        }
        const userNotes = stripAutoDerivedNotes(meta.specs.notes || '').trim();
        const autoNotes = autoParts.filter(Boolean).join(' • ').trim();
        meta.specs.notes = [userNotes, autoNotes].filter(Boolean).join(userNotes && autoNotes ? '\n' : '') || null;
      }

      // Preserve First Survey reference across Final Survey edits.
      const existingSurveyMeta = editingIdx != null
        ? (lines[editingIdx] as any)?.metaJson?.survey
        : null;
      if (existingSurveyMeta && typeof existingSurveyMeta === 'object') {
        const preservedRef = getFirstSurveyReference({ survey: existingSurveyMeta });
        (meta as any).survey = {
          source: String(existingSurveyMeta.source || 'final'),
          first_survey_reference: preservedRef
            ? {
                width_mm: preservedRef.width_mm ?? null,
                height_mm: preservedRef.height_mm ?? null,
                specs: preservedRef.specs ? JSON.parse(JSON.stringify(preservedRef.specs)) : undefined,
              }
            : existingSurveyMeta.first_survey_reference ?? null,
        };
      } else if (requireSurveyorMeasures) {
        (meta as any).survey = { source: 'final', first_survey_reference: null };
      }

      const line = buildVisualDesignerLine(selectedStyle, meta, Number.isFinite(q as any) && q > 0 ? q : 1);

      if (editingIdx != null) {
        commitLines(prev => prev.map((l, i) => (i === editingIdx ? line : l)));
      } else {
        commitLines(prev => [...prev, line].map((l, i) => ({ ...l, sortOrder: i })));
      }

      if (mode === 'add_another') {
        resetModalState();
        setOpen(true);
        return;
      }

      setOpen(false);
      resetModalState();
    } catch (e) {
      console.error('Failed to save Visual Designer item', e);
      alert('Failed to save Visual Designer item. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const shellSx = hideShell ? { p: 0, mb: 0, boxShadow: 'none', bgcolor: 'transparent', border: 'none' } : { p: 2, mb: 2 };

  return (
    <>
      <Card sx={shellSx}>
        {!hideShell && (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1, gap: 1 }}>
          <Typography level="title-md">Visual Designer (Quote Mode)</Typography>
            <RadioGroup
            orientation="horizontal"
            value={enabled ? 'v2_lite' : 'off'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleChange(e.target.value as 'off' | 'v2_lite')}
            sx={{ gap: 2 }}
          >
            <Radio value="off" label="Visual Designer: Off" disabled={readOnly} />
            <Radio value="v2_lite" label="Visual Designer" disabled={readOnly} />
          </RadioGroup>
        </Stack>
        )}

        {!(hideShell || enabled) ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            If enabled, you can attach window visuals/specs to this quote without affecting pricing totals.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {!hideShell && (
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              Visual Designer uses Window Generator designs, with per-quote overrides (no new library styles created).
            </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button size="sm" onClick={openNew} disabled={readOnly}>Add Window Design</Button>
              {visualDesignerLines.length > 0 && (
                <Button size="sm" variant="outlined" color="danger" disabled={readOnly} onClick={() => {
                  const ok = window.confirm('Remove all Visual Designer items from this quote?');
                  if (!ok) return;
                  removeAll();
                }}>
                  Remove All
                </Button>
              )}
              <Typography level="body-sm" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
                {visualDesignerLines.length} item{visualDesignerLines.length === 1 ? '' : 's'}
              </Typography>
            </Stack>

            {/* Bulk edit bar (for editing existing items) */}
            {visualDesignerLines.length > 0 && (
              <Box
                ref={bulkBarRef}
                component="fieldset"
                disabled={readOnly}
                sx={{
                  border: 0,
                  m: 0,
                  p: 0,
                  minInlineSize: 0,
                  ...(readOnly ? { opacity: 0.55, pointerEvents: 'none', filter: 'grayscale(0.18)' } : {}),
                }}
              >
              <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'sm' }}>
                <Stack spacing={1}>
                  <Typography level="body-sm" fontWeight="lg">Bulk change (applies to all Visual Designer items)</Typography>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <FormControl size="sm" sx={{ minWidth: 190 }}>
                      <FormLabel>Window type</FormLabel>
                      <Select
                        value={bulkWindowTypeId || null}
                        onChange={(_, v) => setBulkWindowTypeId(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        {QUOTE_WINDOW_TYPES.map(o => (
                          <Option key={o.id} value={o.id}>{o.name}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                      <FormLabel>Material</FormLabel>
                      <Select
                        value={bulkMaterialId || null}
                        onChange={(_, v) => setBulkMaterialId(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        {MATERIAL_OPTIONS.map(o => (
                          <Option key={o.id} value={o.id}>{o.name}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 190 }}>
                      <FormLabel>Style family</FormLabel>
                      <Select
                        value={bulkStyleFamilyCategory || null}
                        onChange={(_, v) => setBulkStyleFamilyCategory(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        {STYLE_FAMILY_OPTIONS.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 150 }}>
                      <FormLabel>Glass type</FormLabel>
                      <Select
                        value={bulkGlassTypeCode || null}
                        onChange={(_, v) => setBulkGlassTypeCode(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value="clear">Clear</Option>
                        <Option value="satin">Satin</Option>
                        {glassStyleTiles.map((t) => {
                          const p = toPublicImageUrl(t.path);
                          return (
                            <Option key={`tile-${p}`} value={`tile:${p}` as any}>
                              {t.name}
                            </Option>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 180 }}>
                      <FormLabel>Profile Type</FormLabel>
                      <Select
                        value={bulkProfileTypeCode || null}
                        onChange={(_, v) => setBulkProfileTypeCode(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option
                          disabled
                          value="__profile-group-none-bulk"
                          sx={{ fontWeight: 'lg', opacity: '1 !important', color: '#000 !important' }}
                        >
                          None
                        </Option>
                        <Option value={BULK_NONE}>None</Option>
                        {PROFILE_VARIANT_GROUPS
                          .filter((g) => g.options.length > 0)
                          .map((group) => (
                            <React.Fragment key={`bulk-profile-group-${group.label}`}>
                              <Option
                                disabled
                                value={`__profile-group-bulk-${group.label}`}
                                sx={{ fontWeight: 'lg', opacity: '1 !important', color: '#000 !important' }}
                              >
                                {group.label}
                              </Option>
                              {group.options.map((o) => (
                                <Option key={o.code} value={o.code}>{o.label}</Option>
                              ))}
                            </React.Fragment>
                          ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 190 }}>
                      <FormLabel>Beading type</FormLabel>
                      <Select
                        value={bulkBeadingTypeCode || null}
                        onChange={(_, v) => setBulkBeadingTypeCode(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value={BULK_NONE}>None</Option>
                        {BEADING_TYPE_OPTIONS.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 220 }}>
                      <FormLabel>External colour</FormLabel>
                      <Select
                        value={bulkFrameColorExtCode || null}
                        onChange={(_, v) => {
                          const next = String(v || '');
                          setBulkFrameColorExtCode(next);
                          setShowBulkRalFieldValidation(false);
                          if (next !== CUSTOM_FRAME_COLOUR_CODE) {
                            setBulkFrameColorExtCustomRal('');
                            setBulkFrameColorExtCustomHex('');
                          }
                        }}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        {FRAME_SIDE_COLOUR_OPTIONS_WITH_CUSTOM.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>
                    {bulkFrameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && (
                      <>
                        <FormControl size="sm" sx={{ minWidth: 170 }}>
                          <FormLabel>External RAL (custom)</FormLabel>
                          <Input
                            size="sm"
                            error={showBulkExtCustomRalError}
                            value={bulkFrameColorExtCustomRal}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setBulkFrameColorExtCustomRal(e.target.value);
                            }}
                            placeholder="e.g. RAL7006" disabled={readOnly}
                          />
                          {showBulkExtCustomRalError && <FormHelperText>RAL value is required.</FormHelperText>}
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 170 }}>
                          <FormLabel>External HEX (custom)</FormLabel>
                          <Input
                            size="sm"
                            error={showBulkExtCustomHexError}
                            value={bulkFrameColorExtCustomHex}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setBulkFrameColorExtCustomHex(normalizeHexInput(e.target.value));
                            }}
                            placeholder="e.g. #6D6552" disabled={readOnly}
                          />
                          {showBulkExtCustomHexError && <FormHelperText>Valid HEX value is required.</FormHelperText>}
                        </FormControl>
                      </>
                    )}

                    <FormControl size="sm" sx={{ minWidth: 220 }}>
                      <FormLabel>Internal colour</FormLabel>
                      <Select
                        value={bulkFrameColorIntCode || null}
                        onChange={(_, v) => {
                          const next = String(v || '');
                          setBulkFrameColorIntCode(next);
                          setShowBulkRalFieldValidation(false);
                          if (next !== CUSTOM_FRAME_COLOUR_CODE) {
                            setBulkFrameColorIntCustomRal('');
                            setBulkFrameColorIntCustomHex('');
                          }
                        }}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        {FRAME_SIDE_COLOUR_OPTIONS_WITH_CUSTOM.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>
                    {bulkFrameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && (
                      <>
                        <FormControl size="sm" sx={{ minWidth: 170 }}>
                          <FormLabel>Internal RAL (custom)</FormLabel>
                          <Input
                            size="sm"
                            error={showBulkIntCustomRalError}
                            value={bulkFrameColorIntCustomRal}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setBulkFrameColorIntCustomRal(e.target.value);
                            }}
                            placeholder="e.g. RAL7006" disabled={readOnly}
                          />
                          {showBulkIntCustomRalError && <FormHelperText>RAL value is required.</FormHelperText>}
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 170 }}>
                          <FormLabel>Internal HEX (custom)</FormLabel>
                          <Input
                            size="sm"
                            error={showBulkIntCustomHexError}
                            value={bulkFrameColorIntCustomHex}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setBulkFrameColorIntCustomHex(normalizeHexInput(e.target.value));
                            }}
                            placeholder="e.g. #6D6552" disabled={readOnly}
                          />
                          {showBulkIntCustomHexError && <FormHelperText>Valid HEX value is required.</FormHelperText>}
                        </FormControl>
                      </>
                    )}

                    <FormControl size="sm" sx={{ minWidth: 170 }}>
                      <FormLabel>Handle colour</FormLabel>
                      <Select
                        value={bulkHandleColorCode || null}
                        onChange={(_, v) => setBulkHandleColorCode(String(v || ''))}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value={BULK_NONE}>None</Option>
                        {HANDLE_COLOUR_OPTIONS.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 170 }}>
                      <FormLabel>Handle style</FormLabel>
                      <Select
                        value={bulkHandleStyleCode || null}
                        onChange={(_, v) => setBulkHandleStyleCode(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value={BULK_NONE}>None</Option>
                          {HANDLE_STYLE_OPTIONS.map(o => (
                          <Option key={o.code} value={o.code}>{o.label}</Option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 140 }}>
                      <FormLabel>Trickle vents</FormLabel>
                      <Select
                        value={bulkTrickleVents || null}
                        onChange={(_, v) => setBulkTrickleVents(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value="yes">Yes</Option>
                        <Option value="no">No</Option>
                      </Select>
                    </FormControl>

                    {designerVersion === 'v2_lite' && (
                      <FormControl size="sm" sx={{ minWidth: 190 }}>
                        <FormLabel>Window hinge type</FormLabel>
                        <Select
                          value={bulkWindowHingeTypeCode || null}
                          onChange={(_, v) => setBulkWindowHingeTypeCode(((v as any) || '') as any)}
                          placeholder="No change" disabled={readOnly}
                        >
                          <Option value="">No change</Option>
                          {WINDOW_HINGE_TYPE_OPTIONS.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {designerVersion === 'v2_lite' && (
                      <FormControl size="sm" sx={{ minWidth: 170 }}>
                        <FormLabel>Corner joint</FormLabel>
                        <Select
                          value={bulkJointStyle || null}
                          onChange={(_, v) => setBulkJointStyle(((v as any) || '') as any)}
                          placeholder="No change" disabled={readOnly}
                        >
                          <Option value="">No change</Option>
                          <Option value="mitre-45">45° Mitre</Option>
                          <Option value="timber-weld">Timber Weld</Option>
                        </Select>
                      </FormControl>
                    )}

                    {designerVersion === 'v2_lite' && (
                      <FormControl size="sm" sx={{ minWidth: 190 }}>
                        <FormLabel>Astragal bar side</FormLabel>
                        <Select
                          value={bulkAstragalBarSide || null}
                          onChange={(_, v) => setBulkAstragalBarSide(((v as any) || '') as any)}
                          placeholder="No change" disabled={readOnly}
                        >
                          <Option value="">No change</Option>
                          <Option value={BULK_NONE}>None</Option>
                          {BAR_SIDE_OPTIONS.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {designerVersion === 'v2_lite' && (
                      <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>Georgian bars</FormLabel>
                        <Select
                          value={bulkGeorgianBars || null}
                          onChange={(_, v) => setBulkGeorgianBars(((v as any) || '') as any)}
                          placeholder="No change" disabled={readOnly}
                        >
                          <Option value="">No change</Option>
                          <Option value={BULK_NONE}>None</Option>
                        </Select>
                      </FormControl>
                    )}

                    {designerVersion === 'v2_lite' && (
                      <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>Astragal bars</FormLabel>
                        <Select
                          value={bulkAstragalBars || null}
                          onChange={(_, v) => setBulkAstragalBars(((v as any) || '') as any)}
                          placeholder="No change" disabled={readOnly}
                        >
                          <Option value="">No change</Option>
                          <Option value={BULK_NONE}>None</Option>
                        </Select>
                      </FormControl>
                    )}

                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                      <FormLabel>Trickle vents position</FormLabel>
                      <Select
                        value={bulkTrickleVentsPosition || null}
                        onChange={(_, v) => setBulkTrickleVentsPosition(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value="none">No trickle vents</Option>
                        <Option value="sash">Head of Sash</Option>
                        <Option value="head">Head Jamb</Option>
                        <Option value="extender">Extender</Option>
                        <Option value="custom">Custom</Option>
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 140 }}>
                      <FormLabel>Cill</FormLabel>
                      <Select
                        value={bulkCillOption || null}
                        onChange={(_, v) => setBulkCillOption(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value="none">None</Option>
                        <Option value="150">150mm</Option>
                        <Option value="180">180mm</Option>
                        <Option value="stub">Stub</Option>
                      </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 170 }}>
                      <FormLabel>Design dimensions</FormLabel>
                      <Select
                        value={bulkDesignDimsOn || null}
                        onChange={(_, v) => setBulkDesignDimsOn(((v as any) || '') as any)}
                        placeholder="No change" disabled={readOnly}
                      >
                        <Option value="">No change</Option>
                        <Option value="yes">On</Option>
                        <Option value="no">Off</Option>
                      </Select>
                    </FormControl>

                    {designerVersion === 'v2_lite' && (
                      <>
                        <FormControl size="sm" sx={{ minWidth: 200 }}>
                          <FormLabel>Window hinge type</FormLabel>
                          <Select
                            value={bulkWindowHingeTypeCode || null}
                            onChange={(_, v) => setBulkWindowHingeTypeCode(((v as any) || '') as any)}
                            placeholder="No change" disabled={readOnly}
                          >
                            <Option value="">No change</Option>
                            {WINDOW_HINGE_TYPE_OPTIONS.map(o => (
                              <Option key={o.code} value={o.code}>{o.label}</Option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: 190 }}>
                          <FormLabel>Astragal bar side</FormLabel>
                          <Select
                            value={bulkAstragalBarSide || null}
                            onChange={(_, v) => setBulkAstragalBarSide(((v as any) || '') as any)}
                            placeholder="No change" disabled={readOnly}
                          >
                            <Option value="">No change</Option>
                            <Option value={BULK_NONE}>None</Option>
                            {BAR_SIDE_OPTIONS.map(o => (
                              <Option key={o.code} value={o.code}>{o.label}</Option>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    )}

                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => { void applyBulkChanges(); }}
                      disabled={
                        readOnly ||
                        bulkApplyStatus === 'running' ||
                        (!bulkWindowTypeId &&
                        !bulkMaterialId &&
                        !bulkStyleFamilyCategory &&
                        !bulkGlassTypeCode &&
                        !bulkProfileTypeCode &&
                        !bulkBeadingTypeCode &&
                        !bulkFrameColorExtCode &&
                        !bulkFrameColorIntCode &&
                        !bulkFrameColorExtCustomRal &&
                        !bulkFrameColorExtCustomHex &&
                        !bulkFrameColorIntCustomRal &&
                        !bulkFrameColorIntCustomHex &&
                        !bulkHandleColorCode &&
                        !bulkHandleStyleCode &&
                        !bulkTrickleVents &&
                        !bulkTrickleVentsPosition &&
                        !bulkCillOption &&
                        !bulkDesignDimsOn &&
                        !bulkWindowHingeTypeCode &&
                        !bulkJointStyle &&
                        !bulkAstragalBarSide &&
                        !bulkGeorgianBars &&
                        !bulkAstragalBars)
                      }
                    >
                      {bulkApplyStatus === 'running'
                        ? (bulkApplyProgress.phase === 'uploading' ? 'Generating previews…' : `Applying (${bulkApplyProgress.completed}/${bulkApplyProgress.total})…`)
                        : 'Apply'}
                    </Button>
                    {bulkApplyStatus === 'running' && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CircularProgress size="sm" />
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {bulkApplyProgress.phase === 'uploading'
                            ? 'Generating preview images…'
                            : `Updating line ${Math.min(bulkApplyProgress.completed + 1, bulkApplyProgress.total)} of ${bulkApplyProgress.total}`}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  {bulkApplyErrors.length > 0 && bulkApplyStatus !== 'running' && (
                    <Alert
                      color={bulkApplyStatus === 'failed' ? 'danger' : 'warning'}
                      variant="soft"
                      sx={{ mt: 0.5 }}
                    >
                      <Stack spacing={0.5}>
                        {bulkApplyStatus === 'failed' && bulkApplyErrors.length === 1 && bulkApplyErrors[0].lineIndex === -1 ? (
                          <Typography level="body-sm">{bulkApplyErrors[0].message}</Typography>
                        ) : (
                          <>
                            <Typography level="body-sm">
                              {bulkApplyStatus === 'failed'
                                ? 'Bulk edit failed for all selected lines.'
                                : `Some lines could not be updated (${bulkApplyErrors.length} failed).`}
                            </Typography>
                            {bulkApplyErrors.filter((e) => e.lineIndex >= 0).map((e) => (
                              <Typography key={`${e.lineIndex}-${e.reference}`} level="body-xs">
                                {e.reference}: {e.message}
                              </Typography>
                            ))}
                          </>
                        )}
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              </Sheet>
              </Box>
            )}

            {visualDesignerLines.length === 0 ? (
              <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                <Typography level="body-sm">No Visual Designer items yet. Click “Add Window Design”.</Typography>
              </Sheet>
            ) : (
              <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 0 }}>
                {visualDesignerLines.map(({ l, idx }) => {
                  const meta = (l as any).metaJson as VisualDesignerMetaV1;
                  const styleAny: any = (meta as any)?.window_style || {};
                  const templatePayload =
                    (styleAny?.templatePayload as WindowGeneratorPayload | undefined) ||
                    (styleAny?.template_payload as WindowGeneratorPayload | undefined) ||
                    (styleAny?.payload as WindowGeneratorPayload | undefined) ||
                    (libraryTemplates.find((t: any) => String(t?.id) === String(styleAny?.id))?.payload as WindowGeneratorPayload | undefined);
                  const imgExternal = toPublicImageUrl(meta?.window_style?.preview_path_external || meta?.window_style?.preview_path);
                  const imgInternal = toPublicImageUrl(meta?.window_style?.preview_path_internal || '');
                  const imgPresExternal = toPublicImageUrl(meta?.window_style?.preview_path_presentation_external || '');
                  const imgPresInternal = toPublicImageUrl(meta?.window_style?.preview_path_presentation_internal || '');
                  const imgDesignExternal = toPublicImageUrl(meta?.window_style?.preview_path_design_external || '');
                  const imgDesignInternal = toPublicImageUrl(meta?.window_style?.preview_path_design_internal || '');
                  const isUploadedStyleMeta = String((meta as any)?.window_style?.source || '').trim().toLowerCase() === 'upload';
                  const uploadedImageSlotsForCard = [
                    { label: 'Presentation · Outside', url: imgPresExternal || toPublicImageUrl(meta?.window_style?.preview_path_external || '') },
                    { label: 'Presentation · Inside', url: imgPresInternal || toPublicImageUrl(meta?.window_style?.preview_path_internal || '') },
                    { label: 'Design · Outside', url: imgDesignExternal },
                    { label: 'Design · Inside', url: imgDesignInternal },
                  ].filter((slot) => !!String(slot.url || '').trim());

                  const livePayloadForCard = resolveQuoteLineCardLivePayload(meta, libraryTemplates);
                  const bayPayloadForCard = livePayloadForCard && isBayLikePayload(livePayloadForCard) ? livePayloadForCard : null;
                  const isBayPayloadForCard = !!bayPayloadForCard;

                  return (
                    <Grid key={idx} xs={12}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                          <Box sx={{ width: { xs: '100%', md: 520 }, maxWidth: '100%', flexShrink: 0, '& svg': { width: '100%', height: 'auto', display: 'block' }, '& canvas': { width: '100%', height: 'auto', display: 'block' } }}>
                            {livePayloadForCard ? (
                              <Grid container spacing={2} sx={{ width: '100%' }}>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'wg', payload: livePayloadForCard as WindowGeneratorPayload, view: 'external', mode: 'presentation', title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Presentation · Outside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <WindowGeneratorSvg payload={livePayloadForCard} widthPx={420} view="external" mode="presentation" />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Presentation · Outside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'wg', payload: livePayloadForCard as WindowGeneratorPayload, view: 'internal', mode: 'presentation', title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Presentation · Inside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <WindowGeneratorSvg payload={livePayloadForCard} widthPx={420} view="internal" mode="presentation" />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Presentation · Inside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'wg', payload: livePayloadForCard as WindowGeneratorPayload, view: 'external', mode: 'design', title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Design · Outside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <WindowGeneratorSvg payload={livePayloadForCard} widthPx={420} view="external" mode="design" />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Design · Outside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'wg', payload: livePayloadForCard as WindowGeneratorPayload, view: 'internal', mode: 'design', title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Design · Inside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <WindowGeneratorSvg payload={livePayloadForCard} widthPx={420} view="internal" mode="design" />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Design · Inside</Typography>
                                  </Stack>
                                </Grid>
                                {isBayPayloadForCard && (
                                  <Grid xs={12}>
                                    <Stack spacing={0.35}>
                                      <Box
                                        sx={{
                                          borderRadius: 8,
                                          overflow: 'hidden',
                                          bgcolor: '#d9dee7',
                                          aspectRatio: '10 / 7',
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          '& svg': { width: '100%', height: '100%', display: 'block' },
                                        }}
                                      >
                                        <WindowGeneratorBaySchematicSvg payload={bayPayloadForCard as WindowGeneratorPayload} widthPx={420} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Bay Schematic · Outside</Typography>
                                    </Stack>
                                  </Grid>
                                )}
                              </Grid>
                            ) : isUploadedStyleMeta && uploadedImageSlotsForCard.length > 0 ? (
                              <Grid container spacing={2} sx={{ width: '100%' }}>
                                {uploadedImageSlotsForCard.map((slot) => (
                                  <Grid key={slot.label} xs={12} sm={6}>
                                    <Stack spacing={0.35}>
                                      <Box
                                        onClick={() => setZoomPreview({ kind: 'img', url: slot.url, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · ${slot.label}` })}
                                        sx={{
                                          cursor: 'zoom-in',
                                          borderRadius: 8,
                                          overflow: 'hidden',
                                          bgcolor: '#d9dee7',
                                          aspectRatio: '8 / 7',
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                        }}
                                      >
                                        <ImageCanvasViewer imageUrl={slot.url} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>{slot.label}</Typography>
                                    </Stack>
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (imgPresExternal && imgPresInternal && imgDesignExternal && imgDesignInternal) ? (
                              <Grid container spacing={2} sx={{ width: '100%' }}>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgPresExternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Presentation · Outside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgPresExternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Presentation · Outside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgPresInternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Presentation · Inside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgPresInternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Presentation · Inside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgDesignExternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Design · Outside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgDesignExternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Design · Outside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgDesignInternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Design · Inside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgDesignInternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Design · Inside</Typography>
                                  </Stack>
                                </Grid>
                                {isBayPayloadForCard && bayPayloadForCard && (
                                  <Grid xs={12}>
                                    <Stack spacing={0.35}>
                                      <Box
                                        sx={{
                                          borderRadius: 8,
                                          overflow: 'hidden',
                                          bgcolor: '#d9dee7',
                                          aspectRatio: '10 / 7',
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          '& svg': { width: '100%', height: '100%', display: 'block' },
                                        }}
                                      >
                                        <WindowGeneratorBaySchematicSvg payload={bayPayloadForCard as WindowGeneratorPayload} widthPx={420} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Bay Schematic · Outside</Typography>
                                    </Stack>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (imgExternal && imgInternal ? (
                              <Grid container spacing={1}>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgExternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Outside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgExternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Viewed from outside</Typography>
                                  </Stack>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                  <Stack spacing={0.35}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: imgInternal, title: `${meta?.specs?.reference || meta?.window_style?.name || l.name} · Inside` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: 'auto', maxHeight: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={imgInternal} overallWidthMm={null} overallHeightMm={null} heightPx={210} useCrossOrigin={false} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>Viewed from inside</Typography>
                                  </Stack>
                                </Grid>
                              </Grid>
                            ) : templatePayload ? (
                              <WindowGeneratorThumbnail payload={templatePayload} heightPx={380} />
                            ) : (
                              <ImageCanvasViewer imageUrl={imgExternal} overallWidthMm={null} overallHeightMm={null} heightPx={380} useCrossOrigin={false} />
                            ))}
                          </Box>
                          <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ width: '100%' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                                <Typography level="title-sm">{meta?.specs?.reference || meta?.window_style?.name || l.name}</Typography>
                                {meta?.window_type?.name && (
                                  <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>{meta.window_type.name}</Chip>
                                )}
                                {formatProfileDefinition(meta?.window_style?.category) && (
                                  <Chip size="sm" variant="soft" sx={{ whiteSpace: 'nowrap' }}>{formatProfileDefinition(meta?.window_style?.category)}</Chip>
                                )}
                                {meta?.window_material?.name && (
                                  <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>{meta.window_material.name}</Chip>
                                )}
                                <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>Qty: {l.qty ?? 1}</Chip>
                              </Stack>
                              <Stack direction="column" spacing={0} sx={{ flexShrink: 0, mt: -0.25 }}>
                                <IconButton
                                  size="sm"
                                  variant="outlined"
                                  aria-label="Move line up in document"
                                  disabled={readOnly || idx <= 0}
                                  onClick={() => moveDocumentLine(idx, -1)}
                                >
                                  <IconWrapper icon={FaArrowUp} />
                                </IconButton>
                                <IconButton
                                  size="sm"
                                  variant="outlined"
                                  aria-label="Move line down in document"
                                  disabled={readOnly || idx >= lines.length - 1}
                                  onClick={() => moveDocumentLine(idx, 1)}
                                >
                                  <IconWrapper icon={FaArrowDown} />
                                </IconButton>
                              </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mt: 0.75 }}>
                              <Button size="sm" variant="outlined" disabled={readOnly} onClick={() => openEdit(idx)}>Edit</Button>
                              <Button size="sm" variant="outlined" disabled={readOnly} onClick={() => openClone(idx)}>Clone</Button>
                              {documentId && (
                                <Button size="sm" variant="outlined" disabled={readOnly} onClick={() => openCopyModal(idx)}>Copy to quote</Button>
                              )}
                              <Button
                                size="sm"
                                variant="plain"
                                color="danger"
                                disabled={readOnly}
                                onClick={() => {
                                  const ok = window.confirm('Delete this Visual Designer item?');
                                  if (!ok) return;
                                  commitLines(prev => prev.filter((_, i) => i !== idx).map((line, i) => ({ ...line, sortOrder: i })));
                                }}
                              >
                                Delete
                              </Button>
                            </Stack>
                            <Box sx={{ mt: 0.5 }}>
                              <Table
                                size="sm"
                                variant="plain"
                                style={{ '--TableCell-paddingY': '3px', '--TableCell-paddingX': '0px' } as any}
                              >
                                <tbody>
                                  {getDesignerSummaryRows(meta).map((r) => (
                                    <tr key={r.label}>
                                      <td style={{ width: 210, minWidth: 210, color: 'rgba(0,0,0,0.65)', fontSize: 12, fontWeight: 700, paddingRight: 22, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        {r.label}
                                      </td>
                                      <td style={{ color: 'rgba(0,0,0,0.85)', fontSize: 12, verticalAlign: 'top' }}>
                                        <Typography level="body-sm" sx={{ ml: 2.5, color: 'rgba(0,0,0,0.85)', fontSize: 12 }}>
                                          {r.value}
                                        </Typography>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Box>
                          </Stack>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Stack>
        )}
      </Card>

      <Modal
        open={copyModalOpen}
        onClose={() => {
          if (copySubmitting) return;
          setCopyModalOpen(false);
          setCopySourceIdx(null);
          setCopyMode(documentId ? 'same_version' : (customerId ? 'related_customer' : 'any_existing'));
          setCopyError(null);
          setCopySuccess(null);
          setCopyTargetDocumentId('');
          setCopyTargetVersionId('');
          setCopyTargets([]);
          setCopyTargetVersions([]);
          setCopyAllFenestrationItems(false);
        }}
      >
        <ModalDialog
          sx={{
            width: { xs: '96vw', sm: 620 },
            maxWidth: 'calc(100vw - 24px)',
            overflow: 'hidden',
          }}
        >
          <DialogTitle>Copy Visual Designer item</DialogTitle>
          <DialogContent sx={{ overflowX: 'hidden' }}>
            <Stack spacing={1.25}>
              {copyError && <Alert color="danger">{copyError}</Alert>}
              {copySuccess && <Alert color="success">{copySuccess}</Alert>}
              <FormControl>
                <FormLabel>Copy mode</FormLabel>
                <RadioGroup
                  value={copyMode}
                  onChange={(event) => {
                    setCopyMode(String((event.target as HTMLInputElement).value) as any);
                    setCopyError(null);
                    setCopySuccess(null);
                    setCopyTargetDocumentId('');
                    setCopyTargetVersionId('');
                    setCopyTargetVersions([]);
                  }}
                >
                  <Radio
                    value="same_version"
                    disabled={!copySameQuoteModeEnabled}
                    label="1. Copy to another version of this quote/estimate (version must exist)"
                  />
                  <Radio value="related_customer" label="2. Copy to related customer quote/estimate (based on Customer ID)" />
                  <Radio value="any_existing" label="3. Copy to any existing quote/estimate version" />
                </RadioGroup>
              </FormControl>

              {(copyMode === 'related_customer' || copyMode === 'any_existing' || copyMode === 'same_version') && (
                <FormControl>
                  <FormLabel>Target quote / estimate</FormLabel>
                  <Select
                    value={copyTargetDocumentId || null}
                    onChange={(_, value) => setCopyTargetDocumentId(String(value || ''))}
                    disabled={copyLoadingTargets || copySubmitting || copyTargets.length === 0}
                    placeholder={copyLoadingTargets ? 'Loading...' : 'Select target document'}
                  >
                    {copyTargets.map((target) => (
                      <Option key={target.id} value={target.id}>
                        {target.label}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Target version</FormLabel>
                <Select
                  value={copyTargetVersionId || null}
                  onChange={(_, value) => setCopyTargetVersionId(String(value || ''))}
                  disabled={copyLoadingVersions || copySubmitting || copyTargetVersions.length === 0}
                  placeholder={copyLoadingVersions ? 'Loading versions...' : 'Select target version'}
                >
                  {copyTargetVersions.map((v) => (
                    <Option key={v.id} value={v.id}>
                      {v.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                This copies the selected fenestration item into the chosen destination version.
              </Typography>
              <Checkbox
                checked={copyAllFenestrationItems}
                onChange={(event) => setCopyAllFenestrationItems(event.target.checked)}
                disabled={copySubmitting}
                label="Copy all fenestration items?"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setCopyModalOpen(false);
                setCopySourceIdx(null);
                setCopyMode(documentId ? 'same_version' : (customerId ? 'related_customer' : 'any_existing'));
                setCopyError(null);
                setCopySuccess(null);
                setCopyTargetDocumentId('');
                setCopyTargetVersionId('');
                setCopyTargets([]);
                setCopyTargetVersions([]);
                setCopyAllFenestrationItems(false);
              }}
              disabled={copySubmitting}
            >
              Close
            </Button>
            <Button
              onClick={submitCopyToQuote}
              loading={copySubmitting}
              disabled={copySubmitting || copyLoadingTargets || copyLoadingVersions || copyTargets.length === 0 || copyTargetVersions.length === 0}
            >
              Copy item
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!zoomPreview} onClose={() => setZoomPreview(null)}>
        <ModalDialog layout="center" sx={{ p: 2, maxWidth: '95vw', maxHeight: '95vh' }}>
          <DialogTitle sx={{ pr: 5 }}>{zoomPreview?.title || 'Preview'}</DialogTitle>
          <DialogContent sx={{ overflow: 'auto' }}>
            {zoomPreview?.kind === 'wg' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <WindowGeneratorSvg
                  payload={zoomPreview.payload}
                  widthPx={860}
                  view={zoomPreview.view}
                  mode={zoomPreview.mode}
                />
              </Box>
            ) : zoomPreview?.kind === 'img' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img src={zoomPreview.url} alt={zoomPreview.title} style={{ maxWidth: '92vw', maxHeight: '80vh', width: 'auto', height: 'auto' }} />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setZoomPreview(null)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={open} onClose={() => { setOpen(false); resetModalState(); }}>
        <ModalDialog
          sx={{
            maxWidth: 980,
            width: { xs: '100vw', sm: '95vw' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            m: { xs: 0, sm: 'auto' },
            borderRadius: { xs: 0, sm: 'md' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DialogTitle>
            {editingIdx != null ? 'Edit Visual Designer Item' : 'Add Visual Designer Item'}
          </DialogTitle>
          <DialogContent
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 1, sm: 2 },
              // Hide scrollbars (keep scroll capability)
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Stack spacing={{ xs: 1.25, sm: 2 }}>
              <Sheet variant="soft" sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 'sm' }}>
                <Typography level="title-sm">Step {step} of 4</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Step 1 selects window type. Step 2 selects material. Step 3 selects a style from the image bank. Step 4 captures lightweight quote-mode specs.
                </Typography>
              </Sheet>

              {step === 1 && (
                <Stack spacing={1.25}>
                  <Typography level="h4">Select Window Type</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Tip: tap a card to continue.
                  </Typography>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {QUOTE_WINDOW_TYPES.map((type) => {
                      const isSelected = selectedTypeId === type.id;
                      return (
                        <Grid key={type.id} xs={6} sm={3}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': { borderColor: 'primary.500', boxShadow: 'sm' },
                              border: isSelected ? '2px solid' : '1px solid',
                              borderColor: isSelected ? 'primary.500' : 'neutral.200',
                              overflow: 'hidden',
                              backgroundColor: 'white',
                              p: 0,
                            }}
                            onClick={() => {
                              setSelectedTypeId(type.id);
                              // Changing type should reset downstream selections
                              setDoorMasterLeaf(DEFAULT_MASTER_LEAF);
                              setDoorPanelCount(type.id === SLIDING_DOORS_TYPE_ID ? SLIDER_PANEL_COUNT_RANGE.default : BIFOLD_PANEL_COUNT_RANGE.default);
                              setDoorStackDirection(DEFAULT_STACK_DIRECTION);
                              setDoorTrafficDoor(DEFAULT_TRAFFIC_DOOR);
                              setDoorThreshold(DEFAULT_THRESHOLD);
                              setSelectedMaterialId(null);
                              setSelectedStyleId(null);
                              setStyleSearch('');
                              setStep(2);
                            }}
                          >
                            <Box sx={{ p: { xs: 1, sm: 1.5 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Box
                                component="img"
                                src={type.imagePath}
                                alt={type.name}
                                sx={{
                                  width: { xs: '100%', sm: '90%' },
                                  maxWidth: { xs: '100%', sm: 200 },
                                  height: 'auto',
                                  maxHeight: { xs: 90, sm: 120 },
                                  objectFit: 'contain',
                                  transition: 'transform 0.3s ease',
                                  transform: isSelected ? 'scale(1.05)' : 'scale(1)'
                                }}
                              />
                              <Typography level="body-sm" sx={{ mt: 0.5, fontWeight: isSelected ? 'lg' : 'md', color: isSelected ? 'primary.700' : 'text.primary', textAlign: 'center' }}>
                                {type.name}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Stack>
              )}

              {step === 2 && (
                <Stack spacing={1.25}>
                  <Typography level="h4">Select Material</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Tap a material to continue.
                  </Typography>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {MATERIAL_OPTIONS.map(m => {
                      const isSelected = selectedMaterialId === m.id;
                      return (
                        <Grid key={m.id} xs={4}>
                          <Card
                            variant="outlined"
                            onClick={() => {
                              setSelectedMaterialId(m.id);
                              // Material changes the style pool; reset chosen style/search
                              setSelectedStyleId(null);
                              setStyleSearch('');
                              setStep(3);
                            }}
                            sx={{
                              cursor: 'pointer',
                              textAlign: 'center',
                              p: 1.25,
                              border: isSelected ? '2px solid' : '1px solid',
                              borderColor: isSelected ? 'primary.500' : 'neutral.200',
                              bgcolor: isSelected ? 'primary.softBg' : 'background.surface',
                            }}
                          >
                            <Typography level="body-sm" fontWeight={isSelected ? 'lg' : 'md'}>
                              {m.name}
                            </Typography>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Stack>
              )}

              {step === 3 && (
                <Stack spacing={1.25}>
                  <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md', border: '1px solid', borderColor: 'neutral.200', bgcolor: 'background.surface' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                      <Stack spacing={0.25}>
                        <Typography level="title-sm">Option 1 · Create in Window Generator</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          Build a new custom design, then continue with the generated preview.
                        </Typography>
                      </Stack>
                      <Button
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={() => {
                          setWgModalSessionKey((prev) => prev + 1);
                          setWgModalOpen(true);
                        }}
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      >
                        Create in Window Generator
                      </Button>
                    </Stack>
                  </Sheet>

                  {designerVersion === 'v2_lite' && (
                    <input
                      ref={uploadPresentationExternalInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0] || null;
                        // allow selecting same file again
                        e.currentTarget.value = '';
                        if (!file) return;
                        await uploadReferenceImageVariant('presentation-external', file);
                      }}
                    />
                  )}

                  {designerVersion === 'v2_lite' && (
                    <input
                      ref={uploadPresentationInternalInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0] || null;
                        // allow selecting same file again
                        e.currentTarget.value = '';
                        if (!file) return;
                        await uploadReferenceImageVariant('presentation-internal', file);
                      }}
                    />
                  )}

                  {designerVersion === 'v2_lite' && (
                    <input
                      ref={uploadDesignExternalInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0] || null;
                        // allow selecting same file again
                        e.currentTarget.value = '';
                        if (!file) return;
                        await uploadReferenceImageVariant('design-external', file);
                      }}
                    />
                  )}

                  {designerVersion === 'v2_lite' && (
                    <input
                      ref={uploadDesignInternalInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0] || null;
                        // allow selecting same file again
                        e.currentTarget.value = '';
                        if (!file) return;
                        await uploadReferenceImageVariant('design-internal', file);
                      }}
                    />
                  )}

                  {designerVersion === 'v2_lite' && (
                    <Sheet
                      variant="soft"
                      sx={{
                        p: 1.25,
                        borderRadius: 'md',
                        border: '1px solid',
                        borderColor: uploadedStyle && selectedStyleId === uploadedStyle.id ? 'primary.400' : 'neutral.200',
                        bgcolor: uploadedStyle && selectedStyleId === uploadedStyle.id ? 'primary.softBg' : 'background.surface',
                        opacity: isUploadingReference ? 0.75 : 1,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'flex-start' }} justifyContent="space-between">
                          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 220 }}>
                            <Typography level="title-sm">Option 2 · Upload Manual Creation</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              Upload any presentation/design reference images, then click Use Custom.
                            </Typography>
                            {referenceUploadError && (
                              <Alert color="danger" variant="soft" sx={{ mt: 0.75 }}>
                                {referenceUploadError}
                              </Alert>
                            )}
                            {isUploadingReference && (
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                <CircularProgress size="sm" />
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Uploading image...</Typography>
                              </Stack>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={isUploadingReference}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setReferenceUploadError(null);
                                uploadPresentationExternalInputRef.current?.click();
                              }}
                            >
                              Presentation outside
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={isUploadingReference}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setReferenceUploadError(null);
                                uploadPresentationInternalInputRef.current?.click();
                              }}
                            >
                              Presentation inside
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={isUploadingReference}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setReferenceUploadError(null);
                                uploadDesignExternalInputRef.current?.click();
                              }}
                            >
                              Design outside
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={isUploadingReference}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setReferenceUploadError(null);
                                uploadDesignInternalInputRef.current?.click();
                              }}
                            >
                              Design inside
                            </Button>
                            <Button
                              size="sm"
                              variant="solid"
                              color="primary"
                              disabled={isUploadingReference || !hasUploadedReferenceImage(uploadedStyle)}
                              onClick={() => {
                                if (!uploadedStyle || !hasUploadedReferenceImage(uploadedStyle)) return;
                                setSelectedStyleId(uploadedStyle.id);
                                setStep(4);
                              }}
                            >
                              Use Custom
                            </Button>
                          </Stack>
                        </Stack>
                        {uploadedStyle && (
                          (() => {
                            const uploadedSlots = [
                              { key: 'presentation-external' as const, label: 'Presentation · Outside', url: uploadedStyle.presentationExternalUrl },
                              { key: 'presentation-internal' as const, label: 'Presentation · Inside', url: uploadedStyle.presentationInternalUrl },
                              { key: 'design-external' as const, label: 'Design · Outside', url: uploadedStyle.designExternalUrl },
                              { key: 'design-internal' as const, label: 'Design · Inside', url: uploadedStyle.designInternalUrl },
                            ].filter((slot) => !!String(slot.url || '').trim());
                            if (!uploadedSlots.length) return null;
                            return (
                              <Grid container spacing={0.75}>
                                {uploadedSlots.map((slot) => (
                                  <Grid key={slot.label} xs={6} sm={3}>
                                    <Sheet variant="outlined" sx={{ p: 0.75, borderRadius: 'sm', bgcolor: 'background.body' }}>
                                      <Stack spacing={0.35}>
                                        <ImageCanvasViewer imageUrl={toPublicImageUrl(slot.url)} overallWidthMm={null} overallHeightMm={null} heightPx={88} useCrossOrigin={false} />
                                        <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>{slot.label}</Typography>
                                        <Button
                                          size="sm"
                                          variant="plain"
                                          color="danger"
                                          onClick={(e: React.MouseEvent) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            removeReferenceImageVariant(slot.key);
                                          }}
                                          sx={{ minHeight: 24, alignSelf: 'center' }}
                                        >
                                          Remove
                                        </Button>
                                      </Stack>
                                    </Sheet>
                                  </Grid>
                                ))}
                              </Grid>
                            );
                          })()
                        )}
                      </Stack>
                    </Sheet>
                  )}

                  <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md', border: '1px solid', borderColor: 'neutral.200', bgcolor: 'background.surface' }}>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }} justifyContent="space-between">
                        <Stack spacing={0.25}>
                          <Typography level="title-sm">Option 3 · Select from library</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            Choose one of your saved designs to continue.
                          </Typography>
                        </Stack>
                        <FormControl size="sm" sx={{ width: { xs: '100%', sm: 320 } }}>
                          <FormLabel>Search styles</FormLabel>
                          <Input
                            size="sm"
                            placeholder="Search styles..."
                            value={styleSearch}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStyleSearch(e.target.value)}
                          />
                        </FormControl>
                      </Stack>
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {sortedStyles.map(style => {
                      const isLibrary = 'source' in style && style.source === 'window_generator';
                      const isSelected = selectedStyleId === style.id;
                      const isFav = typeof style.id === 'number' && favoriteStyleIds.has(style.id);
                      const isSavingFav = typeof style.id === 'number' && savingFavoriteIds.has(style.id);
                      const showStyleName = !/^Custom\s+\(Unsaved\)$/i.test(String(style.name || '').trim());
                      const styleThumbMarginPx = isDoorTypeId(selectedTypeId) && !isLibrary ? 12 : undefined;
                      let thumbPayload: WindowGeneratorPayload | null = null;
                      if (isLibrary && 'payload' in style) {
                        if (
                          designerVersion === 'v2_lite' &&
                          isSelected &&
                          workingPayload &&
                          (workingPayload as any).root &&
                          (workingPayload as any).w != null &&
                          (workingPayload as any).h != null
                        ) {
                          thumbPayload = workingPayload;
                        } else {
                          thumbPayload = (style as any).payload || null;
                        }
                      }
                      return (
                        <Grid key={style.id} xs={6} sm={4} md={3}>
                          <Card
                            variant={isSelected ? 'outlined' : 'plain'}
                            sx={{
                              cursor: 'pointer',
                              borderColor: isSelected ? 'primary.500' : undefined,
                              boxShadow: isSelected ? 'sm' : undefined,
                              height: '100%',
                              position: 'relative',
                            }}
                            onClick={() => {
                              setSelectedStyleId(style.id);
                              // v2 Lite: user can override via Window Generator modal (don’t force step 4 immediately)
                              if (designerVersion === 'v2_lite' && ('source' in style) && (style as any).source === 'window_generator') {
                                setWorkingPayload((style as any).payload ? deepCloneJson((style as any).payload) : null);
                              }
                              setStep(4);
                            }}
                          >
                            {typeof style.id === 'number' && (
                              <IconButton
                                size="sm"
                                variant="plain"
                                aria-label={isFav ? 'Unfavorite' : 'Favorite'}
                                disabled={isSavingFav}
                                onClick={(e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleFavorite(style.id as number);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  zIndex: 2,
                                  color: isFav ? '#d4af37' : '#111827',
                                  backgroundColor: 'rgba(255,255,255,0.75)',
                                  borderRadius: '999px',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                              >
                                <IconWrapper
                                  icon={isFav ? FaStar : FaRegStar}
                                  style={{ fontSize: 20, lineHeight: 1 }}
                                />
                              </IconButton>
                            )}
                            <Stack spacing={1}>
                              {isLibrary && 'payload' in style ? (
                                (() => {
                                  // Prefer WindowGeneratorThumbnail for library templates: it always renders from payload.
                                  // Image URLs may fail (storage bucket, CORS). Thumbnail is reliable.
                                  return (
                                    <WindowGeneratorThumbnail
                                      payload={thumbPayload || style.payload}
                                      heightPx={180}
                                      marginPx={styleThumbMarginPx ?? 14}
                                    />
                                  );
                                })()
                              ) : (
                                <ImageCanvasViewer
                                  imageUrl={toPublicImageUrl((style as WindowStyleConfig).previewPath)}
                                  overallWidthMm={null}
                                  overallHeightMm={null}
                                  heightPx={160}
                                  marginPx={styleThumbMarginPx}
                                />
                              )}
                              <Stack spacing={0.5}>
                                {showStyleName && (
                                  <Typography level="body-sm" fontWeight="lg">{style.name}</Typography>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                  <Chip size="sm" variant="soft">{formatProfileDefinition(style.category) || style.category}</Chip>
                                  {doorPresetStyle && style.id === doorPresetStyle.id
                                    ? <Chip size="sm" variant="outlined" color="primary">Start from preset</Chip>
                                    : isLibrary && <Chip size="sm" variant="outlined">Your design</Chip>}
                                  {isSelected && <Chip size="sm" color="primary">Selected</Chip>}
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                        </Grid>
                      );
                    })}
                      </Grid>
                    </Stack>
                  </Sheet>
                </Stack>
              )}

              {step === 4 && (
                <Stack spacing={2}>
                  {selectedStyle && (
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                          <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', minWidth: 200 }}>
                            <Select
                              size="sm"
                              variant="outlined"
                              value={selectedTypeId || null}
                              placeholder="Window type"
                              onChange={(_, v) => setSelectedTypeId((v as any) || null)}
                            >
                              {QUOTE_WINDOW_TYPES.map(t => (
                                <Option key={t.id} value={t.id}>{t.name}</Option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', minWidth: 120 }}>
                            <Select
                              size="sm"
                              variant="outlined"
                              value={selectedMaterialId || null}
                              placeholder="Material"
                              onChange={(_, v) => setSelectedMaterialId(((v as any) || null) as any)}
                            >
                              {MATERIAL_OPTIONS.map(m => (
                                <Option key={m.id} value={m.id}>{m.name}</Option>
                              ))}
                            </Select>
                          </FormControl>
                          {designerVersion === 'v2_lite' ? (
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', minWidth: 160 }}>
                              <Select
                                size="sm"
                                variant="outlined"
                                value={styleFamilyCategory}
                                onChange={(_, v) => setStyleFamilyCategory(normalizeStyleFamilyCategory(v))}
                              >
                                {STYLE_FAMILY_OPTIONS.map(o => (
                                  <Option key={o.code} value={o.code}>{o.label}</Option>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip size="sm" variant="soft">{formatProfileDefinition(selectedStyle.category) || selectedStyle.category}</Chip>
                          )}
                        </Stack>
                        <Box sx={{ width: '100%', maxWidth: 640 }}>
                          {'payload' in selectedStyle && selectedStyle.payload ? (
                            (() => {
                              const s = selectedStyle as any;
                              const payloadToRender = (workingPayload || v2PayloadToUse || s.payload) as WindowGeneratorPayload | null;
                              const hasLivePayload = !!payloadToRender?.root && payloadToRender.w != null && payloadToRender.h != null;
                              if (hasLivePayload) {
                                const isBay = isBayLikePayload(payloadToRender);
                                const isCurvedBay = isBay && String((payloadToRender as any)?.presentation?.bayConfig?.bayType || '').toUpperCase() === 'CURVED_BOW';
                                const presWidthPx = 320;
                                const presExternalShiftXPx = 0;
                                const presExternalShiftYPx = isCurvedBay ? -24 : 0;
                                const PREVIEW_AR = '8 / 7'; // matches legacy WG preview boxes (160×140)
                                return (
                                  <Grid container spacing={1} sx={{ p: 0.5 }}>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& svg': { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' } }}>
                                        <WindowGeneratorSvg key={`wg-prev-${previewNonce}-pres-ext`} payload={payloadToRender} widthPx={presWidthPx} view="external" mode="presentation" contentOffsetXPx={presExternalShiftXPx} contentOffsetYPx={presExternalShiftYPx} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Presentation · Outside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& svg': { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' } }}>
                                        <WindowGeneratorSvg key={`wg-prev-${previewNonce}-pres-int`} payload={payloadToRender} widthPx={presWidthPx} view="internal" mode="presentation" />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Presentation · Inside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& svg': { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' } }}>
                                        <WindowGeneratorSvg key={`wg-prev-${previewNonce}-des-ext`} payload={payloadToRender} widthPx={320} view="external" mode="design" />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Design · Outside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& svg': { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' } }}>
                                        <WindowGeneratorSvg key={`wg-prev-${previewNonce}-des-int`} payload={payloadToRender} widthPx={320} view="internal" mode="design" />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Design · Inside</Typography>
                                    </Grid>
                                    {isBay && (
                                      <Grid xs={12}>
                                        <Box sx={{ aspectRatio: '10 / 7', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& svg': { width: '100%', height: '100%', display: 'block' } }}>
                                          <WindowGeneratorBaySchematicSvg key={`wg-prev-${previewNonce}-bay-schematic`} payload={payloadToRender} widthPx={320} />
                                        </Box>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Bay Schematic · Outside</Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                );
                              }
                              const presExt = toPublicImageUrl(s.preview_presentation_external_url || s.preview_external_url || '');
                              const presInt = toPublicImageUrl(s.preview_presentation_internal_url || s.preview_internal_url || '');
                              const desExt = toPublicImageUrl(s.preview_design_external_url || '');
                              const desInt = toPublicImageUrl(s.preview_design_internal_url || '');
                              if (presExt && presInt && desExt && desInt) {
                                const PREVIEW_AR = '8 / 7'; // matches legacy WG preview boxes (160×140)
                                return (
                                  <Grid container spacing={1} sx={{ p: 0.5 }}>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& canvas': { width: '100%', height: '100%', display: 'block' } }}>
                                        <ImageCanvasViewer imageUrl={presExt} overallWidthMm={null} overallHeightMm={null} heightPx={220} useCrossOrigin={false} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Presentation · Outside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& canvas': { width: '100%', height: '100%', display: 'block' } }}>
                                        <ImageCanvasViewer imageUrl={presInt} overallWidthMm={null} overallHeightMm={null} heightPx={220} useCrossOrigin={false} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Presentation · Inside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& canvas': { width: '100%', height: '100%', display: 'block' } }}>
                                        <ImageCanvasViewer imageUrl={desExt} overallWidthMm={null} overallHeightMm={null} heightPx={220} useCrossOrigin={false} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Design · Outside</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                      <Box sx={{ aspectRatio: PREVIEW_AR, width: '100%', bgcolor: '#d9dee7', borderRadius: 'sm', overflow: 'hidden', '& canvas': { width: '100%', height: '100%', display: 'block' } }}>
                                        <ImageCanvasViewer imageUrl={desInt} overallWidthMm={null} overallHeightMm={null} heightPx={220} useCrossOrigin={false} />
                                      </Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>Design · Inside</Typography>
                                    </Grid>
                                  </Grid>
                                );
                              }
                              return (
                                <WindowGeneratorThumbnail
                                  payload={payloadToRender || selectedStyle.payload}
                                  heightPx={420}
                                  marginPx={14}
                                />
                              );
                            })()
                          ) : (
                            (() => {
                              const isUpload = 'source' in (selectedStyle as any) && (selectedStyle as any).source === 'upload';
                              if (isUpload) {
                                const up = selectedStyle as UploadStyleOption;
                                const uploadedSlots = [
                                  { label: 'Presentation · Outside', url: up.presentationExternalUrl },
                                  { label: 'Presentation · Inside', url: up.presentationInternalUrl },
                                  { label: 'Design · Outside', url: up.designExternalUrl },
                                  { label: 'Design · Inside', url: up.designInternalUrl },
                                ].filter((slot) => !!String(slot.url || '').trim());
                                return (
                                  <Grid container spacing={1} sx={{ p: 0.5 }}>
                                    {uploadedSlots.map((slot) => (
                                      <Grid key={slot.label} xs={6}>
                                        <Stack spacing={0.35}>
                                          <ImageCanvasViewer imageUrl={toPublicImageUrl(slot.url)} overallWidthMm={null} overallHeightMm={null} heightPx={200} useCrossOrigin={false} />
                                          <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>{slot.label}</Typography>
                                        </Stack>
                                      </Grid>
                                    ))}
                                  </Grid>
                                );
                              }
                              return (
                                <ImageCanvasViewer
                                  imageUrl={(selectedDoorPreviewUrls && selectedDoorPreviewUrls[0]) ? selectedDoorPreviewUrls[0] : toPublicImageUrl((selectedStyle as WindowStyleConfig).previewPath)}
                                  fallbackUrls={selectedDoorPreviewUrls ? selectedDoorPreviewUrls.slice(1) : undefined}
                                  overallWidthMm={null}
                                  overallHeightMm={null}
                                  heightPx={400}
                                />
                              );
                            })()
                          )}
                        </Box>
                      </Stack>
                    </Sheet>
                  )}

                  {designerVersion === 'v2_lite' && selectedStyle && ('payload' in selectedStyle) && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => setWgLiteModalOpen(true)}
                      >
                        Configure in Window Generator
                      </Button>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        disabled={!workingPayload}
                        onClick={() => setWorkingPayload(null)}
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      >
                        Reset overrides
                      </Button>
                      {workingPayload && (
                        <Chip size="sm" variant="soft" color="primary" sx={{ ml: { sm: 'auto' }, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                          Overrides applied
                        </Chip>
                      )}
                    </Stack>
                  )}

                  <Divider />

                  <Grid container spacing={{ xs: 1, sm: 1.25 }} sx={{ maxWidth: 900, width: '100%' }}>
                    {/* Row 1: Reference, Width, Height */}
                    <Grid xs={12} sm={5}>
                      <FormControl size="sm" error={showReferenceError} sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Reference *</FormLabel>
                        <Input
                          size="sm"
                          value={reference}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setReference(e.target.value);
                            if (String(e.target.value || '').trim()) setShowReferenceFieldValidation(false);
                          }}
                          onBlur={() => setShowReferenceFieldValidation(true)}
                          placeholder="e.g. Front Bedroom"
                        />
                        {showReferenceError && <FormHelperText>Reference is required.</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={3}>
                      <FormControl size="sm" error={showWidthError} sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>
                          {isBaySpecsMode ? 'Tangent width (mm)' : 'Width (mm)'}
                          {requireSurveyorMeasures ? ' *' : ''}
                        </FormLabel>
                        <Input
                          size="sm"
                          type="number"
                          slotProps={{ input: { min: MIN_FENESTRATION_DIM_MM, max: MAX_FENESTRATION_WIDTH_MM } }}
                          value={widthMm}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setWidthMm(e.target.value);
                            if (String(e.target.value || '').trim()) setShowDimensionFieldValidation(false);
                          }}
                          onBlur={() => {
                            const next = clampFenestrationDimensionMm(widthMm.trim() ? Number(widthMm) : null, 'width');
                            if (next != null) setWidthMm(String(next));
                            if (requireSurveyorMeasures) setShowDimensionFieldValidation(true);
                          }}
                          disabled={designerVersion === 'v2' && !requireSurveyorMeasures}
                        />
                        {showWidthError && (
                          <FormHelperText>
                            {isBaySpecsMode ? 'Tangent width is required.' : 'Width is required.'}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" error={showHeightError} sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Height (mm){requireSurveyorMeasures ? ' *' : ''}</FormLabel>
                        <Input
                          size="sm"
                          type="number"
                          slotProps={{ input: { min: MIN_FENESTRATION_DIM_MM, max: MAX_FENESTRATION_HEIGHT_MM } }}
                          value={heightMm}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setHeightMm(e.target.value);
                            if (String(e.target.value || '').trim()) setShowDimensionFieldValidation(false);
                          }}
                          onBlur={() => {
                            const next = clampFenestrationDimensionMm(heightMm.trim() ? Number(heightMm) : null, 'height');
                            if (next != null) setHeightMm(String(next));
                            if (requireSurveyorMeasures) setShowDimensionFieldValidation(true);
                          }}
                          disabled={designerVersion === 'v2' && !requireSurveyorMeasures}
                        />
                        {showHeightError && <FormHelperText>Height is required.</FormHelperText>}
                      </FormControl>
                    </Grid>

                    {!!(lines[editingIdx ?? -1] as any)?.metaJson?.survey?.first_survey_reference && (
                      <Grid xs={12}>
                        <AccordionGroup>
                          <Accordion defaultExpanded={false}>
                            <AccordionSummary>First survey measurements</AccordionSummary>
                            <AccordionDetails>
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Original drawings still use these sizes. Enter new measurements above without changing First Survey.
                              </Typography>
                              <Typography level="body-sm" sx={{ mt: 0.75 }}>
                                {formatFirstSurveyReferenceSummary(
                                  getFirstSurveyReference((lines[editingIdx ?? -1] as any)?.metaJson),
                                )}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        </AccordionGroup>
                      </Grid>
                    )}

                    {/* Row 2: Glass, Profile, Beading, Colour, Handle */}
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Glass type</FormLabel>
                        <Select
                          size="sm"
                          value={(glassStyleTilePath.trim()
                            ? (`tile:${glassStyleTilePath.trim()}` as any)
                            : (glassTypeCode === 'obscured' ? 'satin' : 'clear'))}
                          onChange={(_, v) => {
                            const next = String(v || 'clear');
                            if (next.startsWith('tile:')) {
                              const p = next.slice('tile:'.length);
                              setGlassStyleTilePath(p);
                              setGlassTypeCode('obscured');
                            } else if (next === 'satin') {
                              setGlassStyleTilePath('');
                              setGlassTypeCode('obscured');
                            } else {
                              setGlassStyleTilePath('');
                              setGlassTypeCode('clear');
                            }
                          }}
                          disabled={designerVersion === 'v2'}
                        >
                          <Option value="clear">Clear</Option>
                          <Option value="satin">Satin</Option>
                          {glassStyleTiles.map((t) => {
                            const p = toPublicImageUrl(t.path);
                            return (
                              <Option key={`tile-${p}`} value={`tile:${p}` as any}>
                                {t.name}
                              </Option>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Glass Specification</FormLabel>
                        <Select
                          size="sm"
                          value={glassSpecification}
                          onChange={(_, v) => setGlassSpecification(((v as any) || DEFAULT_GLASS_SPECIFICATION) as GlassSpecificationCode)}
                        >
                          {GLASS_SPECIFICATION_OPTIONS.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Profile Type</FormLabel>
                        <Select
                          size="sm"
                          value={variantCode === '' ? BULK_NONE : variantCode}
                          onChange={(_, v) => setVariantCode(((v as any) === BULK_NONE ? '' : ((v as any) || '')) as OptionalProfileVariantCode)}
                          disabled={designerVersion === 'v2'}
                        >
                          <Option
                            disabled
                            value="__profile-group-none-edit"
                            sx={{ fontWeight: 'lg', opacity: '1 !important', color: '#000 !important' }}
                          >
                            None
                          </Option>
                          <Option value={BULK_NONE}>None</Option>
                          {PROFILE_VARIANT_GROUPS
                            .filter((g) => g.options.length > 0)
                            .map((group) => (
                              <React.Fragment key={`edit-profile-group-${group.label}`}>
                                <Option
                                  disabled
                                  value={`__profile-group-edit-${group.label}`}
                                  sx={{ fontWeight: 'lg', opacity: '1 !important', color: '#000 !important' }}
                                >
                                  {group.label}
                                </Option>
                                {group.options.map((o) => (
                                  <Option key={o.code} value={o.code}>{o.label}</Option>
                                ))}
                              </React.Fragment>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Beading type</FormLabel>
                        <Select
                          size="sm"
                          value={beadingTypeCode === '' ? BULK_NONE : beadingTypeCode}
                          onChange={(_, v) => setBeadingTypeCode(((v as any) === BULK_NONE ? '' : ((v as any) || '')) as OptionalProfileVariantCode)}
                          disabled={designerVersion === 'v2'}
                        >
                          <Option value={BULK_NONE}>None</Option>
                          {BEADING_TYPE_OPTIONS.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 3 */}
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>External colour</FormLabel>
                        <Select
                          size="sm"
                          value={frameColorExtCode}
                          onChange={(_, v) => {
                            const next = String(v || 'white-smooth');
                            setFrameColorExtCode(next);
                            setShowRalFieldValidation(false);
                            if (next !== CUSTOM_FRAME_COLOUR_CODE) {
                              setFrameColorExtCustomRal('');
                              setFrameColorExtCustomHex('');
                            }
                            const combined = getFrameSelectionFromSideCodes(
                              next === CUSTOM_FRAME_COLOUR_CODE ? frameColorIntCode : next,
                              frameColorIntCode === CUSTOM_FRAME_COLOUR_CODE ? next : frameColorIntCode
                            ).combinedCode;
                            setFrameColorCode(combined || (next === CUSTOM_FRAME_COLOUR_CODE ? frameColorCode : next));
                          }}
                        >
                          {FRAME_SIDE_COLOUR_OPTIONS_WITH_CUSTOM.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Internal colour</FormLabel>
                        <Select
                          size="sm"
                          value={frameColorIntCode}
                          onChange={(_, v) => {
                            const next = String(v || 'white-smooth');
                            setFrameColorIntCode(next);
                            setShowRalFieldValidation(false);
                            if (next !== CUSTOM_FRAME_COLOUR_CODE) {
                              setFrameColorIntCustomRal('');
                              setFrameColorIntCustomHex('');
                            }
                            const combined = getFrameSelectionFromSideCodes(
                              frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE ? next : frameColorExtCode,
                              next === CUSTOM_FRAME_COLOUR_CODE ? frameColorExtCode : next
                            ).combinedCode;
                            setFrameColorCode(combined || (frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE ? frameColorCode : frameColorExtCode));
                          }}
                        >
                          {FRAME_SIDE_COLOUR_OPTIONS_WITH_CUSTOM.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Handle colour</FormLabel>
                        <Select size="sm" value={handleColorCode} onChange={(_, v) => setHandleColorCode(String(v || 'white'))} disabled={designerVersion === 'v2'}>
                          {HANDLE_COLOUR_OPTIONS.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {frameColorExtCode === CUSTOM_FRAME_COLOUR_CODE && (
                      <Grid xs={6} sm={4}>
                        <Stack spacing={0.75}>
                          <FormLabel>External RAL Custom</FormLabel>
                          <Typography
                            level="body-xs"
                            component="a"
                            href="https://hex-to-rgb.com/convert/ral-to-hex"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ width: 'fit-content', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            Find RAL to HEX
                          </Typography>
                          <Input
                            size="sm"
                            error={showExtCustomRalError}
                            value={frameColorExtCustomRal}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFrameColorExtCustomRal(e.target.value);
                            }}
                            placeholder="RAL code (e.g. RAL7006)"
                          />
                          {showExtCustomRalError && <FormHelperText>RAL value is required.</FormHelperText>}
                          <Input
                            size="sm"
                            error={showExtCustomHexError}
                            value={frameColorExtCustomHex}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFrameColorExtCustomHex(normalizeHexInput(e.target.value));
                            }}
                            placeholder="HEX (e.g. #6D6A63)"
                          />
                          {showExtCustomHexError && <FormHelperText>Valid HEX value is required.</FormHelperText>}
                        </Stack>
                      </Grid>
                    )}
                    {frameColorIntCode === CUSTOM_FRAME_COLOUR_CODE && (
                      <Grid xs={6} sm={4}>
                        <Stack spacing={0.75}>
                          <FormLabel>Internal RAL Custom</FormLabel>
                          <Typography
                            level="body-xs"
                            component="a"
                            href="https://hex-to-rgb.com/convert/ral-to-hex"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ width: 'fit-content', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            Find RAL to HEX
                          </Typography>
                          <Input
                            size="sm"
                            error={showIntCustomRalError}
                            value={frameColorIntCustomRal}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFrameColorIntCustomRal(e.target.value);
                            }}
                            placeholder="RAL code (e.g. RAL7006)"
                          />
                          {showIntCustomRalError && <FormHelperText>RAL value is required.</FormHelperText>}
                          <Input
                            size="sm"
                            error={showIntCustomHexError}
                            value={frameColorIntCustomHex}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFrameColorIntCustomHex(normalizeHexInput(e.target.value));
                            }}
                            placeholder="HEX (e.g. #6D6A63)"
                          />
                          {showIntCustomHexError && <FormHelperText>Valid HEX value is required.</FormHelperText>}
                        </Stack>
                      </Grid>
                    )}
                    {designerVersion === 'v2_lite' && (
                      <Grid xs={6} sm={4}>
                        <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                          <FormLabel>Design dimensions</FormLabel>
                          <RadioGroup
                            orientation="horizontal"
                            value={designDimsOn ? 'on' : 'off'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesignDimsOn(e.target.value === 'on')}
                            sx={{ gap: 1 }}
                          >
                            <Radio value="off" label="Off" />
                            <Radio value="on" label="On" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Row 4 */}
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Handle style</FormLabel>
                        <Select
                          size="sm"
                          value={handleStyleCode}
                          onChange={(_, v) => {
                            const raw = (v as any) || (isDoorSelection ? 'lever' : 'offset');
                            setHandleStyleCode(normalizeHandleStyleForType(raw as HandleStyleCode, isDoorSelection));
                          }}
                          disabled={designerVersion === 'v2'}
                        >
                          {availableHandleStyleOptions.map(o => (
                            <Option key={o.code} value={o.code}>{o.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {isDoorTypeId(selectedTypeId) && (
                      <>
                        {doorKindUsesHingePlacement(selectedDoorKind) && (
                          <Grid xs={6} sm={4}>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Hinge placement</FormLabel>
                              <Select
                                size="sm"
                                value={doorHingePlacement}
                                onChange={(_, v) => setDoorHingePlacement(((v as any) || 'left') as DoorHingePlacementCode)}
                                disabled={designerVersion === 'v2'}
                              >
                                {DOOR_HINGE_PLACEMENT_OPTIONS.map(o => (
                                  <Option key={o.code} value={o.code}>{o.label}</Option>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {doorKindUsesOpenDirection(selectedDoorKind) && (
                          <Grid xs={6} sm={4}>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Open direction</FormLabel>
                              <Select
                                size="sm"
                                value={doorOpenDirection}
                                onChange={(_, v) => setDoorOpenDirection(((v as any) || 'out') as DoorOpenDirectionCode)}
                              >
                                {DOOR_OPEN_DIRECTION_OPTIONS.map(o => (
                                  <Option key={o.code} value={o.code}>{o.label}</Option>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {selectedDoorKind === 'french' && (
                          <Grid xs={6} sm={4}>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Master leaf (viewed from outside)</FormLabel>
                              <Select
                                size="sm"
                                value={doorMasterLeaf}
                                onChange={(_, v) => {
                                  const next = ((v as any) || DEFAULT_MASTER_LEAF) as DoorSideCode;
                                  setDoorMasterLeaf(next);
                                  setWorkingPayload((prev) => (prev ? applyMasterLeafToPayload(prev, next) : prev));
                                }}
                                disabled={designerVersion === 'v2'}
                              >
                                {MASTER_LEAF_OPTIONS.map(o => (
                                  <Option key={o.code} value={o.code}>{o.label}</Option>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {(selectedDoorKind === 'bifold' || selectedDoorKind === 'slider') && effectiveDoorPanelCount != null && (
                          <Grid xs={6} sm={4}>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Panels</FormLabel>
                              <Select
                                size="sm"
                                value={effectiveDoorPanelCount}
                                onChange={(_, v) => {
                                  if (v == null || Number(v) === effectiveDoorPanelCount) return;
                                  requestDoorRedraw(Number(v), doorTrafficDoor);
                                }}
                                disabled={designerVersion === 'v2'}
                              >
                                {(() => {
                                  const range = selectedDoorKind === 'slider' ? SLIDER_PANEL_COUNT_RANGE : BIFOLD_PANEL_COUNT_RANGE;
                                  return Array.from({ length: range.max - range.min + 1 }, (_, i) => range.min + i).map((n) => (
                                    <Option key={n} value={n}>{n}</Option>
                                  ));
                                })()}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {selectedDoorKind === 'bifold' && (
                          <>
                            <Grid xs={6} sm={4}>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Stack (viewed from outside)</FormLabel>
                                <Select
                                  size="sm"
                                  value={doorStackDirection}
                                  onChange={(_, v) => setDoorStackDirection(((v as any) || DEFAULT_STACK_DIRECTION) as StackDirectionCode)}
                                  disabled={designerVersion === 'v2'}
                                >
                                  {STACK_DIRECTION_OPTIONS.map(o => (
                                    <Option key={o.code} value={o.code}>{o.label}</Option>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid xs={6} sm={4}>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Traffic door (viewed from outside)</FormLabel>
                                <Select
                                  size="sm"
                                  value={doorTrafficDoor}
                                  onChange={(_, v) => {
                                    const next = ((v as any) || DEFAULT_TRAFFIC_DOOR) as TrafficDoorCode;
                                    if (next === doorTrafficDoor) return;
                                    requestDoorRedraw(effectiveDoorPanelCount, next);
                                  }}
                                  disabled={designerVersion === 'v2'}
                                >
                                  {TRAFFIC_DOOR_OPTIONS.map(o => (
                                    <Option key={o.code} value={o.code}>{o.label}</Option>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                        {selectedDoorKind === 'slider' && (
                          <Grid xs={12} sm={4}>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Sliding</FormLabel>
                              <Typography level="body-sm" sx={{ pt: 0.75 }}>
                                {describeSlidingPanels(workingPayload) || 'No sliding panels in the drawing'}
                              </Typography>
                              <FormHelperText>Read from the drawing. Change it in Configure in Window Generator.</FormHelperText>
                            </FormControl>
                          </Grid>
                        )}
                        <Grid xs={6} sm={4}>
                          <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                            <FormLabel>Threshold?</FormLabel>
                            <Select
                              size="sm"
                              value={doorThreshold}
                              onChange={(_, v) => setDoorThreshold(((v as any) || DEFAULT_THRESHOLD) as ThresholdCode)}
                            >
                              {THRESHOLD_OPTIONS.map(o => (
                                <Option key={o.code} value={o.code}>{o.label}</Option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Quantity</FormLabel>
                        <Input size="sm" type="number" value={qty} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQty(e.target.value)} />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }} disabled={designerVersion === 'v2'}>
                        <FormLabel>Trickle vents</FormLabel>
                        <RadioGroup
                          orientation="horizontal"
                          value={trickleVents}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = e.target.value as 'yes' | 'no';
                            setTrickleVents(next);
                            if (next === 'yes') {
                              setTrickleVentsPosition((prev) => (
                                prev === 'custom' || prev === 'head' || prev === 'sash' || prev === 'extender'
                                  ? prev
                                  : 'sash'
                              ));
                            }
                          }}
                          sx={{ gap: 1 }}
                        >
                          <Radio value="no" label="No" />
                          <Radio value="yes" label="Yes" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Row 5 */}
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Trickle vents position</FormLabel>
                        <Select
                          size="sm"
                          disabled={designerVersion === 'v2'}
                          value={trickleVents === 'no' ? 'none' : trickleVentsPosition}
                          onChange={(_, v) => applyTrickleVentsPositionSelection(String(v || ''))}
                        >
                          <Option value="none">No trickle vents</Option>
                          <Option value="custom">Custom</Option>
                          <Option value="head">Head Jamb</Option>
                          <Option value="extender">Extender</Option>
                          <Option value="sash">Head of Sash</Option>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Cill</FormLabel>
                        <Select size="sm" value={cillOption} onChange={(_, v) => setCillOption((v as any) || 'none')} disabled={designerVersion === 'v2'}>
                          <Option value="none">None</Option>
                          <Option value="150">150mm</Option>
                          <Option value="180">180mm</Option>
                          <Option value="stub">Stub</Option>
                        </Select>
                      </FormControl>
                    </Grid>

                    {designerVersion === 'v2_lite' && (
                      <Grid xs={12}>
                        <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', width: '100%' }}>
                          <FormLabel>{EXTENDERS_MM_OUTPUT_LABEL}</FormLabel>
                          <Box
                            sx={{
                              display: 'grid',
                              width: '100%',
                              boxSizing: 'border-box',
                              gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(4, minmax(0, 1fr))',
                              },
                              gap: 1,
                              mt: 0.5,
                              '& .MuiFormControl-root': { minWidth: 0, width: '100%' },
                            }}
                          >
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Top</FormLabel>
                              <Input
                                size="sm"
                                type="number"
                                value={extTopMm}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtTopMm(e.target.value)}
                                onBlur={() => {
                                  if (trickleVentsPosition === 'extender') {
                                    setExtTopMm((prev) => ensureExtenderTopMm(prev));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Bottom</FormLabel>
                              <Input size="sm" type="number" value={extBottomMm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtBottomMm(e.target.value)} />
                            </FormControl>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Left</FormLabel>
                              <Input size="sm" type="number" value={extLeftMm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtLeftMm(e.target.value)} />
                            </FormControl>
                            <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                              <FormLabel>Right</FormLabel>
                              <Input size="sm" type="number" value={extRightMm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtRightMm(e.target.value)} />
                            </FormControl>
                          </Box>
                        </FormControl>
                      </Grid>
                    )}

                    {designerVersion === 'v2_lite' && selectedTypeId !== 'upvc-door' && !isMultiLeafDoorSelection && (
                      <Grid xs={6} sm={4}>
                        <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                          <FormLabel>Window hinge type</FormLabel>
                          <Select
                            size="sm"
                            value={windowHingeTypeCode}
                            onChange={(_, v) => setWindowHingeTypeCode(((v as any) || 'standard') as any)}
                          >
                            {WINDOW_HINGE_TYPE_OPTIONS.map(o => (
                              <Option key={o.code} value={o.code}>{o.label}</Option>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {designerVersion === 'v2_lite' && (
                      <Grid xs={6} sm={4}>
                        <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                          <FormLabel>Corner joint</FormLabel>
                          <Select
                            size="sm"
                            value={jointStyle}
                            onChange={(_, v) => setJointStyle((v as any) === 'timber-weld' ? 'timber-weld' : 'mitre-45')}
                          >
                            <Option value="mitre-45">45° Mitre</Option>
                            <Option value="timber-weld">Timber Weld</Option>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Row 6 */}
                    <Grid xs={12}>
                      <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                        <FormLabel>Notes (optional)</FormLabel>
                        <Textarea size="sm" minRows={1} maxRows={3} value={notes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)} />
                      </FormControl>
                    </Grid>

                    {designerVersion === 'v2_lite' && (
                      <Grid xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography level="body-sm" fontWeight="lg" sx={{ mb: 0.25 }}>
                          Georgian &amp; astragal bars
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
                          Fine-tune bar grids on this line&apos;s Window Generator drawing. Saved on the quote only — the library style is unchanged.
                        </Typography>
                        <Stack spacing={1.5}>
                          <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', overflow: 'hidden' }}>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 1 }}>
                              Georgian bars
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid xs={12} sm={6} md={3}>
                                <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                  <FormLabel>Enabled</FormLabel>
                                  <RadioGroup
                                    orientation="horizontal"
                                    value={georgianOn}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianOn(e.target.value as any)}
                                    sx={{ gap: 1 }}
                                  >
                                    <Radio value="no" label="No" />
                                    <Radio value="yes" label="Yes" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                            </Grid>
                            <Box
                              sx={{
                                display: 'grid',
                                width: '100%',
                                boxSizing: 'border-box',
                                gridTemplateColumns: {
                                  xs: 'repeat(2, minmax(0, 1fr))',
                                  sm: 'repeat(3, minmax(0, 1fr))',
                                  md: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 1,
                                mt: 1,
                                '& .MuiFormControl-root': { minWidth: 0, width: '100%' },
                              }}
                            >
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Cols</FormLabel>
                                <Input size="sm" type="number" value={georgianCols} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianCols(e.target.value)} disabled={georgianOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Rows</FormLabel>
                                <Input size="sm" type="number" value={georgianRows} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianRows(e.target.value)} disabled={georgianOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Width (mm)</FormLabel>
                                <Input size="sm" type="number" value={georgianWidthMm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianWidthMm(e.target.value)} disabled={georgianOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Offset X (%)</FormLabel>
                                <Input size="sm" type="number" value={georgianOffXPct} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianOffXPct(e.target.value)} disabled={georgianOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', gridColumn: { xs: 'span 2', sm: 'auto' } }}>
                                <FormLabel>Offset Y (%)</FormLabel>
                                <Input size="sm" type="number" value={georgianOffYPct} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeorgianOffYPct(e.target.value)} disabled={georgianOn !== 'yes'} />
                              </FormControl>
                            </Box>
                          </Sheet>

                          <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', overflow: 'hidden' }}>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 1 }}>
                              Astragal bars
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid xs={12} sm={6} md={3}>
                                <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                  <FormLabel>Enabled</FormLabel>
                                  <RadioGroup
                                    orientation="horizontal"
                                    value={astragalOn}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalOn(e.target.value as any)}
                                    sx={{ gap: 1 }}
                                  >
                                    <Radio value="no" label="No" />
                                    <Radio value="yes" label="Yes" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                              <Grid xs={12} sm={6} md={4}>
                                <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }} disabled={astragalOn !== 'yes'}>
                                  <FormLabel>Bar side</FormLabel>
                                  <Select
                                    size="sm"
                                    value={astragalBarSide}
                                    onChange={(_, v) => setAstragalBarSide((((v as any) || 'none') as any))}
                                  >
                                    {BAR_SIDE_OPTIONS.map(o => (
                                      <Option key={o.code} value={o.code}>{o.label}</Option>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                            <Box
                              sx={{
                                display: 'grid',
                                width: '100%',
                                boxSizing: 'border-box',
                                gridTemplateColumns: {
                                  xs: 'repeat(2, minmax(0, 1fr))',
                                  sm: 'repeat(3, minmax(0, 1fr))',
                                  md: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 1,
                                mt: 1,
                                '& .MuiFormControl-root': { minWidth: 0, width: '100%' },
                              }}
                            >
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Cols</FormLabel>
                                <Input size="sm" type="number" value={astragalCols} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalCols(e.target.value)} disabled={astragalOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Rows</FormLabel>
                                <Input size="sm" type="number" value={astragalRows} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalRows(e.target.value)} disabled={astragalOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Width (mm)</FormLabel>
                                <Input size="sm" type="number" value={astragalWidthMm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalWidthMm(e.target.value)} disabled={astragalOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0' }}>
                                <FormLabel>Offset X (%)</FormLabel>
                                <Input size="sm" type="number" value={astragalOffXPct} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalOffXPct(e.target.value)} disabled={astragalOn !== 'yes'} />
                              </FormControl>
                              <FormControl size="sm" sx={{ '--FormControl-minWidth': '0', gridColumn: { xs: 'span 2', sm: 'auto' } }}>
                                <FormLabel>Offset Y (%)</FormLabel>
                                <Input size="sm" type="number" value={astragalOffYPct} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAstragalOffYPct(e.target.value)} disabled={astragalOn !== 'yes'} />
                              </FormControl>
                            </Box>
                          </Sheet>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => {
                  if (step === 1) {
                    setOpen(false);
                    resetModalState();
                    return;
                  }
                  setStep((prev) => (prev === 4 ? 3 : prev === 3 ? 2 : 1));
                }}
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </Button>
              {step === 4 && <Button variant="outlined" onClick={() => setStep(3)}>Change Style</Button>}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {step === 4 ? (
                <>
                  <Button variant="outlined" loading={isSaving} disabled={!reference.trim() || isSaving} onClick={() => save('add_another')}>
                    Save + Add Another
                  </Button>
                  <Button loading={isSaving} disabled={!reference.trim() || isSaving} onClick={() => save('close')}>
                    Save
                  </Button>
                </>
              ) : null}
            </Stack>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!pendingDoorRedraw} onClose={() => setPendingDoorRedraw(null)}>
        <ModalDialog role="alertdialog" sx={{ maxWidth: 440 }}>
          <DialogTitle>Redraw the door?</DialogTitle>
          <DialogContent>
            This drawing is different from the starting preset. Redrawing starts again from the{' '}
            {pendingDoorRedraw ? `${pendingDoorRedraw.panelCount}-panel` : ''} preset and replaces the current drawing.
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (pendingDoorRedraw) applyDoorRedraw(pendingDoorRedraw.panelCount, pendingDoorRedraw.trafficDoor);
                setPendingDoorRedraw(null);
              }}
            >
              Redraw
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setPendingDoorRedraw(null)}>
              Keep my drawing
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Window Generator: create design and add to library + quote without leaving Visual Designer */}
      <Modal open={wgModalOpen} onClose={() => setWgModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: '99vw', width: '99vw', height: '97vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          <DialogTitle>Create window design</DialogTitle>
          <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            <Box sx={{ height: '100%', minHeight: 500 }}>
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>}>
              <WindowGenerator
                key={`wg-create-${wgModalSessionKey}`}
                handleStyleOptions={availableHandleStyleOptions}
                initialPayload={createWindowGeneratorInitialPayload}
                onExportPayload={(payload: WindowGeneratorPayload) => {
                  const p = deepCloneJson(payload);
                  const liveId = `wg-live-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
                  setInlineWgStyle({
                    id: liveId,
                    name: 'Custom design',
                    category: 'bespoke',
                    source: 'window_generator',
                    payload: p,
                    preview_design_external_url: null,
                    preview_design_internal_url: null,
                    preview_presentation_external_url: null,
                    preview_presentation_internal_url: null,
                    preview_external_url: null,
                    preview_internal_url: null,
                  });
                  setSelectedStyleId(liveId);
                  setWorkingPayload(p);
                  try {
                    const derived = deriveFromWindowGeneratorPayload(p);
                    applyDerivedToUi({
                      derived,
                      setWidthMm,
                      setHeightMm,
                      setGlassTypeCode,
                      setFrameColorCode,
                      setFrameColorExtCode,
                      setFrameColorIntCode,
                      setFrameColorExtCustomRal,
                      setFrameColorIntCustomRal,
                      setFrameColorExtCustomHex,
                      setFrameColorIntCustomHex,
                      setDesignDimsOn,
                      setHandleColorCode,
                      setHandleStyleCode,
                      setTrickleVents,
                      setTrickleVentsPosition,
                      setCillOption,
                      setExtTopMm,
                      setExtBottomMm,
                      setExtLeftMm,
                      setExtRightMm,
                      setGeorgianOn,
                      setGeorgianCols,
                      setGeorgianRows,
                      setGeorgianWidthMm,
                      setGeorgianOffXPct,
                      setGeorgianOffYPct,
                      setGlassStyleTilePath,
                      setAstragalOn,
                      setAstragalCols,
                      setAstragalRows,
                      setAstragalWidthMm,
                      setAstragalOffXPct,
                      setAstragalOffYPct,
                      setJointStyle,
                      setWindowHingeTypeCode,
                      setAstragalBarSide,
                      setSelectedTypeId,
                    });
                  } catch {
                    // Non-fatal
                  }
                  setStep(4);
                  setPreviewNonce(n => n + 1);
                  setWgModalOpen(false);
                }}
                onSavedToLibrary={async (arg: OnSavedToLibraryArg) => {
                  const t = 'id' in arg && arg.id
                    ? arg as WindowGeneratorTemplate
                    : await windowGeneratorTemplatesService.create((arg as { name: string; payload: WindowGeneratorPayload }).name, (arg as { name: string; payload: WindowGeneratorPayload }).payload);
                  let nextT: any = t as any;
                  // Generate+store previews on the template (one-time)
                  if (!(
                    nextT.preview_presentation_external_url &&
                    nextT.preview_presentation_internal_url &&
                    nextT.preview_design_external_url &&
                    nextT.preview_design_internal_url
                  )) {
                    try {
                      const urls = await uploadVisualDesignerPreviewsAndGetUrls(nextT.payload);
                      nextT = await windowGeneratorTemplatesService.updatePreviews(nextT.id, urls);
                    } catch {
                      // Non-fatal
                    }
                  }
                  setLibraryTemplates(prev => [nextT, ...prev.filter(x => x.id !== nextT.id)].slice(0, 10));
                  setSelectedStyleId(nextT.id);
                  setWorkingPayload(nextT.payload ? deepCloneJson(nextT.payload) : null);
                  setStep(4);
                  setPreviewNonce(n => n + 1);
                  setWgModalOpen(false);
                }}
              />
              </React.Suspense>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setWgModalOpen(false)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Window Generator: configure a library design for this quote without saving */}
      <Modal open={wgLiteModalOpen} onClose={() => setWgLiteModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: '99vw', width: '99vw', height: '97vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          <DialogTitle>Configure window design</DialogTitle>
          <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            <Box sx={{ height: '100%', minHeight: 500 }}>
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>}>
              <WindowGenerator
                handleStyleOptions={availableHandleStyleOptions}
                initialPayload={workingPayload ? deepCloneJson(workingPayload) : null}
                onSavedToLibrary={async (arg: OnSavedToLibraryArg) => {
                  // Allow saving the configured design as a NEW library template (do not overwrite the original template).
                  const t = 'id' in arg && arg.id
                    ? arg as WindowGeneratorTemplate
                    : await windowGeneratorTemplatesService.create(
                        (arg as { name: string; payload: WindowGeneratorPayload }).name,
                        (arg as { name: string; payload: WindowGeneratorPayload }).payload
                      );
                  let nextT: any = t as any;
                  if (!(
                    nextT.preview_presentation_external_url &&
                    nextT.preview_presentation_internal_url &&
                    nextT.preview_design_external_url &&
                    nextT.preview_design_internal_url
                  )) {
                    try {
                      const urls = await uploadVisualDesignerPreviewsAndGetUrls(nextT.payload);
                      nextT = await windowGeneratorTemplatesService.updatePreviews(nextT.id, urls);
                    } catch {
                      // Non-fatal
                    }
                  }
                  setLibraryTemplates(prev => [nextT, ...prev.filter(x => x.id !== nextT.id)].slice(0, 10));
                  setSelectedStyleId(nextT.id);
                  setWorkingPayload(nextT.payload ? deepCloneJson(nextT.payload) : null);
                  setStep(4);
                  setPreviewNonce(n => n + 1);
                  setWgLiteModalOpen(false);
                }}
                onExportPayload={(payload: WindowGeneratorPayload) => {
                  const p = deepCloneJson(payload);
                  setWorkingPayload(p);
                  // “Use Design” should immediately reflect in the quote wizard, even when editing an existing line.
                  // (By default we block WG→UI hydration for v2_lite edits to avoid reverting to library templates.)
                  try {
                    const derived = deriveFromWindowGeneratorPayload(p);
                    applyDerivedToUi({
                      derived,
                      setWidthMm,
                      setHeightMm,
                      setGlassTypeCode,
                      setFrameColorCode,
                      setFrameColorExtCode,
                      setFrameColorIntCode,
                      setFrameColorExtCustomRal,
                      setFrameColorIntCustomRal,
                      setFrameColorExtCustomHex,
                      setFrameColorIntCustomHex,
                      setDesignDimsOn,
                      setHandleColorCode,
                      setHandleStyleCode,
                      setTrickleVents,
                      setTrickleVentsPosition,
                      setCillOption,
                      setExtTopMm,
                      setExtBottomMm,
                      setExtLeftMm,
                      setExtRightMm,
                      setGeorgianOn,
                      setGeorgianCols,
                      setGeorgianRows,
                      setGeorgianWidthMm,
                      setGeorgianOffXPct,
                      setGeorgianOffYPct,
                      setGlassStyleTilePath,
                      setAstragalOn,
                      setAstragalCols,
                      setAstragalRows,
                      setAstragalWidthMm,
                      setAstragalOffXPct,
                      setAstragalOffYPct,
                      setJointStyle,
                      setWindowHingeTypeCode,
                      setAstragalBarSide,
                      setSelectedTypeId,
                    });
                  } catch {
                    // Non-fatal
                  }
                  setPreviewNonce(n => n + 1);
                  setWgLiteModalOpen(false);
                }}
              />
              </React.Suspense>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setWgLiteModalOpen(false)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default QuoteVisualDesignerCard;

